<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useOfficerAppointmentStore } from '../stores/officerAppointmentStore';
import { useTranslation } from '@/utils/locales/messages';
import { useLanguageStore } from '@/shared/stores/languageStore';
import { apiClient } from '@/shared/api/client';
import { formatDateByJurisdiction } from '@/features/register/utils/dateFormat';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import { contactsApi } from '@/features/Contact/api/contactsApi';
import { useDateField, formatDocDate } from '@/shared/composables/dateUtils';
import { useAddressFormatter } from '@/shared/composables/useAddressFormatter';
import { useEntityDocVariables } from '@/shared/composables/useEntityDocVariables';
import ContactListPage from '@/features/Contact/views/ContactListPage.vue';
import ContactDetailPage from '@/features/Contact/views/ContactDetailPage.vue';
import TaskFlowSection from './TaskFlowSection.vue';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { updateControllerAppointmentEFilingData } from '../api/controllerAppointmentApi';

const languageStore = useLanguageStore();
const sessionStore = useSessionStore();
const { createSyncT } = useTranslation();
const t = createSyncT(languageStore.currentLanguage);

// 주소 포맷팅 함수
const { formatAddress: formatAddressUtil, formatAddressLines: formatAddressLinesUtil } = useAddressFormatter();

// Entity doc_variables 조회
const { getEntityData } = useEntityDocVariables();

const officerAppointmentStore = useOfficerAppointmentStore();

// Props
interface Props {
  taskConfig?: any;
  initialData?: any | null; // E-Filing 페이지에서 TASK_DATA 전달용
  readOnly?: boolean; // 읽기 전용 모드 (E-Filing 검증용)
  showTaskFlow?: boolean; // Task Flow 섹션 표시 여부 (E-Filing에서는 false)
  eFilingMode?: boolean; // E-Filing 모드 (입력 폼 숨기고 기존 데이터만 표시)
  validationErrors?: Record<string, string>; // 필드별 validation 에러 메시지
  taskId?: number | string; // Task ID (E-Filing 모드에서 eFilingData 업데이트용)
}

const props = withDefaults(defineProps<Props>(), {
  taskConfig: null,
  initialData: null,
  readOnly: false,
  showTaskFlow: true,
  eFilingMode: false,
  validationErrors: () => ({}),
  taskId: undefined
});

// E-Filing 모드 여부 (템플릿에서 쉽게 사용하기 위한 computed)
const eFilingMode = computed(() => props.eFilingMode);

// Emits
const emit = defineEmits<{
  'auto-select-templates': [config: { categoryName: string; templateIds: string[] }],
  'filter-select-templates': [config: { categoryName: string; templateIds: string[]; filterValue: string }],
  'reset-templates': [],
  'update:taskFlow': [value: { all: boolean; createDocument: boolean; requestTSign: boolean; proceedWithEFiling: boolean }]
}>();

// Task Flow 상태
const taskFlow = ref({
  all: false,
  createDocument: false,
  requestTSign: false,
  proceedWithEFiling: false
});

// Task Flow 지원 여부 (PROCESS_STEPS 기반)
const isCreateDocumentSupported = computed(() => {
  const config = props.taskConfig;
  if (!config || !config.processSteps || !Array.isArray(config.processSteps)) {
    return false;
  }
  return config.processSteps.some((step: string) => {
    const lowerStep = step.toLowerCase();
    return lowerStep === 'doc' || lowerStep === 'document' || lowerStep.includes('doc');
  });
});

const isRequestTSignSupported = computed(() => {
  const config = props.taskConfig;
  if (!config || !config.processSteps || !Array.isArray(config.processSteps)) {
    return false;
  }
  return config.processSteps.some((step: string) => {
    const lowerStep = step.toLowerCase();
    return lowerStep === 't-sign' || lowerStep === 'tsign' || lowerStep.includes('sign');
  });
});

const isProceedWithEFilingSupported = computed(() => {
  const config = props.taskConfig;
  if (!config || !config.processSteps || !Array.isArray(config.processSteps)) {
    return false;
  }
  return config.processSteps.some((step: string) => {
    const lowerStep = step.toLowerCase();
    return lowerStep === 'e-filing' || lowerStep.includes('filing');
  });
});

const isAllSupported = computed(() => {
  return isCreateDocumentSupported.value &&
         isRequestTSignSupported.value &&
         isProceedWithEFilingSupported.value;
});

// Task Flow 변경 시 상위 컴포넌트에 전달
watch(taskFlow, (newValue) => {
  emit('update:taskFlow', { ...newValue });
}, { deep: true });

// Task Flow 자동 업데이트
const updateTaskFlow = () => {
  taskFlow.value.createDocument = isCreateDocumentSupported.value;
  taskFlow.value.requestTSign = isRequestTSignSupported.value;
  taskFlow.value.proceedWithEFiling = isProceedWithEFilingSupported.value;
  taskFlow.value.all = isAllSupported.value;
  
  emit('update:taskFlow', { ...taskFlow.value });
};

// 이미 자동 선택 이벤트를 발생시켰는지 추적 (개인/법인별)
const hasEmittedAutoSelectForIndividual = ref(false);
const hasEmittedAutoSelectForCorporate = ref(false);

// 개인/법인 컨트롤러 선택 시 자동 템플릿 선택 함수
const autoSelectTemplatesByCategory = (categoryCd: string) => {
  const taskConfig = props.taskConfig;

  if (!taskConfig) {
    console.warn('⚠️ Task Config not available. Please select a Task Type first.');
    return;
  }

  // 개인(B09002) 또는 법인(B09001)에 따라 플래그 확인
  if (categoryCd === 'B09002' && hasEmittedAutoSelectForIndividual.value) {
    console.log('⏭️ Auto-select already emitted for individual controller, skipping...');
    return;
  }
  if (categoryCd === 'B09001' && hasEmittedAutoSelectForCorporate.value) {
    console.log('⏭️ Auto-select already emitted for corporate controller, skipping...');
    return;
  }

  try {
    if (taskConfig.documents && Array.isArray(taskConfig.documents)) {
      // filter 값이 categoryCd와 일치하는 문서만 필터링
      const filteredDocuments = taskConfig.documents.filter(
        (doc: any) => doc.filter === categoryCd // filter가 'B09002' 또는 'B09001'인 문서
      );

      if (filteredDocuments.length > 0) {
        const templateIds = filteredDocuments
          .map((doc: any) => doc.DOC_TYPE || doc.TMP_ID)
          .filter((id: any) => id); // null/undefined 제거

        // 카테고리명 = documents의 category (DIRECTORY에서 온 값)
        const categoryName = filteredDocuments[0]?.category?.trim() || null;

        console.log(`📋 [ControllerAppointmentForm] ${categoryCd === 'B09002' ? 'Individual' : 'Corporate'} Controller 문서 자동 선택:`, {
          totalDocuments: taskConfig.documents.length,
          filteredDocuments: filteredDocuments.length,
          templateIds,
          categoryName,
          categoryCd
        });

        // 템플릿 자동 선택 이벤트 emit (filter-select-templates 사용)
        emit('filter-select-templates', {
          categoryName: categoryName || '',
          templateIds,
          filterValue: categoryCd
        });

        // 이벤트 발생 완료 표시
        if (categoryCd === 'B09002') {
          hasEmittedAutoSelectForIndividual.value = true;
        } else if (categoryCd === 'B09001') {
          hasEmittedAutoSelectForCorporate.value = true;
        }
      } else {
        console.log(`📋 [ControllerAppointmentForm] ${categoryCd === 'B09002' ? 'Individual' : 'Corporate'} Controller용 문서가 없음`);
      }
    }
  } catch (error) {
    console.error('Failed to auto-select templates:', error);
  }
};

// ========== Entity ==========
const entity = ref('');

// ========== 개인 Controller 추가 폼 ==========
const showIndividualAddForm = ref(false);
const showNewContactModal = ref(false);

// 개인 Controller 폼 데이터
const individualForm = ref({
  controllerName: '', // CONTACT_CD
  identificationType: '', // ID_TYPE_CD
  identificationNumber: '', // CONTACT_REG_NO
  aliases: '', // ALIASES
  nationality: '', // NATIONALITY_CD
  dateOfBirth: '', // DATE_OF_BIRTH
  emailAddress: '', // CONTACT_EMAIL
  countryCode: '', // COUNTRY_CD
  areaCode: '', // AREA_CODE
  contactNumber: '', // MOBILE_NO
  addressType: '', // ADDRESS_TYPE_CD
  postalCode: '', // POSTAL_CODE
  blockHouseNo: '', // BLOCK_NO
  streetName: '', // STREET_NAME
  buildingName: '', // BUILDING_NAME
  level: '', // ADDR_LEVEL
  unit: '', // ADDR_UNIT
  foreignAddressLine1: '', // FOREIGN_ADDRESS_LINE_1
  foreignAddressLine2: '', // FOREIGN_ADDRESS_LINE_2
  // Contact Address (대체 주소)
  alternateAddressType: '', // ALTERNATE_ADDRESS_CD
  altPostalCode: '', // ALT_POSTAL_CODE
  altBlockHouseNo: '', // ALT_BLOCK_NO
  altStreetName: '', // ALT_STREET_NAME
  altBuildingName: '', // ALT_BUILDING_NAME
  altLevel: '', // ALT_ADDR_LEVEL
  altUnit: '', // ALT_ADDR_UNIT
  altForeignAddressLine1: '', // ALT_FOREIGN_ADDRESS_LINE_1
  altForeignAddressLine2: '', // ALT_FOREIGN_ADDRESS_LINE_2
  dateOfBecoming: '', // BECOMING_DATE
  confirmationByController: 'No' // CONFIRMATION_CONTROLLER (기본값: No)
});

// 개인 Contact 목록 (B09002만)
const individualContactOptions = ref<Array<{ title: string; value: string }>>([]);
const originalIndividualContactOptions = ref<Array<{ title: string; value: string }>>([]); // 원본 contact 목록 (정렬 전)
const isLoadingContacts = ref(false);

// 개인 Controller Name 표시용 computed
const individualControllerNameDisplay = computed(() => {
  if (!individualForm.value.controllerName) return '';
  const selectedOption = individualContactOptions.value.find(opt => opt.value === individualForm.value.controllerName);
  return selectedOption?.title || '';
});

// 개인 Controller Contact Address Type (대체 주소 타입)
const individualContactAddressTypeCd = computed({
  get: () => {
    return individualForm.value.alternateAddressType || individualForm.value.addressType || 'B07001';
  },
  set: (value: string) => {
    individualForm.value.alternateAddressType = value;
  }
});

// 개인 Controller Contact Address가 Local인지 확인
const isIndividualContactAddressLocal = computed(() => {
  return individualContactAddressTypeCd.value === 'B07001';
});

// ========== 법인 Controller 추가 폼 ==========
const showCorporateAddForm = ref(false);
const showNewCorporateContactModal = ref(false);

// 법인 Controller 폼 데이터
const corporateForm = ref({
  controllerName: '', // CONTACT_CD
  uen: '', // REG_NO (CONTACT_REG_NO)
  legalForm: '', // LEGAL_FORM
  jurisdiction: '', // JURISDICTION
  incorporationStatute: '', // INCORPORATION_STATUTE
  corporateEntityRegister: '', // CORPORATE_ENTITY_REGISTER
  emailAddress: '', // CONTACT_EMAIL
  countryCode: '', // COUNTRY_CD
  areaCode: '', // AREA_CODE
  contactNumber: '', // MOBILE_NO
  addressType: '', // ADDRESS_TYPE_CD
  postalCode: '', // POSTAL_CODE
  blockHouseNo: '', // BLOCK_NO
  streetName: '', // STREET_NAME
  buildingName: '', // BUILDING_NAME
  level: '', // ADDR_LEVEL
  unit: '', // ADDR_UNIT
  foreignAddressLine1: '', // FOREIGN_ADDRESS_LINE_1
  foreignAddressLine2: '', // FOREIGN_ADDRESS_LINE_2
  // Contact Address (대체 주소)
  alternateAddressType: '', // ALTERNATE_ADDRESS_CD
  altPostalCode: '', // ALT_POSTAL_CODE
  altBlockHouseNo: '', // ALT_BLOCK_NO
  altStreetName: '', // ALT_STREET_NAME
  altBuildingName: '', // ALT_BUILDING_NAME
  altLevel: '', // ALT_ADDR_LEVEL
  altUnit: '', // ALT_ADDR_UNIT
  altForeignAddressLine1: '', // ALT_FOREIGN_ADDRESS_LINE_1
  altForeignAddressLine2: '', // ALT_FOREIGN_ADDRESS_LINE_2
  dateOfBecoming: '', // BECOMING_DATE
  confirmationByController: 'No' // CONFIRMATION_CONTROLLER (기본값: No)
});

// 법인 Contact 목록 (B09001만)
const corporateContactOptions = ref<Array<{ title: string; value: string }>>([]);
const isLoadingCorporateContacts = ref(false);

// 법인 Controller Name 표시용 computed
const corporateControllerNameDisplay = computed(() => {
  if (!corporateForm.value.controllerName) return '';
  const selectedOption = corporateContactOptions.value.find(opt => opt.value === corporateForm.value.controllerName);
  return selectedOption?.title || '';
});

// 법인 Controller Contact Address Type (대체 주소 타입)
const corporateContactAddressTypeCd = computed({
  get: () => {
    return corporateForm.value.alternateAddressType || corporateForm.value.addressType || 'B07001';
  },
  set: (value: string) => {
    corporateForm.value.alternateAddressType = value;
  }
});

// 법인 Controller Contact Address가 Local인지 확인
const isCorporateContactAddressLocal = computed(() => {
  return corporateContactAddressTypeCd.value === 'B07001';
});

// L_OFFICER 테이블의 Officer 정보 (entity_cd별) - 개인 Controller Name 목록 상단에 표시될 contact_cd
const individualPriorityContactCds = ref<Set<string>>(new Set());
// L_SHAREHOLDER_DETAIL 테이블의 주주 정보 (entity_cd별) - Officer 아래에 표시될 contact_cd
const individualShareholderContactCds = ref<Set<string>>(new Set());

// 자동 바인딩된 필드 추적 (개인)
const autoBoundFields = ref<Record<string, boolean>>({
  identificationNumber: false,
  aliases: false,
  nationality: false,
  dateOfBirth: false,
  emailAddress: false,
  countryCode: false,
  areaCode: false,
  contactNumber: false,
  addressType: false,
  postalCode: false,
  blockHouseNo: false,
  streetName: false,
  buildingName: false,
  level: false,
  unit: false,
  foreignAddressLine1: false,
  foreignAddressLine2: false
});

// 자동 바인딩된 필드 추적 (법인)
const corporateAutoBoundFields = ref<Record<string, boolean>>({
  uen: false,
  legalForm: false,
  jurisdiction: false,
  incorporationStatute: false,
  corporateEntityRegister: false,
  emailAddress: false,
  countryCode: false,
  areaCode: false,
  contactNumber: false,
  addressType: false,
  postalCode: false,
  blockHouseNo: false,
  streetName: false,
  buildingName: false,
  level: false,
  unit: false,
  foreignAddressLine1: false,
  foreignAddressLine2: false
});

// 데이터 복원 중 플래그 (setFormDataFromInitialData에서 사용)
const isRestoringData = ref(false);

// 날짜 필드 composable
const {
  dateValue: dateOfBirthValue,
  dateDisplay: dateOfBirthDisplay,
  datePickerValue: dateOfBirthPickerValue,
  dateMenu: dateOfBirthMenu,
  datePlaceholder,
  datePickerProps
} = useDateField();

const dateOfBecomingField = useDateField();

// Jurisdiction 기반 포맷으로 dateDisplay 덮어쓰기
const dateOfBecomingDisplay = computed(() => formatDateByJurisdiction(dateOfBecomingField.dateValue.value));
const dateOfBecomingMenu = computed({
  get: () => dateOfBecomingField.dateMenu.value,
  set: (val: boolean) => { dateOfBecomingField.dateMenu.value = val; }
});
const dateOfBecomingPickerValue = computed({
  get: () => dateOfBecomingField.datePickerValue.value,
  set: (val: Date | null) => { dateOfBecomingField.datePickerValue.value = val; }
});

// 오늘 날짜 (미래 날짜 선택 방지용)
const todayDate = computed(() => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
});

// 옵션 데이터
const identificationTypeOptions = ref<Array<{ title: string; value: string }>>([]);
const nationalityOptions = ref<Array<{ title: string; value: string }>>([]);
const countryOptions = ref<Array<{ title: string; value: string }>>([]);
const addressTypeOptions = ref<Array<{ title: string; value: string }>>([]);
const jurisdictionOptions = ref<Array<{ title: string; value: string }>>([]);

// ========== Controller 목록 ==========
interface Controller {
  REGISTRABLE_CONTROLLER_ID: string;
  FULL_NAME: string;
  CATEGORY_CD: string;
  CONTACT_REG_NO?: string;
  ID_TYPE?: string;
  NATIONALITY_SCRAPING?: string;
  NATIONALITY_CD?: string;
  REG_NO?: string;
  JURISDICTION_SCRAPING?: string;
  JURISDICTION?: string;
  BECOMING_DATE?: string;
  DATE_OF_BIRTH?: string;
  ADDRESS_TYPE_CD?: string;
  POSTAL_CODE?: string;
  BLOCK_NO?: string;
  STREET_NAME?: string;
  BUILDING_NAME?: string;
  ADDR_LEVEL?: string;
  ADDR_UNIT?: string;
  FOREIGN_ADDRESS_LINE_1?: string;
  FOREIGN_ADDRESS_LINE_2?: string;
  REGISTER_TYPE?: string;
  LEGAL_FORM?: string;
  INCORPORATION_STATUTE?: string;
  CORPORATE_ENTITY_REGISTER?: string;
  ALIASES?: string;
  [key: string]: any;
}

const controllers = ref<Controller[]>([]);
const isLoadingControllers = ref(false);

// 개인 컨트롤러 (B09002)
const individualControllers = computed(() => {
  return controllers.value.filter(c => c.CATEGORY_CD === 'B09002');
});

// 법인 컨트롤러 (B09001)
const corporateControllers = computed(() => {
  return controllers.value.filter(c => c.CATEGORY_CD === 'B09001');
});

// 아코디언 확장/축소 상태
const individualExpandedPanels = ref<string[]>([]);
const corporateExpandedPanels = ref<string[]>([]);

// ID_TYPE 코드명 매핑 (B03)
const idTypeCodeNameMap = ref<Map<string, string>>(new Map());

// ID_TYPE 코드명 로드
const loadIdTypeCodeNames = async () => {
  try {
    const response = await baseCodeApi.getCodesByMainCode('CM', 'B03', undefined);
    const codes = (response as any).codes || response.data?.codes || [];
    codes.forEach((code: any) => {
      const totalCode = String(code.totalCode || code.TOTAL_CODE || '').trim();
      const codeName = String(code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '').trim();
      if (totalCode && codeName) {
        idTypeCodeNameMap.value.set(totalCode, codeName);
      }
    });
    console.log('✅ ID Type Code Names loaded:', idTypeCodeNameMap.value);
  } catch (error) {
    console.error('❌ Failed to load ID Type Code Names:', error);
  }
};

// ID_TYPE 코드명 조회
const getIdTypeCodeName = (code: string): string => {
  if (!code) return '';
  return idTypeCodeNameMap.value.get(code) || code;
};

// Nationality 코드명 매핑 (B04)
const nationalityCodeNameMap = ref<Map<string, string>>(new Map());

// Nationality 코드명 로드
const loadNationalityCodeNames = async () => {
  try {
    const response = await baseCodeApi.getCodesByMainCode('CM', 'B04', undefined);
    const codes = (response as any).codes || response.data?.codes || [];
    codes.forEach((code: any) => {
      const totalCode = String(code.totalCode || code.TOTAL_CODE || '').trim();
      const codeName = String(code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '').trim();
      if (totalCode && codeName) {
        nationalityCodeNameMap.value.set(totalCode, codeName);
      }
    });
    console.log('✅ Nationality Code Names loaded:', nationalityCodeNameMap.value);
  } catch (error) {
    console.error('❌ Failed to load Nationality Code Names:', error);
  }
};

// Nationality 코드명 조회
const getNationalityCodeName = (code: string): string => {
  if (!code) return '';
  return nationalityCodeNameMap.value.get(code) || code;
};

// Jurisdiction 코드명 매핑 (B15)
const jurisdictionCodeNameMap = ref<Map<string, string>>(new Map());

// Jurisdiction 코드명 로드
const loadJurisdictionCodeNames = async () => {
  try {
    const response = await baseCodeApi.getCodesByMainCode('CM', 'B15', undefined);
    const codes = (response as any).codes || response.data?.codes || [];
    codes.forEach((code: any) => {
      const totalCode = String(code.totalCode || code.TOTAL_CODE || '').trim();
      const codeName = String(code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '').trim();
      if (totalCode && codeName) {
        jurisdictionCodeNameMap.value.set(totalCode, codeName);
      }
    });
    console.log('✅ Jurisdiction Code Names loaded:', jurisdictionCodeNameMap.value);
  } catch (error) {
    console.error('❌ Failed to load Jurisdiction Code Names:', error);
  }
};

// Jurisdiction 코드명 조회
const getJurisdictionCodeName = (code: string): string => {
  if (!code) return '';
  return jurisdictionCodeNameMap.value.get(code) || code;
};

// 날짜 포맷팅 (YYYY-MM-DD 형식)
const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return formatDateByJurisdiction(dateStr);
  } catch {
    return '';
  }
};

// 날짜 포맷팅 (28 May 1977 형식) - 공통 dateUtils의 formatDocDate 사용
const formatDateLong = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';
  return formatDocDate(dateStr);
};

// 주소 포맷팅 (3줄로 표현하기 위해 배열 반환) - 공통 함수 사용
const formatAddressLines = (controller: Controller): string[] => {
  const lines = formatAddressLinesUtil(controller, 3);
  return Array.isArray(lines) ? lines : [];
};

// 기존 formatAddress 함수 유지 (다른 곳에서 사용할 수 있음) - 공통 함수 사용
const formatAddress = (controller: Controller): string => {
  const address = formatAddressUtil(controller, { useComma: true });
  return typeof address === 'string' ? address : '';
};

// individualForm 기반 주소 포맷팅 함수 - 공통 함수 사용
// 개인 컨트롤러 주소 포맷팅 (useAddressFormatter 사용)
const formatControllerAddress = (): string => {
  const addressInfo = {
    ADDRESS_TYPE_CD: individualForm.value.addressType || '',
    BLOCK_NO: individualForm.value.blockHouseNo || '',
    STREET_NAME: individualForm.value.streetName || '',
    ADDR_LEVEL: individualForm.value.level || '',
    ADDR_UNIT: individualForm.value.unit || '',
    BUILDING_NAME: individualForm.value.buildingName || '',
    POSTAL_CODE: individualForm.value.postalCode || '',
    FOREIGN_ADDRESS_LINE_1: individualForm.value.foreignAddressLine1 || '',
    FOREIGN_ADDRESS_LINE_2: individualForm.value.foreignAddressLine2 || ''
  };
  // useAddressFormatter의 formatAddress 함수 사용 (Local/Foreign 자동 구분)
  const address = formatAddressUtil(addressInfo, { useComma: true });
  return typeof address === 'string' ? address : '';
};

// 연락처 번호 포맷팅
const formatContactNumber = (controller: Controller): string => {
  const areaCode = controller.AREA_CODE || '';
  const mobileNo = controller.MOBILE_NO || '';
  const countryCode = controller.COUNTRY_CD || '';
  
  if (mobileNo) {
    if (countryCode && !mobileNo.startsWith('+')) {
      return `+${countryCode} ${mobileNo}`;
    }
    if (areaCode) {
      return `${areaCode} ${mobileNo}`;
    }
    return mobileNo;
  }
  
  return '';
};

// E-Filing 모드용 Controller 정보 (initialData에서 가져온 정보)
const eFilingController = ref<Controller | null>(null);

const LEGACY_PRESET_EFILING_CONTACT_CD = '__PRESET_EFILING_CONTACT__';

function applyIndividualFormFieldsFromContactSnapshot(
  cd: Record<string, any>,
  formData: Record<string, any>,
): void {
  individualForm.value.identificationType = cd.idTypeCd || cd.ID_TYPE_CD || '';
  individualForm.value.identificationNumber = cd.contactRegNo || cd.CONTACT_REG_NO || '';
  individualForm.value.aliases = cd.aliases || cd.ALIASES || '';
  individualForm.value.nationality = cd.nationalityCd || cd.NATIONALITY_CD || '';
  if (cd.dateOfBirth || cd.DATE_OF_BIRTH) {
    const dobRaw = cd.dateOfBirth || cd.DATE_OF_BIRTH || '';
    const dob = String(dobRaw).match(/^\d{4}-\d{2}-\d{2}/)
      ? String(dobRaw).substring(0, 10)
      : (() => {
        const m = String(dobRaw).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (!m) return String(dobRaw).substring(0, 10);
        return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
      })();
    individualForm.value.dateOfBirth = dob;
    dateOfBirthValue.value = dob;
  }
  const nationalityText = String(
    cd.nationalityName || cd.NATIONALITY_NAME || formData.controller_nationality || formData.controllerNationality || '',
  ).trim();
  if (!individualForm.value.nationality && nationalityText) {
    individualForm.value.nationality = nationalityText;
  }
  individualForm.value.addressType = cd.addressTypeCd || cd.ADDRESS_TYPE_CD || '';
  const residentialLine = String(
    cd.primaryAddress || cd.foreignAddressLine1 || cd.FOREIGN_ADDRESS_LINE_1
    || formData.controller_residential_address || formData.controllerResidentialAddress || '',
  ).trim();
  if (residentialLine && !individualForm.value.foreignAddressLine1 && !individualForm.value.postalCode) {
    individualForm.value.foreignAddressLine1 = residentialLine;
    if (!individualForm.value.addressType) {
      individualForm.value.addressType = 'B07002';
    }
  }
  individualForm.value.postalCode = cd.postalCode || cd.POSTAL_CODE || '';
  individualForm.value.blockHouseNo = cd.blockNo || cd.BLOCK_NO || '';
  individualForm.value.streetName = cd.streetName || cd.STREET_NAME || '';
  individualForm.value.buildingName = cd.buildingName || cd.BUILDING_NAME || '';
  individualForm.value.level = cd.addrLevel || cd.ADDR_LEVEL || '';
  individualForm.value.unit = cd.addrUnit || cd.ADDR_UNIT || '';
  individualForm.value.foreignAddressLine1 =
    cd.foreignAddressLine1 || cd.FOREIGN_ADDRESS_LINE_1 || individualForm.value.foreignAddressLine1 || '';
  individualForm.value.foreignAddressLine2 =
    cd.foreignAddressLine2 || cd.FOREIGN_ADDRESS_LINE_2 || individualForm.value.foreignAddressLine2 || '';
  individualForm.value.alternateAddressType = cd.alternateAddressCd || cd.ALT_ADDRESS_TYPE_CD || '';
  individualForm.value.altPostalCode = cd.altPostalCode || cd.ALT_POSTAL_CODE || '';
  individualForm.value.altBlockHouseNo = cd.altBlockNo || cd.ALT_BLOCK_NO || '';
  individualForm.value.altStreetName = cd.altStreetName || cd.ALT_STREET_NAME || '';
  individualForm.value.altBuildingName = cd.altBuildingName || cd.ALT_BUILDING_NAME || '';
  individualForm.value.altLevel = cd.altAddrLevel || cd.ALT_ADDR_LEVEL || '';
  individualForm.value.altUnit = cd.altAddrUnit || cd.ALT_ADDR_UNIT || '';
  individualForm.value.altForeignAddressLine1 = cd.altForeignAddressLine1 || cd.ALT_FOREIGN_ADDRESS_LINE_1 || '';
  individualForm.value.altForeignAddressLine2 = cd.altForeignAddressLine2 || cd.ALT_FOREIGN_ADDRESS_LINE_2 || '';
  if (!individualForm.value.countryCode) {
    individualForm.value.countryCode = cd.countryCd || cd.COUNTRY_CD || '';
  }
  individualForm.value.areaCode = cd.areaCode || cd.AREA_CODE || '';
  individualForm.value.contactNumber = cd.mobileNo || cd.MOBILE_NO || '';
  individualForm.value.emailAddress = cd.contactEmail || cd.CONTACT_EMAIL || '';
  const dateOfBecomingVal = cd.dateOfBecoming || cd.DATE_OF_BECOMING
    || formData.dateOfAppointed?.value || formData.dateOfAppointed
    || formData.dateOfBecoming?.value || formData.dateOfBecoming
    || formData.controller_became_date || formData.controllerBecameDate || '';
  if (dateOfBecomingVal) {
    dateOfBecomingField.dateValue.value = dateOfBecomingVal;
    individualForm.value.dateOfBecoming = dateOfBecomingVal;
  }
  const confirmationVal = cd.confirmationByController || cd.CONFIRMATION_BY_CONTROLLER || formData.confirmationByRegistrableController;
  individualForm.value.confirmationByController = confirmationVal === 'true' || confirmationVal === true || confirmationVal === 'Yes' ? 'Yes' : 'No';
}

function setIndividualControllerNameFromPreset(
  formData: Record<string, any>,
  cd: Record<string, any>,
  resolvedControllerCd: string,
  controllerCd: string,
): void {
  const presetOpt = formData.presetControllerContactOption as { title?: string; value?: string } | null | undefined;
  const displayName = String(
    cd.contactName || cd.CONTACT_NAME || formData.controller_full_name || formData.controllerFullName || '',
  ).trim();
  const presetValue = resolvedControllerCd || controllerCd || cd.contactCd || cd.CONTACT_CD || '';
  const opt = presetOpt?.title && presetOpt?.value
    ? { title: String(presetOpt.title), value: String(presetOpt.value) }
    : displayName && presetValue
      ? { title: displayName, value: presetValue }
      : displayName
        ? { title: displayName, value: LEGACY_PRESET_EFILING_CONTACT_CD }
        : null;
  if (opt) {
    if (!individualContactOptions.value.some((o) => o.value === opt.value)) {
      individualContactOptions.value = [opt, ...individualContactOptions.value];
    }
    individualForm.value.controllerName = opt.value;
  } else {
    individualForm.value.controllerName = presetValue;
  }
}

/** E-Filing: Controller Name(CONTACT_CD)만 설정 → watch 가 getContactById 로 나머지 필드 자동 채움 */
async function restoreEfilingViaContactNameLookup(formData: Record<string, any>) {
  if (!props.eFilingMode) return;

  const entityCd = String(formData.entity || formData.entityCd || entity.value || '').trim();
  if (!entityCd) return;

  await loadIndividualContacts();
  await loadIndividualPriorityContacts(entityCd);

  const fullName = String(
    formData.controller_full_name
    ?? formData.controllerFullName
    ?? formData.contactData?.contactName
    ?? formData.contactData?.CONTACT_NAME
    ?? '',
  ).trim();

  let contactCd = String(
    formData.contactCd
    ?? formData.contact_cd
    ?? formData.controllerCd
    ?? formData.contactData?.contactCd
    ?? formData.contactData?.CONTACT_CD
    ?? '',
  ).trim();
  if (contactCd === LEGACY_PRESET_EFILING_CONTACT_CD) contactCd = '';

  const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!contactCd && fullName) {
    const target = norm(fullName);
    const pool = originalIndividualContactOptions.value.filter(
      (o) => o.value && o.value !== 'divider',
    );
    const exact = pool.find((o) => norm(o.title) === target);
    const partial = pool.find(
      (o) => norm(o.title).includes(target) || target.includes(norm(o.title)),
    );
    contactCd = exact?.value || partial?.value || '';
  }

  showIndividualAddForm.value = true;

  const becameRaw = String(
    formData.controller_became_date
    ?? formData.dateOfBecoming
    ?? formData.dateOfAppointed?.value
    ?? formData.contactData?.dateOfBecoming
    ?? '',
  ).trim();
  if (becameRaw) {
    const became = becameRaw.match(/^\d{4}-\d{2}-\d{2}/)
      ? becameRaw.substring(0, 10)
      : (() => {
        const m = becameRaw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (!m) return becameRaw;
        return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
      })();
    dateOfBecomingField.dateValue.value = became;
    individualForm.value.dateOfBecoming = became;
  }

  if (!contactCd) {
    if (individualForm.value.controllerName === LEGACY_PRESET_EFILING_CONTACT_CD) return;
    const cd = formData.contactData;
    if (cd && String(cd.CONTACT_NAME || cd.contactName || '').trim()) return;
    console.warn('[ControllerAppointmentForm] e-filing restore: contactCd not resolved for', fullName);
    return;
  }

  isRestoringData.value = false;
  individualForm.value.controllerName = contactCd;
}

/** E-Filing: Corporate controller — preset B09001, 법인 폼 유지 */
async function restoreEfilingViaCorporateContactLookup(formData: Record<string, any>) {
  if (!props.eFilingMode) return;

  await loadCorporateContacts();

  const corporateName = String(
    formData.controller_corporate_name
    ?? formData.controllerCorporateName
    ?? formData.contactData?.contactName
    ?? formData.contactData?.CONTACT_NAME
    ?? '',
  ).trim();

  let contactCd = String(
    formData.contactCd
    ?? formData.contact_cd
    ?? formData.controllerCd
    ?? formData.contactData?.contactCd
    ?? formData.contactData?.CONTACT_CD
    ?? '',
  ).trim();
  if (contactCd === LEGACY_PRESET_EFILING_CONTACT_CD) contactCd = '';

  const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!contactCd && corporateName) {
    const pool = corporateContactOptions.value.filter((o) => o.value && o.value !== 'divider');
    const target = norm(corporateName);
    const exact = pool.find((o) => norm(o.title) === target);
    const partial = pool.find(
      (o) => norm(o.title).includes(target) || target.includes(norm(o.title)),
    );
    contactCd = exact?.value || partial?.value || '';
  }

  showCorporateAddForm.value = true;
  showIndividualAddForm.value = false;

  const cd = formData.contactData || {};
  if (cd.categoryCd || cd.CATEGORY_CD) {
    /* contactData already applied in setFormDataFromInitialData */
  }

  const becameRaw = String(
    formData.controller_became_date
    ?? formData.dateOfBecoming
    ?? formData.dateOfAppointed?.value
    ?? formData.contactData?.dateOfBecoming
    ?? '',
  ).trim();
  if (becameRaw) {
    const became = becameRaw.match(/^\d{4}-\d{2}-\d{2}/)
      ? becameRaw.substring(0, 10)
      : (() => {
        const m = becameRaw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (!m) return becameRaw;
        return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
      })();
    corporateDateOfBecomingField.dateValue.value = became;
    corporateForm.value.dateOfBecoming = became;
  }

  const presetOpt = formData.presetControllerContactOption as { title?: string; value?: string } | null | undefined;
  if (presetOpt?.title && presetOpt?.value) {
    if (!corporateContactOptions.value.some((o) => o.value === presetOpt.value)) {
      corporateContactOptions.value = [
        { title: String(presetOpt.title), value: String(presetOpt.value) },
        ...corporateContactOptions.value,
      ];
    }
  } else if (corporateName && contactCd) {
    if (!corporateContactOptions.value.some((o) => o.value === contactCd)) {
      corporateContactOptions.value = [
        { title: corporateName, value: contactCd },
        ...corporateContactOptions.value,
      ];
    }
  }

  if (!contactCd) {
    if (corporateForm.value.controllerName === LEGACY_PRESET_EFILING_CONTACT_CD) return;
    const cd = formData.contactData;
    if (cd && String(cd.CONTACT_NAME || cd.contactName || '').trim()) {
      const legacyVal = LEGACY_PRESET_EFILING_CONTACT_CD;
      const displayName = String(cd.CONTACT_NAME || cd.contactName || corporateName).trim();
      if (!corporateContactOptions.value.some((o) => o.value === legacyVal)) {
        corporateContactOptions.value = [
          { title: displayName, value: legacyVal },
          ...corporateContactOptions.value,
        ];
      }
      isRestoringData.value = false;
      corporateForm.value.controllerName = legacyVal;
      return;
    }
    console.warn('[ControllerAppointmentForm] e-filing corporate restore: contactCd not resolved for', corporateName);
    return;
  }

  isRestoringData.value = false;
  corporateForm.value.controllerName = contactCd;
}

// initialData에서 폼 데이터 설정 함수
const setFormDataFromInitialData = async (formData: any) => {
  if (!formData) {
    console.warn('⚠️ [ControllerAppointmentForm] setFormDataFromInitialData: formData가 null 또는 undefined입니다');
    return;
  }

  console.log('📋 [ControllerAppointmentForm] setFormDataFromInitialData:', {
    formData,
    hasEntity: !!formData.entity,
    entityValue: formData.entity,
    formDataKeys: Object.keys(formData)
  });

  // Entity 설정 (entity 필드가 있으면 설정)
  if (formData.entity) {
    entity.value = formData.entity;
    console.log('✅ [ControllerAppointmentForm] Entity 설정:', entity.value);
    // Entity 설정 후 Vue 반응성 시스템이 처리될 때까지 대기
    await nextTick();
    // Entity 설정 후 Controller 목록 로드 (E-Filing 모드가 아닐 때만)
    if (!props.eFilingMode) {
      await Promise.all([
        loadControllers(formData.entity),
        loadIndividualPriorityContacts(formData.entity)
      ]);
    }
  } else {
    console.warn('⚠️ [ControllerAppointmentForm] formData.entity가 없습니다:', {
      formData,
      formDataKeys: Object.keys(formData),
      formDataType: typeof formData
    });
  }

  // Controller 정보 설정 (개인 또는 법인)
  // contactData를 우선 확인하여 controllerCd와 categoryCd 추출
  const controllerCd = formData.contactData?.contactCd || formData.contactData?.CONTACT_CD || formData.controllerCd || formData.controllerNameCd || '';
  const categoryCd = formData.contactData?.categoryCd || formData.contactData?.CATEGORY_CD || formData.categoryCd || '';
  
  console.log('📋 [ControllerAppointmentForm] Controller 정보 추출:', {
    controllerCd,
    categoryCd,
    hasControllerCd: !!controllerCd,
    hasCategoryCd: !!categoryCd,
    hasContactData: !!formData.contactData,
    contactDataKeys: formData.contactData ? Object.keys(formData.contactData) : [],
    formDataKeys: Object.keys(formData)
  });
  
  // Contact Data 설정 (controllerName 설정 전에 먼저 설정하여 watch(controllerName)가 DB 조회하지 않도록)
  // OfficerAppointmentForm.vue의 패턴과 동일하게 처리
  if (formData.contactData) {
    console.log('📋 [ControllerAppointmentForm] contactData 발견:', formData.contactData);
    // 데이터 복원 모드 시작
    isRestoringData.value = true;
    
    try {
      const cd = formData.contactData;
      const resolvedCategoryCd = cd.categoryCd || cd.CATEGORY_CD || categoryCd || 'B09002'; // 기본값: 개인 컨트롤러
      const resolvedControllerCd = cd.contactCd || cd.CONTACT_CD || controllerCd || '';

      console.log('📋 [ControllerAppointmentForm] resolvedCategoryCd:', resolvedCategoryCd, 'resolvedControllerCd:', resolvedControllerCd, 'eFilingMode:', props.eFilingMode);

      if (resolvedCategoryCd === 'B09001') {
        showIndividualAddForm.value = false;
      } else if (resolvedCategoryCd === 'B09002') {
        showCorporateAddForm.value = false;
      }

      // 개인 컨트롤러 (B09002)
      if (resolvedCategoryCd === 'B09002') {
        applyIndividualFormFieldsFromContactSnapshot(cd, formData);
        setIndividualControllerNameFromPreset(formData, cd, resolvedControllerCd, controllerCd);
        if (props.eFilingMode) {
          showIndividualAddForm.value = true;
        }
        await nextTick();
        await new Promise(resolve => setTimeout(resolve, 50));
        console.log('✅ [ControllerAppointmentForm] 개인 컨트롤러 폼 데이터 설정 완료:', {
          controllerName: individualForm.value.controllerName,
          showIndividualAddForm: showIndividualAddForm.value,
          eFilingMode: props.eFilingMode,
        });
      } else if (resolvedCategoryCd === 'B09001') {
      // 법인 컨트롤러 - contactData를 사용하여 폼 설정
          corporateForm.value.uen = cd.uen || cd.UEN || cd.contactRegNo || cd.CONTACT_REG_NO || '';
          corporateForm.value.legalForm = cd.legalForm || cd.LEGAL_FORM || '';
          corporateForm.value.jurisdiction = cd.jurisdiction || cd.JURISDICTION || '';
          corporateForm.value.incorporationStatute = cd.incorporationStatute || cd.INCORPORATION_STATUTE || '';
          corporateForm.value.corporateEntityRegister = cd.corporateEntityRegister || cd.CORPORATE_ENTITY_REGISTER || '';
          corporateForm.value.addressType = cd.addressTypeCd || cd.ADDRESS_TYPE_CD || '';
          corporateForm.value.postalCode = cd.postalCode || cd.POSTAL_CODE || '';
          corporateForm.value.blockHouseNo = cd.blockNo || cd.BLOCK_NO || '';
          corporateForm.value.streetName = cd.streetName || cd.STREET_NAME || '';
          corporateForm.value.buildingName = cd.buildingName || cd.BUILDING_NAME || '';
          corporateForm.value.level = cd.addrLevel || cd.ADDR_LEVEL || '';
          corporateForm.value.unit = cd.addrUnit || cd.ADDR_UNIT || '';
          corporateForm.value.foreignAddressLine1 = cd.foreignAddressLine1 || cd.FOREIGN_ADDRESS_LINE_1 || '';
          corporateForm.value.foreignAddressLine2 = cd.foreignAddressLine2 || cd.FOREIGN_ADDRESS_LINE_2 || '';
          corporateForm.value.alternateAddressType = cd.alternateAddressCd || cd.ALT_ADDRESS_TYPE_CD || cd.addressTypeCd || cd.ADDRESS_TYPE_CD || '';
          corporateForm.value.altPostalCode = cd.altPostalCode || cd.ALT_POSTAL_CODE || '';
          corporateForm.value.altBlockHouseNo = cd.altBlockNo || cd.ALT_BLOCK_NO || '';
          corporateForm.value.altStreetName = cd.altStreetName || cd.ALT_STREET_NAME || '';
          corporateForm.value.altBuildingName = cd.altBuildingName || cd.ALT_BUILDING_NAME || '';
          corporateForm.value.altLevel = cd.altAddrLevel || cd.ALT_ADDR_LEVEL || '';
          corporateForm.value.altUnit = cd.altAddrUnit || cd.ALT_ADDR_UNIT || '';
          corporateForm.value.altForeignAddressLine1 = cd.altForeignAddressLine1 || cd.ALT_FOREIGN_ADDRESS_LINE_1 || '';
          corporateForm.value.altForeignAddressLine2 = cd.altForeignAddressLine2 || cd.ALT_FOREIGN_ADDRESS_LINE_2 || '';
          corporateForm.value.countryCode = cd.countryCd || cd.COUNTRY_CD || '';
          corporateForm.value.areaCode = cd.areaCode || cd.AREA_CODE || '';
          corporateForm.value.contactNumber = cd.mobileNo || cd.MOBILE_NO || '';
          corporateForm.value.emailAddress = cd.contactEmail || cd.CONTACT_EMAIL || '';
          // dateOfBecoming은 contactData에서 우선 확인, 없으면 formData에서 확인
          const dateOfBecomingVal = cd.dateOfBecoming || cd.DATE_OF_BECOMING || formData.dateOfAppointed?.value || formData.dateOfAppointed || formData.dateOfBecoming?.value || formData.dateOfBecoming || '';
          if (dateOfBecomingVal) {
            corporateDateOfBecomingField.dateValue.value = dateOfBecomingVal;
            corporateForm.value.dateOfBecoming = dateOfBecomingVal;
          }
          // confirmationByController도 contactData에서 우선 확인
          const confirmationVal = cd.confirmationByController || cd.CONFIRMATION_BY_CONTROLLER || formData.confirmationByRegistrableController;
          corporateForm.value.confirmationByController = confirmationVal === 'true' || confirmationVal === true || confirmationVal === 'Yes' ? 'Yes' : 'No';

          const presetOpt = formData.presetControllerContactOption as { title?: string; value?: string } | null | undefined;
          const displayName = String(
            cd.contactName || cd.CONTACT_NAME || formData.controller_corporate_name || formData.controllerCorporateName || '',
          ).trim();
          const presetValue = resolvedControllerCd || controllerCd || cd.contactCd || cd.CONTACT_CD || '';
          const opt = presetOpt?.title && presetOpt?.value
            ? { title: String(presetOpt.title), value: String(presetOpt.value) }
            : displayName && presetValue
              ? { title: displayName, value: presetValue }
              : displayName
                ? { title: displayName, value: LEGACY_PRESET_EFILING_CONTACT_CD }
                : null;
          if (opt) {
            if (!corporateContactOptions.value.some((o) => o.value === opt.value)) {
              corporateContactOptions.value = [opt, ...corporateContactOptions.value];
            }
            corporateForm.value.controllerName = opt.value;
          } else {
            corporateForm.value.controllerName = presetValue;
          }

          // E-Filing 모드에서는 폼을 표시 (contactData가 있으면 항상 표시)
          if (props.eFilingMode) {
            showCorporateAddForm.value = true;
            console.log('✅ [ControllerAppointmentForm] showCorporateAddForm.value = true 설정됨 (E-Filing 모드)');
          }
          
          // watch가 실행될 때까지 대기
          await nextTick();
          await new Promise(resolve => setTimeout(resolve, 50));
          
          console.log('✅ [ControllerAppointmentForm] 법인 컨트롤러 폼 데이터 설정 완료:', {
            controllerName: corporateForm.value.controllerName,
            showCorporateAddForm: showCorporateAddForm.value,
            eFilingMode: props.eFilingMode
          });
      }
    } finally {
      // 데이터 복원 모드 종료
      isRestoringData.value = false;
    }
    if (props.eFilingMode) {
      const hasPresetContactSnapshot = Boolean(
        String(formData.contactData?.CONTACT_NAME || formData.contactData?.contactName || '').trim(),
      );
      if (!hasPresetContactSnapshot) {
        const cat = String(
          formData.contactData?.categoryCd
          ?? formData.contactData?.CATEGORY_CD
          ?? formData.categoryCd
          ?? formData.categoryCode
          ?? '',
        ).trim();
        if (cat === 'B09001') {
          await restoreEfilingViaCorporateContactLookup(formData);
        } else {
          await restoreEfilingViaContactNameLookup(formData);
        }
      }
    }
    return;
  }
  
  // contactData가 없으면 기존 방식대로 처리
  if (controllerCd && categoryCd) {
    // 데이터 복원 모드 시작
    isRestoringData.value = true;
    
    try {
    if (props.eFilingMode) {
        // contactData가 없으면 기존 방식대로 Contact API 호출
      // Contact 정보를 API로부터 로드
      let contact: any = null;
      try {
        const contactResponse: any = await contactsApi.getContactById(controllerCd);
        contact = contactResponse?.data?.contact || contactResponse?.contact || contactResponse?.data || contactResponse;
        
        console.log('📋 [ControllerAppointmentForm] E-Filing 모드 Contact 정보 로드:', contact);
      } catch (error) {
        console.error('❌ [ControllerAppointmentForm] Contact 정보 로드 실패:', error);
      }
      
      // formData에서 fallback 값 가져오기
      const getValue = (field: any) => {
        if (field && typeof field === 'object' && 'value' in field) {
          return field.value;
        }
        return field || '';
      };
      
      // Contact 정보를 Controller 객체로 변환 (Contact API 우선, 없으면 formData 사용)
      const controllerInfo: Controller = {
        REGISTRABLE_CONTROLLER_ID: `EFILING_${Date.now()}`,
        FULL_NAME: (contact as any)?.CONTACT_NAME || (contact as any)?.contactName || getValue(formData.controllerName) || '',
        CATEGORY_CD: categoryCd,
        CONTACT_CD: controllerCd, // Contact Detail 모달을 열기 위한 CONTACT_CD 설정
        CONTACT_REG_NO: (contact as any)?.CONTACT_REG_NO || (contact as any)?.contactRegNo || getValue(formData.registrationIdNo) || getValue(formData.identityCardOrPassport) || '',
        ID_TYPE: (contact as any)?.ID_TYPE_CD || (contact as any)?.idTypeCd || getValue(formData.identificationType) || getValue(formData.idTypeCd) || '',
        NATIONALITY_CD: (contact as any)?.NATIONALITY_CD || (contact as any)?.nationalityCd || getValue(formData.nationalityCd) || '',
        REG_NO: (contact as any)?.REG_NO || (contact as any)?.regNo || getValue(formData.registrationIdNo) || getValue(formData.acraUen) || getValue(formData.nonAcraUen) || '',
        JURISDICTION: (contact as any)?.JURISDICTION || (contact as any)?.jurisdiction || getValue(formData.jurisdiction) || '',
        BECOMING_DATE: getValue(formData.dateOfAppointed) || getValue(formData.dateOfBecoming) || '',
        DATE_OF_BIRTH: (contact as any)?.DATE_OF_BIRTH || (contact as any)?.dateOfBirth || (getValue(formData.dateOfBirth) || getValue(formData.birthDate) || '').substring(0, 10),
        ADDRESS_TYPE_CD: (contact as any)?.ADDRESS_TYPE_CD || (contact as any)?.addressTypeCd || getValue(formData.addressTypeCd) || '',
        POSTAL_CODE: (contact as any)?.POSTAL_CODE || (contact as any)?.postalCode || getValue(formData.postalCode) || '',
        BLOCK_NO: (contact as any)?.BLOCK_NO || (contact as any)?.blockNo || getValue(formData.blockHouseNo) || '',
        STREET_NAME: (contact as any)?.STREET_NAME || (contact as any)?.streetName || getValue(formData.streetName) || '',
        BUILDING_NAME: (contact as any)?.BUILDING_NAME || (contact as any)?.buildingName || getValue(formData.buildingEstateName) || getValue(formData.buildingName) || '',
        ADDR_LEVEL: (contact as any)?.ADDR_LEVEL || (contact as any)?.addrLevel || getValue(formData.level) || getValue(formData.entityAddrLevel) || '',
        ADDR_UNIT: (contact as any)?.ADDR_UNIT || (contact as any)?.addrUnit || getValue(formData.unit) || getValue(formData.entityAddrUnit) || '',
        FOREIGN_ADDRESS_LINE_1: (contact as any)?.FOREIGN_ADDRESS_LINE_1 || (contact as any)?.foreignAddressLine1 || getValue(formData.foreignAddressLine1) || '',
        FOREIGN_ADDRESS_LINE_2: (contact as any)?.FOREIGN_ADDRESS_LINE_2 || (contact as any)?.foreignAddressLine2 || getValue(formData.foreignAddressLine2) || '',
        CONTACT_EMAIL: (contact as any)?.CONTACT_EMAIL || (contact as any)?.contactEmail || getValue(formData.emailAddress) || '',
        COUNTRY_CD: (contact as any)?.COUNTRY_CD || (contact as any)?.countryCd || getValue(formData.countryCode) || '',
        AREA_CODE: (contact as any)?.AREA_CODE || (contact as any)?.areaCode || getValue(formData.areaCode) || '',
        MOBILE_NO: (contact as any)?.MOBILE_NO || (contact as any)?.mobileNo || getValue(formData.mobileNo) || '',
        CONFIRMATION_CONTROLLER: getValue(formData.confirmationByRegistrableController) === 'true' || getValue(formData.confirmationByRegistrableController) === true,
        NATIONALITY_SCRAPING: (contact as any)?.NATIONALITY_SCRAPING || (contact as any)?.nationalityScraping || getValue(formData.nationalityBigo1) || getValue(formData.nationalityCodeName) || '',
        ALIASES: (contact as any)?.ALIASES || (contact as any)?.aliases || getValue(formData.aliases) || ''
      };
      eFilingController.value = controllerInfo;
      
      // E-Filing 모드일 때는 controllers 배열에 추가하여 표시
        // categoryCd가 없으면 기본값 사용
        const resolvedCategoryCd = categoryCd || 'B09002'; // 기본값: 개인 컨트롤러
        if (resolvedCategoryCd === 'B09002') {
        controllers.value = [controllerInfo];
        individualExpandedPanels.value = [controllerInfo.REGISTRABLE_CONTROLLER_ID];
          console.log('✅ [ControllerAppointmentForm] 개인 Controller 추가 완료 (Contact API 사용):', {
          controllersLength: controllers.value.length,
          individualControllersLength: individualControllers.value.length,
          expandedPanels: individualExpandedPanels.value,
          controllerInfo
        });
        } else if (resolvedCategoryCd === 'B09001') {
        controllers.value = [controllerInfo];
        corporateExpandedPanels.value = [controllerInfo.REGISTRABLE_CONTROLLER_ID];
          if (props.eFilingMode) {
            showCorporateAddForm.value = true;
            showIndividualAddForm.value = false;
          }
          console.log('✅ [ControllerAppointmentForm] 법인 Controller 추가 완료 (Contact API 사용):', {
          controllersLength: controllers.value.length,
          corporateControllersLength: corporateControllers.value.length,
          expandedPanels: corporateExpandedPanels.value,
          controllerInfo
        });
      }
      
        console.log('✅ [ControllerAppointmentForm] E-Filing Controller 정보 로드 완료 (Contact API 사용):', controllerInfo);
    } else {
      // 일반 모드일 때는 폼에 데이터 설정
      if (categoryCd === 'B09002') {
          // 개인 컨트롤러 - Contact Data를 먼저 설정 (화면에 있는 데이터 기준, 우선순위 높음)
          if (formData.contactData) {
            const cd = formData.contactData;
            // Basic 섹션
            individualForm.value.identificationType = cd.idTypeCd || cd.ID_TYPE_CD || (formData.identityCardOrPassport ? formData.identificationType : '');
            individualForm.value.identificationNumber = cd.contactRegNo || cd.CONTACT_REG_NO || formData.registrationIdNo?.value || formData.registrationIdNo || formData.identityCardOrPassport?.value || formData.identityCardOrPassport || '';
            individualForm.value.aliases = cd.aliases || cd.ALIASES || formData.aliases?.value || formData.aliases || '';
            individualForm.value.nationality = cd.nationalityCd || cd.NATIONALITY_CD || formData.nationalityCd || '';
            if (cd.dateOfBirth || cd.DATE_OF_BIRTH || formData.dateOfBirth?.value || formData.dateOfBirth || formData.birthDate?.value || formData.birthDate) {
              const dob = cd.dateOfBirth || cd.DATE_OF_BIRTH || formData.dateOfBirth?.value || formData.dateOfBirth || formData.birthDate?.value || formData.birthDate;
              const dobFormatted = (dob || '').substring(0, 10);
              individualForm.value.dateOfBirth = dobFormatted;
              dateOfBirthValue.value = dobFormatted;
            }
            // Residential Address 섹션
            individualForm.value.addressType = cd.addressTypeCd || cd.ADDRESS_TYPE_CD || formData.addressTypeCd || '';
            individualForm.value.postalCode = cd.postalCode || cd.POSTAL_CODE || formData.postalCode || '';
            individualForm.value.blockHouseNo = cd.blockNo || cd.BLOCK_NO || formData.blockHouseNo || '';
            individualForm.value.streetName = cd.streetName || cd.STREET_NAME || formData.streetName || '';
            individualForm.value.buildingName = cd.buildingName || cd.BUILDING_NAME || formData.buildingEstateName?.value || formData.buildingEstateName || formData.buildingName?.value || formData.buildingName || '';
            individualForm.value.level = cd.addrLevel || cd.ADDR_LEVEL || formData.level || formData.entityAddrLevel || '';
            individualForm.value.unit = cd.addrUnit || cd.ADDR_UNIT || formData.unit || formData.entityAddrUnit || '';
            individualForm.value.foreignAddressLine1 = cd.foreignAddressLine1 || cd.FOREIGN_ADDRESS_LINE_1 || formData.foreignAddressLine1 || '';
            individualForm.value.foreignAddressLine2 = cd.foreignAddressLine2 || cd.FOREIGN_ADDRESS_LINE_2 || formData.foreignAddressLine2 || '';
            // Contact Address (대체 주소) 섹션
            individualForm.value.alternateAddressType = cd.alternateAddressCd || cd.ALT_ADDRESS_TYPE_CD || '';
            individualForm.value.altPostalCode = cd.altPostalCode || cd.ALT_POSTAL_CODE || '';
            individualForm.value.altBlockHouseNo = cd.altBlockNo || cd.ALT_BLOCK_NO || '';
            individualForm.value.altStreetName = cd.altStreetName || cd.ALT_STREET_NAME || '';
            individualForm.value.altBuildingName = cd.altBuildingName || cd.ALT_BUILDING_NAME || '';
            individualForm.value.altLevel = cd.altAddrLevel || cd.ALT_ADDR_LEVEL || '';
            individualForm.value.altUnit = cd.altAddrUnit || cd.ALT_ADDR_UNIT || '';
            individualForm.value.altForeignAddressLine1 = cd.altForeignAddressLine1 || cd.ALT_FOREIGN_ADDRESS_LINE_1 || '';
            individualForm.value.altForeignAddressLine2 = cd.altForeignAddressLine2 || cd.ALT_FOREIGN_ADDRESS_LINE_2 || '';
            // Contact 섹션
            individualForm.value.countryCode = cd.countryCd || cd.COUNTRY_CD || formData.countryCode || '';
            individualForm.value.areaCode = cd.areaCode || cd.AREA_CODE || formData.areaCode || '';
            individualForm.value.contactNumber = cd.mobileNo || cd.MOBILE_NO || formData.mobileNo?.value || formData.mobileNo || '';
            individualForm.value.emailAddress = cd.contactEmail || cd.CONTACT_EMAIL || formData.emailAddress?.value || formData.emailAddress || '';
          } else {
            // contactData가 없으면 formData의 직접 필드 사용 (fallback)
        individualForm.value.identificationType = formData.identityCardOrPassport ? formData.identificationType : '';
        individualForm.value.identificationNumber = formData.registrationIdNo?.value || formData.registrationIdNo || formData.identityCardOrPassport?.value || formData.identityCardOrPassport || '';
        individualForm.value.aliases = formData.aliases?.value || formData.aliases || '';
        individualForm.value.nationality = formData.nationalityCd || '';
        if (formData.dateOfBirth?.value || formData.dateOfBirth || formData.birthDate?.value || formData.birthDate) {
          const dob = formData.dateOfBirth?.value || formData.dateOfBirth || formData.birthDate?.value || formData.birthDate;
          individualForm.value.dateOfBirth = dob;
          dateOfBirthValue.value = dob;
        }
        individualForm.value.emailAddress = formData.emailAddress?.value || formData.emailAddress || '';
        individualForm.value.countryCode = formData.countryCode || '';
        individualForm.value.areaCode = formData.areaCode || '';
        individualForm.value.contactNumber = formData.mobileNo?.value || formData.mobileNo || '';
        individualForm.value.addressType = formData.addressTypeCd || '';
        individualForm.value.postalCode = formData.postalCode || '';
        individualForm.value.blockHouseNo = formData.blockHouseNo || '';
        individualForm.value.streetName = formData.streetName || '';
        individualForm.value.buildingName = formData.buildingEstateName?.value || formData.buildingEstateName || formData.buildingName?.value || formData.buildingName || '';
        individualForm.value.level = formData.level || formData.entityAddrLevel || '';
        individualForm.value.unit = formData.unit || formData.entityAddrUnit || '';
        individualForm.value.foreignAddressLine1 = formData.foreignAddressLine1 || '';
        individualForm.value.foreignAddressLine2 = formData.foreignAddressLine2 || '';
          }
        if (formData.dateOfAppointed?.value || formData.dateOfAppointed || formData.dateOfBecoming?.value || formData.dateOfBecoming) {
          const dateVal = formData.dateOfAppointed?.value || formData.dateOfAppointed || formData.dateOfBecoming?.value || formData.dateOfBecoming;
          dateOfBecomingField.dateValue.value = dateVal;
          individualForm.value.dateOfBecoming = dateVal;
        }
        individualForm.value.confirmationByController = formData.confirmationByRegistrableController === 'true' || formData.confirmationByRegistrableController === true ? 'Yes' : 'No';
          
          // controllerName을 마지막에 설정 (watch가 실행되지 않도록)
          individualForm.value.controllerName = controllerCd;
          // watch가 실행될 때까지 대기
          await nextTick();
          await new Promise(resolve => setTimeout(resolve, 50));
      } else if (categoryCd === 'B09001') {
          // 법인 컨트롤러 - Contact Data를 먼저 설정 (화면에 있는 데이터 기준, 우선순위 높음)
          if (formData.contactData) {
            const cd = formData.contactData;
            // Basic 섹션
            corporateForm.value.uen = cd.uen || cd.UEN || formData.acrAuen || formData.controller_uen || formData.controllerUen || cd.contactRegNo || cd.CONTACT_REG_NO || formData.nonAcrAuen || '';
            corporateForm.value.legalForm = cd.legalForm || cd.LEGAL_FORM || formData.legalForm?.value || formData.legalForm || '';
            corporateForm.value.jurisdiction = cd.jurisdiction || cd.JURISDICTION || formData.jurisdiction || '';
            corporateForm.value.incorporationStatute = cd.incorporationStatute || cd.INCORPORATION_STATUTE || formData.incorporationStatute?.value || formData.incorporationStatute || '';
            corporateForm.value.corporateEntityRegister = cd.corporateEntityRegister || cd.CORPORATE_ENTITY_REGISTER || formData.corporateEntityRegister?.value || formData.corporateEntityRegister || '';
            // Residential Address 섹션
            corporateForm.value.addressType = cd.addressTypeCd || cd.ADDRESS_TYPE_CD || formData.addressTypeCd || '';
            corporateForm.value.postalCode = cd.postalCode || cd.POSTAL_CODE || formData.postalCode || '';
            corporateForm.value.blockHouseNo = cd.blockNo || cd.BLOCK_NO || formData.blockHouseNo || '';
            corporateForm.value.streetName = cd.streetName || cd.STREET_NAME || formData.streetName || '';
            corporateForm.value.buildingName = cd.buildingName || cd.BUILDING_NAME || formData.buildingEstateName?.value || formData.buildingEstateName || formData.buildingName?.value || formData.buildingName || '';
            corporateForm.value.level = cd.addrLevel || cd.ADDR_LEVEL || formData.level || formData.entityAddrLevel || '';
            corporateForm.value.unit = cd.addrUnit || cd.ADDR_UNIT || formData.unit || formData.entityAddrUnit || '';
            corporateForm.value.foreignAddressLine1 = cd.foreignAddressLine1 || cd.FOREIGN_ADDRESS_LINE_1 || formData.foreignAddressLine1 || '';
            corporateForm.value.foreignAddressLine2 = cd.foreignAddressLine2 || cd.FOREIGN_ADDRESS_LINE_2 || formData.foreignAddressLine2 || '';
            // Contact Address (대체 주소) 섹션
            corporateForm.value.alternateAddressType = cd.alternateAddressCd || cd.ALT_ADDRESS_TYPE_CD || '';
            corporateForm.value.altPostalCode = cd.altPostalCode || cd.ALT_POSTAL_CODE || '';
            corporateForm.value.altBlockHouseNo = cd.altBlockNo || cd.ALT_BLOCK_NO || '';
            corporateForm.value.altStreetName = cd.altStreetName || cd.ALT_STREET_NAME || '';
            corporateForm.value.altBuildingName = cd.altBuildingName || cd.ALT_BUILDING_NAME || '';
            corporateForm.value.altLevel = cd.altAddrLevel || cd.ALT_ADDR_LEVEL || '';
            corporateForm.value.altUnit = cd.altAddrUnit || cd.ALT_ADDR_UNIT || '';
            corporateForm.value.altForeignAddressLine1 = cd.altForeignAddressLine1 || cd.ALT_FOREIGN_ADDRESS_LINE_1 || '';
            corporateForm.value.altForeignAddressLine2 = cd.altForeignAddressLine2 || cd.ALT_FOREIGN_ADDRESS_LINE_2 || '';
            // Contact 섹션
            corporateForm.value.countryCode = cd.countryCd || cd.COUNTRY_CD || formData.countryCode || '';
            corporateForm.value.areaCode = cd.areaCode || cd.AREA_CODE || formData.areaCode || '';
            corporateForm.value.contactNumber = cd.mobileNo || cd.MOBILE_NO || formData.controller_contact_no || formData.controllerContactNo || formData.mobileNo?.value || formData.mobileNo || '';
            corporateForm.value.emailAddress = cd.contactEmail || cd.CONTACT_EMAIL || formData.controller_email || formData.controllerEmail || formData.emailAddress?.value || formData.emailAddress || '';
          } else {
            // contactData가 없으면 formData의 직접 필드 사용 (fallback)
        corporateForm.value.uen = formData.registrationIdNo?.value || formData.registrationIdNo || formData.acraUen?.value || formData.acraUen || formData.nonAcraUen?.value || formData.nonAcraUen || '';
        corporateForm.value.legalForm = formData.legalForm?.value || formData.legalForm || '';
        corporateForm.value.jurisdiction = formData.jurisdiction || '';
        corporateForm.value.incorporationStatute = formData.incorporationStatute?.value || formData.incorporationStatute || '';
        corporateForm.value.corporateEntityRegister = formData.corporateEntityRegister?.value || formData.corporateEntityRegister || '';
        corporateForm.value.emailAddress = formData.controller_email || formData.controllerEmail || formData.emailAddress?.value || formData.emailAddress || '';
        const phoneFb = String(formData.controller_contact_no || formData.controllerContactNo || '').trim();
        if (phoneFb && !corporateForm.value.contactNumber) {
          const parts = phoneFb.split(/\s+/);
          if (parts.length >= 2 && /^\d{1,4}$/.test(parts[0])) {
            corporateForm.value.areaCode = parts[0];
            corporateForm.value.contactNumber = parts.slice(1).join(' ');
          } else {
            corporateForm.value.contactNumber = phoneFb;
          }
        }
        const officeFb = String(formData.controller_registered_office || formData.controllerRegisteredOffice || '').trim();
        if (officeFb && !corporateForm.value.foreignAddressLine1) {
          corporateForm.value.addressType = 'B07002';
          corporateForm.value.foreignAddressLine1 = officeFb;
        }
          }
        if (formData.dateOfAppointed?.value || formData.dateOfAppointed || formData.dateOfBecoming?.value || formData.dateOfBecoming) {
          const dateVal = formData.dateOfAppointed?.value || formData.dateOfAppointed || formData.dateOfBecoming?.value || formData.dateOfBecoming;
          corporateDateOfBecomingField.dateValue.value = dateVal;
          corporateForm.value.dateOfBecoming = dateVal;
        }
        corporateForm.value.confirmationByController = formData.confirmationByRegistrableController === 'true' || formData.confirmationByRegistrableController === true ? 'Yes' : 'No';
          
          // controllerName을 마지막에 설정 (watch가 실행되지 않도록)
          corporateForm.value.controllerName = controllerCd;
          // watch가 실행될 때까지 대기
          await nextTick();
          await new Promise(resolve => setTimeout(resolve, 50));
        }
      }
    } finally {
      // 데이터 복원 모드 종료
      isRestoringData.value = false;
    }
  }
  
  if (props.eFilingMode && formData) {
    const cat = String(
      formData.contactData?.categoryCd
      ?? formData.contactData?.CATEGORY_CD
      ?? formData.categoryCd
      ?? formData.categoryCode
      ?? '',
    ).trim();
    if (cat === 'B09001') {
      await restoreEfilingViaCorporateContactLookup(formData);
    } else {
      await restoreEfilingViaContactNameLookup(formData);
    }
  }

  console.log('✅ [ControllerAppointmentForm] setFormDataFromInitialData 완료');
};

// initialData props가 있으면 자동으로 데이터 설정
watch(() => props.initialData, async (newData) => {
  if (newData?.formData) {
    await setFormDataFromInitialData(newData.formData);
  }
}, { immediate: true });

// Entity 변경 시 Controller 목록 조회 및 개인 Contact 정렬
watch(entity, async (newEntityCd) => {
  if (newEntityCd && !props.eFilingMode) {
    await Promise.all([
      loadControllers(newEntityCd),
      loadIndividualPriorityContacts(newEntityCd)
    ]);
  } else if (!newEntityCd) {
    controllers.value = [];
    individualExpandedPanels.value = [];
    corporateExpandedPanels.value = [];
    individualPriorityContactCds.value.clear();
    individualShareholderContactCds.value.clear();
    // 원본 contact 목록으로 복원
    if (originalIndividualContactOptions.value.length > 0) {
      individualContactOptions.value = [...originalIndividualContactOptions.value];
    }
  }
});

// Controller 목록 조회
const loadControllers = async (entityCd: string) => {
  if (!entityCd || !entityCd.trim()) {
    controllers.value = [];
    return;
  }

  try {
    isLoadingControllers.value = true;
    const response = await apiClient.get(`/startTask/common/controllers-all-by-entity/${entityCd}`);
    
    console.log('🔍 Controller 목록 조회 응답:', response);
    
    // apiClient.get은 response.data를 직접 반환하므로 response가 { success: true, controllers: [...] } 형태
    const responseAny = response as any;
    
    // success가 false인 경우 처리
    if (responseAny.success === false) {
      console.warn('⚠️ Controller 목록 조회 실패 (success: false):', responseAny.error || responseAny.message || 'Unknown error');
      controllers.value = [];
      return;
    }

    // controllers 배열 추출 (백엔드 응답: { success: true, controllers: [...] })
    let controllerList: any[] = [];
    
    if (Array.isArray(responseAny.controllers)) {
      // 구조 1: response.controllers (일반적인 경우)
      controllerList = responseAny.controllers;
    } else if (responseAny.data && Array.isArray(responseAny.data.controllers)) {
      // 구조 2: response.data.controllers (중첩 구조)
      controllerList = responseAny.data.controllers;
    } else if (Array.isArray(responseAny.data)) {
      // 구조 3: response.data가 직접 배열인 경우
      controllerList = responseAny.data;
    } else if (Array.isArray(response)) {
      // 구조 4: response가 직접 배열인 경우
      controllerList = response;
    }
    
    if (controllerList.length > 0) {
      controllers.value = controllerList;
      console.log('✅ Controller 목록 조회 성공:', {
        total: controllers.value.length,
        individual: individualControllers.value.length,
        corporate: corporateControllers.value.length,
        sample: controllers.value[0]
      });
    } else {
      console.log('ℹ️ Controller 목록이 비어있습니다. (정상일 수 있습니다)');
      controllers.value = [];
    }
  } catch (error: any) {
    console.error('❌ Failed to fetch controllers:', error);
    console.error('❌ Error details:', {
      message: error.message,
      response: error.response,
      data: error.response?.data
    });
    controllers.value = [];
  } finally {
    isLoadingControllers.value = false;
  }
};

// Expand All / Collapse All
const expandAllIndividual = () => {
  individualExpandedPanels.value = individualControllers.value.map(c => c.REGISTRABLE_CONTROLLER_ID);
};

const collapseAllIndividual = () => {
  individualExpandedPanels.value = [];
};

const expandAllCorporate = () => {
  corporateExpandedPanels.value = corporateControllers.value.map(c => c.REGISTRABLE_CONTROLLER_ID);
};

const collapseAllCorporate = () => {
  corporateExpandedPanels.value = [];
};

// 개인 컨트롤러 정보 포맷팅 (CONTACT_REG_NO - ID_TYPE - NATIONALITY_SCRAPING - Controller from BECOMING_DATE - Present)
const formatIndividualControllerInfo = (controller: Controller): string => {
  const parts: string[] = [];
  
  if (controller.CONTACT_REG_NO) {
    parts.push(controller.CONTACT_REG_NO);
  }
  
  if (controller.ID_TYPE) {
    const idTypeName = getIdTypeCodeName(controller.ID_TYPE);
    parts.push(idTypeName);
  }
  
  if (controller.NATIONALITY_SCRAPING) {
    parts.push(controller.NATIONALITY_SCRAPING);
  }
  
  const becomingDate = formatDate(controller.BECOMING_DATE);
  if (becomingDate) {
    parts.push(`Controller from ${becomingDate} - Present`);
  }
  
  return parts.length > 0 ? `(${parts.join(' - ')})` : '';
};

// 법인 컨트롤러 정보 포맷팅 (REG_NO - JURISDICTION (CODE_NAME) - Controller from BECOMING_DATE - Present)
const formatCorporateControllerInfo = (controller: Controller): string => {
  const parts: string[] = [];
  
  if (controller.REG_NO) {
    parts.push(controller.REG_NO);
  }
  
  // JURISDICTION 코드를 CODE_NAME으로 변환
  const jurisdictionCd = controller.JURISDICTION || '';
  if (jurisdictionCd) {
    const jurisdictionName = getJurisdictionCodeName(jurisdictionCd);
    if (jurisdictionName) {
      parts.push(jurisdictionName);
    }
  }
  
  const becomingDate = formatDate(controller.BECOMING_DATE);
  if (becomingDate) {
    parts.push(`Controller from ${becomingDate} - Present`);
  }
  
  return parts.length > 0 ? `(${parts.join(' - ')})` : '';
};

// ========== FormData 생성 ==========
// 문서 바인딩 변수 매핑 정보
const docVariableMapping = {
  entityName: 'CompanyName',
  entityRegNo: 'RegistrationNo',
  // Notice 변수들 (기존 유지)
  controllerName: 'NoticeController',
  controllerAddress: 'NoticeControllerAddress',
  nationalityBigo1: 'NoticeControllerNationality',
  registrationIdNo: 'NoticeControllerIdNo',
  dateOfBirth: 'NoticeControllerDateOfBirth',
  // Appoint 변수들 (추가)
  appointControllerName: 'AppointControllerName',
  appointControllerAddress: 'AppointControllerAddress',
  appointControllerAltemateAddress: 'AppointControllerAltemateAddress',
  appointControllerIdNo: 'AppointControllerIdNo',
  appointControllerNationality: 'AppointControllerNationality',
  appointControllerJurisdiction: 'AppointControllerJurisdiction',
  appointControllerDateOfBirth: 'AppointControllerDateOfBirth',
  appointControllerEmail: 'AppointControllerEmail',
  appointControllerContactNo: 'AppointControllerContactNo',
  noticeDate: 'NoticeDate',
  registrableControllerList: 'RegistrableControllerList',
  clientNm: 'SecretaryFirmName',
  jurisdiction: 'AppointControllerJurisdiction',
  controllerStartDate: 'ControllerStartDate',
  corporateControllerLegalForm: 'CorporateControllerLegalForm',
  // 추가 DOC 변수
  incorporatedDate: 'IncorporatedDate'
};

// RegistrableControllerList 생성 함수 (공통 사용)
const formatRegistrableControllerList = (): string => {
  // REGISTER_TYPE = 'A06001'인 컨트롤러만 필터링
  const filteredControllers = controllers.value.filter((c: any) => 
    (c.REGISTER_TYPE === 'A06001' || c.REGISTER_TYPE === null || c.REGISTER_TYPE === undefined)
  );
  
  if (filteredControllers.length === 0) {
    return '';
  }
  
  const controllerListStrings: string[] = [];
  
  filteredControllers.forEach((controller: any) => {
    const categoryCd = controller.CATEGORY_CD || '';
    const lines: string[] = [];
    
    if (categoryCd === 'B09002') {
      // 개인 컨트롤러: Name, Residential Address, Nationality, ID/Passport Identification Number, Date of Birth
      const fullName = controller.FULL_NAME || '';
      if (fullName) {
        lines.push(`Name: ${fullName}`);
      }
      
      // 주소 포맷팅
      const addressLines = formatAddressLines(controller);
      if (addressLines.length > 0) {
        lines.push(`Residential Address: ${addressLines.join(' ')}`);
      }
      
      // Nationality
      const nationalityScraping = controller.NATIONALITY_SCRAPING || '';
      const nationalityCd = controller.NATIONALITY_CD || '';
      if (nationalityScraping || nationalityCd) {
        // Nationality 코드명 조회
        const nationalityDisplay = nationalityScraping || getNationalityCodeName(nationalityCd) || '';
        if (nationalityDisplay) {
          lines.push(`Nationality: ${nationalityDisplay}`);
        }
      }
      
      // ID/Passport Identification Number
      const contactRegNo = controller.CONTACT_REG_NO || '';
      if (contactRegNo) {
        lines.push(`ID/Passport Identification Number: ${contactRegNo}`);
      }
      
      // Date of Birth
      // doc_variables로 매핑되는 registrableControllerList 내부의 날짜도 formatDocDate로 변환
      const dateOfBirth = controller.DATE_OF_BIRTH || '';
      if (dateOfBirth) {
        const formattedDate = formatDocDate(dateOfBirth);
        if (formattedDate) {
          lines.push(`Date of Birth: ${formattedDate}`);
        }
      }
    } else if (categoryCd === 'B09001') {
      // 법인 컨트롤러: Name, UEN, Jurisdiction, Address
      const fullName = controller.FULL_NAME || '';
      if (fullName) {
        lines.push(`Name: ${fullName}`);
      }
      
      // UEN (REG_NO)
      const regNo = controller.REG_NO || '';
      if (regNo) {
        lines.push(`UEN: ${regNo}`);
      }
      
      // Jurisdiction (JURISDICTION 코드를 CODE_NAME으로 변환)
      const jurisdictionCd = controller.JURISDICTION || '';
      if (jurisdictionCd) {
        const jurisdictionName = getJurisdictionCodeName(jurisdictionCd);
        if (jurisdictionName) {
          lines.push(`Jurisdiction: ${jurisdictionName}`);
        }
      }
      
      // Address
      const addressLines = formatAddressLines(controller);
      if (addressLines.length > 0) {
        lines.push(`Address: ${addressLines.join(' ')}`);
      }
    }
    
    if (lines.length > 0) {
      // 실제 줄바꿈 사용 (문서 바인딩 시 이미지처럼 표시되도록)
      controllerListStrings.push(lines.join('\n'));
    }
  });
  
  // 각 컨트롤러를 빈 줄로 구분
  return controllerListStrings.join('\n\n');
};

// 단순 문자열 값 반환 (CreateTaskForm에서 기본 데이터로 사용)
const getFormData = async () => {
  // Entity 정보
  const selectedEntityOption = officerAppointmentStore.entityOptions.find(opt => opt.value === entity.value);
  const entityNameVal = selectedEntityOption?.title || '';
  const selectedEntity = officerAppointmentStore.entities.find((ent: any) => 
    (ent.entityCd || ent.ENTITY_CD) === entity.value
  );
  const entityRegNoVal = selectedEntity?.regNo || selectedEntity?.REG_NO || '';
  
  // Notice Date (공통)
  const getCurrentDate = (): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const noticeDateVal = getCurrentDate();
  
  // 법인 컨트롤러인지 확인 (corporateForm.controllerName이 있으면 법인)
  const isCorporate = !!corporateForm.value.controllerName;
  
  let controllerNameVal = '';
  let controllerCd = '';
  let controllerAddressVal = '';
  let registrationIdNoVal = '';
  let dateOfAppointedVal = '';
  let dateOfBecomingVal = '';
  let emailAddressVal = '';
  let countryCodeVal = '';
  let countryCodeNameFormatted = '';
  let areaCodeVal = '';
  let mobileNoVal = '';
  let categoryCd = '';
  let nationalityCd = '';
  let nationalityBigo1Val = '';
  let nationalityCodeName = '';
  let dateOfBirthVal = '';
  let aliasesVal = '';
  let addressTypeCd = '';
  let addressTypeBigo1Val = '';
  let addressTypeCodeName = '';
  let postalCodeVal = '';
  let blockHouseNoVal = '';
  let streetNameVal = '';
  let levelVal = '';
  let unitVal = '';
  let buildingEstateNameVal = '';
  let foreignAddressLine1Val = '';
  let foreignAddressLine2Val = '';
  let legalFormVal = '';
  let jurisdictionVal = '';
  let incorporationStatuteVal = '';
  let corporateEntityRegisterVal = '';
  let confirmationByRegistrableController = 'false';
  
  if (isCorporate) {
    // 법인 컨트롤러 데이터
    const selectedCorporateControllerOption = corporateContactOptions.value.find((opt: any) => opt.value === corporateForm.value.controllerName);
    controllerNameVal = selectedCorporateControllerOption?.title || '';
    controllerCd = corporateForm.value.controllerName || '';
    controllerAddressVal = formatCorporateControllerAddress();
    registrationIdNoVal = corporateForm.value.uen || ''; // 법인의 경우 UEN
    dateOfAppointedVal = corporateDateOfBecomingField.dateValue.value || '';
    dateOfBecomingVal = dateOfAppointedVal;
    emailAddressVal = corporateForm.value.emailAddress || '';
    countryCodeVal = corporateForm.value.countryCode || '';
    
    // Country 정보
    const selectedCountryOption = countryOptions.value.find((opt: any) => opt.value === corporateForm.value.countryCode);
    const countryCodeName = selectedCountryOption?.title || '';
    const countryBigo3 = (selectedCountryOption as any)?.bigo3 || '';
    const countryCodeNumber = countryBigo3 ? (countryBigo3.startsWith('+') ? countryBigo3.substring(1) : countryBigo3) : '';
    countryCodeNameFormatted = countryCodeName && countryCodeNumber ? `${countryCodeName} +${countryCodeNumber}` : countryCodeName;
    
    areaCodeVal = corporateForm.value.areaCode || '';
    mobileNoVal = corporateForm.value.contactNumber || '';
    categoryCd = 'B09001'; // 법인
    legalFormVal = corporateForm.value.legalForm || '';
    jurisdictionVal = corporateForm.value.jurisdiction || '';
    incorporationStatuteVal = corporateForm.value.incorporationStatute || '';
    corporateEntityRegisterVal = corporateForm.value.corporateEntityRegister || '';
    
    // Address Type 정보
    const selectedAddressTypeOption = addressTypeOptions.value.find((opt: any) => opt.value === corporateForm.value.addressType);
    addressTypeBigo1Val = (selectedAddressTypeOption as any)?.bigo1 || '';
    addressTypeCodeName = selectedAddressTypeOption?.title || '';
    addressTypeCd = corporateForm.value.addressType || '';
    postalCodeVal = corporateForm.value.postalCode || '';
    blockHouseNoVal = corporateForm.value.blockHouseNo || '';
    streetNameVal = corporateForm.value.streetName || '';
    levelVal = corporateForm.value.level || '';
    unitVal = corporateForm.value.unit || '';
    buildingEstateNameVal = corporateForm.value.buildingName || '';
    foreignAddressLine1Val = corporateForm.value.foreignAddressLine1 || '';
    foreignAddressLine2Val = corporateForm.value.foreignAddressLine2 || '';
    
    confirmationByRegistrableController = corporateForm.value.confirmationByController === 'Yes' ? 'true' : 'false';
  } else {
    // 개인 컨트롤러 데이터
    const selectedControllerOption = individualContactOptions.value.find((opt: any) => opt.value === individualForm.value.controllerName);
    controllerNameVal = selectedControllerOption?.title || '';
    controllerCd = individualForm.value.controllerName || '';
    controllerAddressVal = formatControllerAddress();
    registrationIdNoVal = individualForm.value.identificationNumber || '';
    dateOfAppointedVal = dateOfBecomingField.dateValue.value || '';
    dateOfBecomingVal = dateOfAppointedVal;
    emailAddressVal = individualForm.value.emailAddress || '';
    countryCodeVal = individualForm.value.countryCode || '';
    
    // Nationality 정보
    const selectedNationalityOption = nationalityOptions.value.find((opt: any) => opt.value === individualForm.value.nationality);
    nationalityBigo1Val = (selectedNationalityOption as any)?.bigo1 || '';
    nationalityCodeName = selectedNationalityOption?.title || '';
    
    // Country 정보
    const selectedCountryOption = countryOptions.value.find((opt: any) => opt.value === individualForm.value.countryCode);
    const countryCodeName = selectedCountryOption?.title || '';
    const countryBigo3 = (selectedCountryOption as any)?.bigo3 || '';
    const countryCodeNumber = countryBigo3 ? (countryBigo3.startsWith('+') ? countryBigo3.substring(1) : countryBigo3) : '';
    countryCodeNameFormatted = countryCodeName && countryCodeNumber ? `${countryCodeName} +${countryCodeNumber}` : countryCodeName;
    
    // Address Type 정보
    const selectedAddressTypeOption = addressTypeOptions.value.find((opt: any) => opt.value === individualForm.value.addressType);
    addressTypeBigo1Val = (selectedAddressTypeOption as any)?.bigo1 || '';
    addressTypeCodeName = selectedAddressTypeOption?.title || '';
    
    // Date of Birth 포맷팅 (YYYY-MM-DD) - 공통 함수
    const formatDateOfBirth = (dateStr: string): string => {
      if (!dateStr) return '';
      try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      } catch {
        return '';
      }
    };
    
    dateOfBirthVal = formatDateOfBirth(individualForm.value.dateOfBirth);
    areaCodeVal = individualForm.value.areaCode || '';
    mobileNoVal = individualForm.value.contactNumber || '';
    categoryCd = 'B09002'; // 개인
    aliasesVal = individualForm.value.aliases || '';
    addressTypeCd = individualForm.value.addressType || '';
    
    // 주소 타입에 따라 해당하지 않는 필드는 빈 문자열로 저장
    const isLocalAddress = addressTypeCd === 'B07001';
    const isForeignAddress = addressTypeCd === 'B07002';
    postalCodeVal = isLocalAddress ? (individualForm.value.postalCode || '') : '';
    blockHouseNoVal = isLocalAddress ? (individualForm.value.blockHouseNo || '') : '';
    streetNameVal = isLocalAddress ? (individualForm.value.streetName || '') : '';
    levelVal = isLocalAddress ? (individualForm.value.level || '') : '';
    unitVal = isLocalAddress ? (individualForm.value.unit || '') : '';
    buildingEstateNameVal = isLocalAddress ? (individualForm.value.buildingName || '') : '';
    foreignAddressLine1Val = isForeignAddress ? (individualForm.value.foreignAddressLine1 || '') : '';
    foreignAddressLine2Val = isForeignAddress ? (individualForm.value.foreignAddressLine2 || '') : '';
    nationalityCd = individualForm.value.nationality || '';
    
    confirmationByRegistrableController = individualForm.value.confirmationByController === 'Yes' ? 'true' : 'false';
  }
  
  // RegistrableControllerList 생성 (공통 함수 사용)
  const registrableControllerListVal = formatRegistrableControllerList();
  
  // Client 정보
  const clientCd = selectedEntity?.clientCd || selectedEntity?.CLIENT_CD || '';
  const clientNm = entityNameVal;
  
  return {
    entity: entity.value || '',
    confirmationByRegistrableController,
    requireControllerSignature: 'false',
    controllerName: controllerNameVal,
    controllerCd,
    representativeName: '',
    representativeCd: '',
    signatory: controllerNameVal,
    signatoryCd: controllerCd,
    signatoryName: '',
    signatoryNameCd: '',
    dateOfBecoming: dateOfBecomingVal,
    dateOfAppointed: dateOfAppointedVal,
    entityName: entityNameVal,
    entityRegNo: entityRegNoVal,
    categoryCd,
    nationalityCd,
    nationalityBigo1: nationalityBigo1Val,
    nationalityCodeName,
    identityCardOrPassport: registrationIdNoVal,
    dateOfBirth: dateOfBirthVal,
    aliases: aliasesVal,
    acraUen: '',
    nonAcraUen: '',
    legalForm: legalFormVal,
    jurisdiction: jurisdictionVal,
    incorporationStatute: incorporationStatuteVal,
    corporateEntityRegister: corporateEntityRegisterVal,
    addressTypeCd,
    addressTypeBigo1: addressTypeBigo1Val,
    addressTypeCodeName,
    postalCode: postalCodeVal,
    blockHouseNo: blockHouseNoVal,
    streetName: streetNameVal,
    level: levelVal,
    unit: unitVal,
    buildingEstateName: buildingEstateNameVal,
    foreignAddressLine1: foreignAddressLine1Val,
    foreignAddressLine2: foreignAddressLine2Val,
    emailAddress: emailAddressVal,
    countryCode: countryCodeVal,
    countryCodeName: countryCodeNameFormatted,
    areaCode: areaCodeVal,
    mobileNo: mobileNoVal,
    noticeDate: noticeDateVal,
    appointedDate: dateOfAppointedVal,
    controllerAddress: controllerAddressVal,
    registrationIdNo: registrationIdNoVal,
    birthDate: dateOfBirthVal,
    registrableControllerList: registrableControllerListVal,
    clientCd,
    clientNm
  };
};

// 법인 컨트롤러 주소 포맷팅 함수 - 공통 함수 사용
// 법인 컨트롤러 주소 포맷팅 (useAddressFormatter 사용)
const formatCorporateControllerAddress = (): string => {
  const form = corporateForm.value;
  const addressInfo = {
    ADDRESS_TYPE_CD: form.addressType || '',
    BLOCK_NO: form.blockHouseNo || '',
    STREET_NAME: form.streetName || '',
    ADDR_LEVEL: form.level || '',
    ADDR_UNIT: form.unit || '',
    BUILDING_NAME: form.buildingName || '',
    POSTAL_CODE: form.postalCode || '',
    FOREIGN_ADDRESS_LINE_1: form.foreignAddressLine1 || '',
    FOREIGN_ADDRESS_LINE_2: form.foreignAddressLine2 || ''
  };
  // useAddressFormatter의 formatAddress 함수 사용 (Local/Foreign 자동 구분)
  const address = formatAddressUtil(addressInfo, { useComma: true });
  return typeof address === 'string' ? address : '';
};

// 대체 주소 포맷팅 함수 (개인 컨트롤러)
const formatControllerAlternateAddress = (): string => {
  const form = individualForm.value;
  const addressInfo = {
    ADDRESS_TYPE_CD: form.alternateAddressType || form.addressType || '',
    BLOCK_NO: form.altBlockHouseNo || '',
    STREET_NAME: form.altStreetName || '',
    ADDR_LEVEL: form.altLevel || '',
    ADDR_UNIT: form.altUnit || '',
    BUILDING_NAME: form.altBuildingName || '',
    POSTAL_CODE: form.altPostalCode || '',
    FOREIGN_ADDRESS_LINE_1: form.altForeignAddressLine1 || '',
    FOREIGN_ADDRESS_LINE_2: form.altForeignAddressLine2 || ''
  };
  const address = formatAddressUtil(addressInfo, { useComma: true });
  return typeof address === 'string' ? address : '';
};

// 대체 주소 포맷팅 함수 (법인 컨트롤러)
const formatCorporateControllerAlternateAddress = (): string => {
  const form = corporateForm.value;
  const addressInfo = {
    ADDRESS_TYPE_CD: form.alternateAddressType || form.addressType || '',
    BLOCK_NO: form.altBlockHouseNo || '',
    STREET_NAME: form.altStreetName || '',
    ADDR_LEVEL: form.altLevel || '',
    ADDR_UNIT: form.altUnit || '',
    BUILDING_NAME: form.altBuildingName || '',
    POSTAL_CODE: form.altPostalCode || '',
    FOREIGN_ADDRESS_LINE_1: form.altForeignAddressLine1 || '',
    FOREIGN_ADDRESS_LINE_2: form.altForeignAddressLine2 || ''
  };
  const address = formatAddressUtil(addressInfo, { useComma: true });
  return typeof address === 'string' ? address : '';
};

// Country Code에서 숫자만 추출 (+ 포함) - OfficerAppointmentForm.vue 방식 참고
const extractCountryCodeNumber = (countryCd: string): string => {
  if (!countryCd) return '';

  // countryOptions에서 해당 코드의 title 찾기
  const countryOption = countryOptions.value.find(opt => opt.value === countryCd);
  const countryName = countryOption?.title || '';

  // 국가명에서 +숫자 패턴 추출 (예: "Singapore (+65)" -> "+65")
  const match = countryName.match(/\(\+(\d+)\)/);
  if (match) {
    return `+${match[1]}`;
  }

  // B05 코드에서 직접 숫자 부분 추출 시도 (예: B05065 -> +65)
  const codeMatch = countryCd.match(/B05(\d+)/);
  if (codeMatch) {
    return `+${codeMatch[1]}`;
  }

  // bigo3에서 추출 시도 (fallback)
  const selectedCountryOption = countryOptions.value.find((opt: any) => opt.value === countryCd);
  const countryBigo3 = (selectedCountryOption as any)?.bigo3 || '';
  if (countryBigo3) {
    return countryBigo3.startsWith('+') ? countryBigo3 : `+${countryBigo3}`;
  }

  return '';
};

// 연락처 번호 포맷팅 함수 (SQL 규칙에 맞게: BIGO3 + AREA_CODE + MOBILE_NO)
// IIF(ISNULL(F.CODE_NAME, '') = '' , '',
//     IIF(ISNULL(F.CODE_NAME, '') = '', '', F.BIGO3 + ' ')											
//     + IIF(ISNULL(A.AREA_CODE, '') = '', '', A.AREA_CODE + ' ')							
//     + A.MOBILE_NO
// )
const formatControllerContactNo = async (countryCd: string, areaCode: string, mobileNo: string): Promise<string> => {
  if (!countryCd || !mobileNo) {
    return mobileNo || '';
  }

  try {
    // COUNTRY_CD의 TOTAL_CODE로 CODE_NAME과 BIGO3 조회
    const response = await baseCodeApi.getCodeByTotalCode('CM', countryCd);
    const code = (response as any).code || (response as any).data?.code || {};
    const codeName = (code.codeName || code.CODE_NAME || '').trim();
    const bigo3 = (code.bigo3 || code.BIGO3 || '').trim();

    // CODE_NAME이 NULL이거나 빈 문자열이면 빈 문자열 반환
    if (!codeName) {
      return '';
    }

    // BIGO3 + ' ' + AREA_CODE + ' ' + MOBILE_NO 형식으로 구성
    const parts: string[] = [];
    
    // BIGO3가 있으면 추가
    if (bigo3) {
      parts.push(bigo3);
    }
    
    // AREA_CODE가 있으면 추가
    if (areaCode && areaCode.trim()) {
      parts.push(areaCode.trim());
    }
    
    // MOBILE_NO 추가
    if (mobileNo && mobileNo.trim()) {
      parts.push(mobileNo.trim());
    }

    // 공백으로 연결
    return parts.join(' ');
  } catch (error) {
    console.error('❌ [ControllerAppointmentForm] Country Code 조회 실패:', error);
    // 에러 발생 시 기존 로직으로 fallback
    const countryCodeNumber = extractCountryCodeNumber(countryCd);
    if (countryCodeNumber && areaCode && mobileNo) {
      return `${countryCodeNumber} ${areaCode} ${mobileNo}`;
    } else if (countryCodeNumber && mobileNo) {
      return `${countryCodeNumber} ${mobileNo}`;
    } else if (areaCode && mobileNo) {
      return `${areaCode} ${mobileNo}`;
    }
    return mobileNo || '';
  }
};

// doc_variable 정보를 포함한 폼 데이터 반환 (OfficerCessationForm.vue 스타일)
const getFormDataWithDocVariables = async () => {
  // Entity 정보
  const selectedEntityOption = officerAppointmentStore.entityOptions.find(opt => opt.value === entity.value);
  const entityNameVal = selectedEntityOption?.title || '';
  const selectedEntity = officerAppointmentStore.entities.find((ent: any) => 
    (ent.entityCd || ent.ENTITY_CD) === entity.value
  );
  const entityRegNoVal = selectedEntity?.regNo || selectedEntity?.REG_NO || '';
  
  // Notice Date (공통)
  const getCurrentDate = (): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  const noticeDateVal = getCurrentDate();
  
  // Date of Birth 포맷팅 (YYYY-MM-DD) - 공통 함수
  const formatDateOfBirth = (dateStr: string): string => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return '';
    }
  };
  
  // 법인 컨트롤러인지 확인 (corporateForm.controllerName이 있으면 법인)
  const isCorporate = !!corporateForm.value.controllerName;
  
  let controllerNameVal = '';
  let controllerAddressVal = '';
  let controllerAlternateAddressVal = '';
  let registrationIdNoVal = '';
  let dateOfAppointedVal = '';
  let jurisdictionVal = '';
  let legalFormVal = '';
  let nationalityBigo1Val = '';
  let dateOfBirthVal = '';
  let emailAddressVal = '';
  let contactNumberVal = '';

  let controllerEmailVal = '';
  let controllerContactNoVal = '';

  
  if (isCorporate) {
    // 법인 컨트롤러 데이터
    const selectedCorporateControllerOption = corporateContactOptions.value.find((opt: any) => opt.value === corporateForm.value.controllerName);
    controllerNameVal = selectedCorporateControllerOption?.title || '';
    // useAddressFormatter를 사용하여 주소 포맷팅 (Local/Foreign 자동 구분)
    controllerAddressVal = formatCorporateControllerAddress();
    controllerAlternateAddressVal = formatCorporateControllerAlternateAddress();
    registrationIdNoVal = corporateForm.value.uen || ''; // 법인의 경우 UEN
    dateOfAppointedVal = corporateDateOfBecomingField.dateValue.value || '';
    // jurisdiction 값을 CODE_NAME으로 변환
    const jurisdictionCd = corporateForm.value.jurisdiction || '';
    jurisdictionVal = jurisdictionCd ? (getJurisdictionCodeName(jurisdictionCd) || jurisdictionCd) : '';
    legalFormVal = corporateForm.value.legalForm || '';
    emailAddressVal = corporateForm.value.emailAddress || '';
    // 연락처 번호 포맷팅 (countryCode + areaCode + contactNumber) - 공통 함수 사용 (async)
    contactNumberVal = await formatControllerContactNo(
      corporateForm.value.countryCode || '',
      corporateForm.value.areaCode || '',
      corporateForm.value.contactNumber || ''
    );
    controllerEmailVal = corporateForm.value.emailAddress || '';
    controllerContactNoVal = corporateForm.value.contactNumber || '';
  } else {
    // 개인 컨트롤러 데이터
    const selectedControllerOption = individualContactOptions.value.find((opt: any) => opt.value === individualForm.value.controllerName);
    controllerNameVal = selectedControllerOption?.title || '';
    // useAddressFormatter를 사용하여 주소 포맷팅 (Local/Foreign 자동 구분)
    controllerAddressVal = formatControllerAddress();
    controllerAlternateAddressVal = formatControllerAlternateAddress();
    registrationIdNoVal = individualForm.value.identificationNumber || '';
    dateOfAppointedVal = dateOfBecomingField.dateValue.value || '';
    
    // Nationality BIGO1
    const selectedNationalityOption = nationalityOptions.value.find((opt: any) => opt.value === individualForm.value.nationality);
    nationalityBigo1Val = (selectedNationalityOption as any)?.bigo1 || '';
    
    // Date of Birth
    dateOfBirthVal = formatDateOfBirth(individualForm.value.dateOfBirth);
    emailAddressVal = individualForm.value.emailAddress || '';
    // 연락처 번호 포맷팅 (countryCode + areaCode + contactNumber) - 공통 함수 사용 (async)
    contactNumberVal = await formatControllerContactNo(
      individualForm.value.countryCode || '',
      individualForm.value.areaCode || '',
      individualForm.value.contactNumber || ''
    );
    controllerEmailVal = individualForm.value.emailAddress || '';
    controllerContactNoVal = individualForm.value.contactNumber || '';
  }
  
  // Entity 설립일 (DATE_OF_INCORPORATION)
  const incorporatedDateVal = (selectedEntity as any)?.dateOfIncorporation || (selectedEntity as any)?.DATE_OF_INCORPORATION || '';
  
  // RegistrableControllerList 생성 (공통 함수 사용)
  const registrableControllerListVal = formatRegistrableControllerList();
  
  // Client 정보
  const clientNm = entityNameVal;
  
  // 필드명(키)을 기준으로 그룹화
  const fieldMap = new Map<string, { value: any; docVariables: string[] }>();
  
  // entityName
  if (entityNameVal) {
    fieldMap.set('entityName', {
      value: entityNameVal,
      docVariables: [docVariableMapping.entityName]
    });
  }
  
  // entityRegNo
  if (entityRegNoVal) {
    fieldMap.set('entityRegNo', {
      value: entityRegNoVal,
      docVariables: [docVariableMapping.entityRegNo]
    });
  }
  
  // controllerName - NoticeController와 AppointControllerName 둘 다 매핑
  if (controllerNameVal) {
    fieldMap.set('controllerName', {
      value: controllerNameVal,
      docVariables: [docVariableMapping.controllerName, docVariableMapping.appointControllerName]
    });
  }
  
  // controllerAddress - NoticeControllerAddress와 AppointControllerAddress 둘 다 매핑
  if (controllerAddressVal) {
    fieldMap.set('controllerAddress', {
      value: controllerAddressVal,
      docVariables: [docVariableMapping.controllerAddress, docVariableMapping.appointControllerAddress]
    });
  }
  
  // controllerAlternateAddress - AppointControllerAltemateAddress 매핑
  if (controllerAlternateAddressVal) {
    fieldMap.set('controllerAlternateAddress', {
      value: controllerAlternateAddressVal,
      docVariables: [docVariableMapping.appointControllerAltemateAddress]
    });
  }
  
  // registrationIdNo - NoticeControllerIdNo와 AppointControllerIdNo 둘 다 매핑
  if (registrationIdNoVal) {
    fieldMap.set('registrationIdNo', {
      value: registrationIdNoVal,
      docVariables: [docVariableMapping.registrationIdNo, docVariableMapping.appointControllerIdNo]
    });
    if (!isCorporate) {
      // 개인 컨트롤러만 identityCardOrPassport 추가
      fieldMap.set('identityCardOrPassport', {
        value: registrationIdNoVal,
        docVariables: [docVariableMapping.registrationIdNo, docVariableMapping.appointControllerIdNo]
      });
    }
  }
  
  // dateOfAppointed (ControllerStartDate)
  // doc_variables로 매핑되는 날짜는 formatDocDate로 변환하여 저장
  if (dateOfAppointedVal) {
    const formattedDateOfAppointed = formatDocDate(dateOfAppointedVal);
    fieldMap.set('dateOfAppointed', {
      value: formattedDateOfAppointed,
      docVariables: [docVariableMapping.controllerStartDate]
    });
  }
  
  // noticeDate
  // doc_variables로 매핑되는 날짜는 formatDocDate로 변환하여 저장
  if (noticeDateVal) {
    const formattedNoticeDate = formatDocDate(noticeDateVal);
    fieldMap.set('noticeDate', {
      value: formattedNoticeDate,
      docVariables: [docVariableMapping.noticeDate]
    });
  }
  
  // 법인 컨트롤러 전용 필드
  if (isCorporate) {
    // jurisdiction - AppointControllerJurisdiction 매핑
    if (jurisdictionVal) {
      fieldMap.set('jurisdiction', {
        value: jurisdictionVal,
        docVariables: [docVariableMapping.jurisdiction, docVariableMapping.appointControllerJurisdiction]
      });
    }
    
    // legalForm
    if (legalFormVal) {
      fieldMap.set('legalForm', {
        value: legalFormVal,
        docVariables: [docVariableMapping.corporateControllerLegalForm]
      });
    }
  } else {
    // 개인 컨트롤러 전용 필드
    // nationalityBigo1 - NoticeControllerNationality와 AppointControllerNationality 둘 다 매핑
    if (nationalityBigo1Val) {
      fieldMap.set('nationalityBigo1', {
        value: nationalityBigo1Val,
        docVariables: [docVariableMapping.nationalityBigo1, docVariableMapping.appointControllerNationality]
      });
    }
    
    // dateOfBirth - NoticeControllerDateOfBirth와 AppointControllerDateOfBirth 둘 다 매핑
    // doc_variables로 매핑되는 날짜는 formatDocDate로 변환하여 저장
    if (dateOfBirthVal) {
      const formattedDateOfBirth = formatDocDate(dateOfBirthVal);
      fieldMap.set('dateOfBirth', {
        value: formattedDateOfBirth,
        docVariables: [docVariableMapping.dateOfBirth, docVariableMapping.appointControllerDateOfBirth]
      });
      fieldMap.set('birthDate', {
        value: formattedDateOfBirth,
        docVariables: [docVariableMapping.dateOfBirth, docVariableMapping.appointControllerDateOfBirth]
      });
    }
  }
  
  // emailAddress - AppointControllerEmail 매핑 (공통)
  if (emailAddressVal) {
    fieldMap.set('emailAddress', {
      value: emailAddressVal,
      docVariables: [docVariableMapping.appointControllerEmail]
    });
  }
  
  // contactNumber - AppointControllerContactNo 매핑 (공통)
  if (contactNumberVal) {
    fieldMap.set('contactNumber', {
      value: contactNumberVal,
      docVariables: [docVariableMapping.appointControllerContactNo]
    });
    fieldMap.set('mobileNo', {
      value: contactNumberVal,
      docVariables: [docVariableMapping.appointControllerContactNo]
    });
  }
  
  // registrableControllerList (항상 추가)
  fieldMap.set('registrableControllerList', {
    value: registrableControllerListVal,
    docVariables: [docVariableMapping.registrableControllerList]
  });
  
  // clientNm
  fieldMap.set('clientNm', {
    value: clientNm,
    docVariables: [docVariableMapping.clientNm]
  });

  // Entity 관련 공통 데이터 조회 및 doc_variables 생성
  if (entity.value) {
    try {
      const entityData = await getEntityData(entity.value);
      
      // {#CompanyName} - entityName에 추가 (기존 필드와 병합)
      if (entityData.companyName) {
        const existingField = fieldMap.get('entityName');
        if (existingField) {
          if (!existingField.docVariables.includes('CompanyName')) {
            existingField.docVariables.push('CompanyName');
          }
        } else {
          fieldMap.set('entityName', {
            value: entityData.companyName,
            docVariables: ['CompanyName']
          });
        }
      }

      // {#RegistrationNo} - entityRegNo에 추가 (기존 필드와 병합)
      if (entityData.registrationNumber) {
        const existingField = fieldMap.get('entityRegNo');
        if (existingField) {
          if (!existingField.docVariables.includes('RegistrationNo')) {
            existingField.docVariables.push('RegistrationNo');
          }
        } else {
          fieldMap.set('entityRegNo', {
            value: entityData.registrationNumber,
            docVariables: ['RegistrationNo']
          });
        }
      }

      // {#IncorporatedDate} - DATE_OF_INCORPORATION 날짜 형식 (formatDocDate 사용)
      if (entityData.incorporationDate) {
        // DD/MM/YYYY 형식을 YYYY-MM-DD로 변환
        let dateToFormat = entityData.incorporationDate;
        const ddMMyyyyMatch = String(dateToFormat).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (ddMMyyyyMatch) {
          const [, day, month, year] = ddMMyyyyMatch;
          dateToFormat = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        
        const formattedDate = formatDocDate(dateToFormat);
        if (formattedDate) {
          fieldMap.set('incorporatedDate', {
            value: formattedDate,
            docVariables: ['IncorporatedDate']
          });
        }
      }

      // {#CompanyAddress}
      if (entityData.companyAddress) {
        fieldMap.set('companyAddress', {
          value: entityData.companyAddress,
          docVariables: ['CompanyAddress']
        });
      }

      // {#FinancialYearEnd} - FINANCIAL_YEAR_END 날짜 형식 (formatDocDate 사용)
      if (entityData.financialYearEnd) {
        // DD/MM/YYYY 형식을 YYYY-MM-DD로 변환
        let dateToFormat = entityData.financialYearEnd;
        const ddMMyyyyMatch = String(dateToFormat).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (ddMMyyyyMatch) {
          const [, day, month, year] = ddMMyyyyMatch;
          dateToFormat = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        
        const formattedDate = formatDocDate(dateToFormat);
        if (formattedDate) {
          fieldMap.set('financialYearEnd', {
            value: formattedDate,
            docVariables: ['FinancialYearEnd']
          });
        }
      }

      // {#PrimaryActivityCode}
      if (entityData.primaryActivityCode) {
        fieldMap.set('primaryActivityCode', {
          value: entityData.primaryActivityCode,
          docVariables: ['PrimaryActivityCode']
        });
      }

      // {#PrimaryActivityDescription}
      if (entityData.primaryActivityDescription) {
        fieldMap.set('primaryActivityDescription', {
          value: entityData.primaryActivityDescription,
          docVariables: ['PrimaryActivityDescription']
        });
      }

      // {#SecondaryActivityCode}
      if (entityData.secondaryActivityCode) {
        fieldMap.set('secondaryActivityCode', {
          value: entityData.secondaryActivityCode,
          docVariables: ['SecondaryActivityCode']
        });
      }

      // {#SecondaryActivityDescription}
      if (entityData.secondaryActivityDescription) {
        fieldMap.set('secondaryActivityDescription', {
          value: entityData.secondaryActivityDescription,
          docVariables: ['SecondaryActivityDescription']
        });
      }

      // {#EntityJurisdiction}
      if (entityData.entityJurisdiction) {
        fieldMap.set('entityJurisdiction', {
          value: entityData.entityJurisdiction,
          docVariables: ['EntityJurisdiction']
        });
      }

      // {#EntityType}
      if (entityData.entityType) {
        fieldMap.set('entityType', {
          value: entityData.entityType,
          docVariables: ['EntityType']
        });
      }

      // {#CompanyEmail}
      if (entityData.companyEmail) {
        fieldMap.set('companyEmail', {
          value: entityData.companyEmail,
          docVariables: ['CompanyEmail']
        });
      }
    } catch (error) {
      console.error('❌ [ControllerAppointmentForm] Entity 데이터 조회 실패:', error);
    }
  }
  
  // incorporatedDate (설립일)
  if (incorporatedDateVal) {
    const formattedIncorporatedDate = formatDocDate(incorporatedDateVal);
    fieldMap.set('incorporatedDate', {
      value: formattedIncorporatedDate,
      docVariables: [docVariableMapping.incorporatedDate]
    });
  }
  
  // appointControllerName (Controller Name)
  if (controllerNameVal) {
    fieldMap.set('appointControllerName', {
      value: controllerNameVal,
      docVariables: [docVariableMapping.appointControllerName]
    });
  }
  
  // appointControllerAddress (Residential address)
  // useAddressFormatter를 통해 포맷팅된 주소 사용 (Local: "9 Straits View, #05-07, Marina One, Singapore 018937" 형식)
  if (controllerAddressVal) {
    fieldMap.set('appointControllerAddress', {
      value: controllerAddressVal,
      docVariables: [docVariableMapping.appointControllerAddress]
    });
  }
  
  // appointControllerEmail (Email address)
  if (controllerEmailVal) {
    fieldMap.set('appointControllerEmail', {
      value: controllerEmailVal,
      docVariables: [docVariableMapping.appointControllerEmail]
    });
  }
  
  // appointControllerContactNo (Contact number) - 포맷팅된 contactNumberVal 사용
  if (contactNumberVal) {
    fieldMap.set('appointControllerContactNo', {
      value: contactNumberVal,
      docVariables: [docVariableMapping.appointControllerContactNo]
    });
  }
  
  // appointControllerIdNo (Identity card number)
  if (registrationIdNoVal) {
    fieldMap.set('appointControllerIdNo', {
      value: registrationIdNoVal,
      docVariables: [docVariableMapping.appointControllerIdNo]
    });
  }
  
  // 개인 컨트롤러 전용 필드
  if (!isCorporate) {
    // appointControllerNationality (Nationality)
    if (nationalityBigo1Val) {
      fieldMap.set('appointControllerNationality', {
        value: nationalityBigo1Val,
        docVariables: [docVariableMapping.appointControllerNationality]
      });
    }
    
    // appointControllerDateOfBirth (Date of birth)
    if (dateOfBirthVal) {
      const formattedDateOfBirth = formatDocDate(dateOfBirthVal);
      fieldMap.set('appointControllerDateOfBirth', {
        value: formattedDateOfBirth,
        docVariables: [docVariableMapping.appointControllerDateOfBirth]
      });
    }
  }
  
  // 결과 객체 생성 - 각 필드를 { value: "...", doc_variables: ["#..."] } 형태로 변환
  const result: Record<string, any> = {};
  fieldMap.forEach((item, fieldName) => {
    const docVariablesArray = item.docVariables.map(v => `#${v}`);
    result[fieldName] = {
      value: item.value,
      doc_variables: docVariablesArray
    };
  });
  
  return result;
};

// saveController 객체 생성 함수
const getSaveController = () => {
  // Nationality BIGO1 (KR 형식)
  const selectedNationalityOption = nationalityOptions.value.find((opt: any) => opt.value === individualForm.value.nationality);
  const nationalityBigo1Val = (selectedNationalityOption as any)?.bigo1 || '';
  
  // Address Type BIGO1 (S 형식)
  const selectedAddressTypeOption = addressTypeOptions.value.find((opt: any) => opt.value === individualForm.value.addressType);
  const addressTypeBigo1Val = (selectedAddressTypeOption as any)?.bigo1 || '';
  
  // Country Code에서 국가 번호 추출 (BIGO3에서)
  const selectedCountryOption = countryOptions.value.find((opt: any) => opt.value === individualForm.value.countryCode);
  const countryBigo3 = (selectedCountryOption as any)?.bigo3 || '';
  const mobileNoCountryCode = countryBigo3 ? (countryBigo3.startsWith('+') ? countryBigo3.substring(1) : countryBigo3) : '';
  
  // Date of Birth 포맷팅 (원본 형식 유지)
  const dateOfBirthVal = individualForm.value.dateOfBirth || '';
  
  // Date of Becoming 포맷팅
  const entryDateVal = dateOfBecomingField.dateValue.value || '';
  
  // confirmationFlag 변환
  const confirmationFlag = individualForm.value.confirmationByController === 'Yes';
  
  // Date of Birth를 doc_variables 포함 형태로 변환
  const formatDateOfBirth = (dateStr: string): string => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return '';
    }
  };
  
  const dateOfBirthFormatted = formatDateOfBirth(dateOfBirthVal);
  
  return {
    rcCategory: 'IND',
    registrableControllerId: '',
    idType: '',
    registrationIdNo: {
      value: individualForm.value.identificationNumber || '',
      doc_variables: ['#NoticeControllerIdNo']
    },
    nationality: nationalityBigo1Val,
    incorpCountry: '',
    incorpCountryRegistrarName: '',
    legalForm: '',
    statute: '',
    birthDate: {
      value: dateOfBirthFormatted,
      doc_variables: ['#NoticeControllerDateOfBirth']
    },
    name: '',
    nameAlias: individualForm.value.aliases || '',
    email: individualForm.value.emailAddress || '',
    mobileNoCountryCode,
    mobileNoAreaCode: individualForm.value.areaCode || '',
    mobileNo: individualForm.value.contactNumber || '',
    addressType: addressTypeBigo1Val,
    postalCode: individualForm.value.postalCode || '',
    blkhseNo: individualForm.value.blockHouseNo || '',
    streetName: individualForm.value.streetName || '',
    buildingName: individualForm.value.buildingName || '',
    levelNo: individualForm.value.level || '',
    unitNo: individualForm.value.unit || '',
    foreignAddr1: individualForm.value.foreignAddressLine1 || '',
    foreignAddr2: individualForm.value.foreignAddressLine2 || '',
    entryDate: entryDateVal,
    ceasedDate: '',
    ceasedReason: '',
    confirmationFlag,
    appointmentStatus: '',
    updatedFlag: true
  };
};

// 초기 데이터 로드
// 개인 Contact 목록 로드 (B09002만)
const loadIndividualContacts = async () => {
  try {
    isLoadingContacts.value = true;
    const response = await contactsApi.getContacts({ page: 1, limit: 10000 });
    const contacts = (response as any).contacts || response.data?.contacts || [];
    
    // 개인만 필터링 (CATEGORY_CD='B09002')
    const individualContacts = contacts.filter((c: any) => 
      c.CATEGORY_CD === 'B09002' || c.categoryCd === 'B09002'
    );
    
    // 원본 contact 목록 저장
    originalIndividualContactOptions.value = individualContacts.map((contact: any) => ({
      title: contact.contactName || contact.CONTACT_NAME || '',
      value: contact.contactCd || contact.CONTACT_CD || ''
    })).filter((item: { title: string; value: string }) => item.title && item.value);
    
    // Entity가 선택된 상태에서 우선순위 contact 정보가 있으면 정렬, 없으면 원본 그대로 사용
    if (entity.value && (individualPriorityContactCds.value.size > 0 || individualShareholderContactCds.value.size > 0)) {
      sortIndividualContactOptions();
    } else {
      individualContactOptions.value = [...originalIndividualContactOptions.value];
    }
    
    console.log('✅ 개인 Contact 목록 로드 완료:', individualContactOptions.value.length);
  } catch (error: any) {
    console.error('❌ Failed to load individual contacts:', error);
    individualContactOptions.value = [];
    originalIndividualContactOptions.value = [];
  } finally {
    isLoadingContacts.value = false;
  }
};

// 법인 Contact 목록 로드 (B09001만)
const loadCorporateContacts = async () => {
  try {
    isLoadingCorporateContacts.value = true;
    const response = await contactsApi.getContacts({ page: 1, limit: 10000 });
    const contacts = (response as any).contacts || response.data?.contacts || [];
    
    // 법인만 필터링 (CATEGORY_CD='B09001')
    const corporateContacts = contacts.filter((c: any) => 
      c.CATEGORY_CD === 'B09001' || c.categoryCd === 'B09001'
    );
    
    corporateContactOptions.value = corporateContacts.map((contact: any) => ({
      title: contact.contactName || contact.CONTACT_NAME || '',
      value: contact.contactCd || contact.CONTACT_CD || ''
    })).filter((item: { title: string; value: string }) => item.title && item.value);
    
    console.log('✅ 법인 Contact 목록 로드 완료:', corporateContactOptions.value.length);
  } catch (error: any) {
    console.error('❌ Failed to load corporate contacts:', error);
    corporateContactOptions.value = [];
  } finally {
    isLoadingCorporateContacts.value = false;
  }
};

// L_OFFICER 테이블에서 Officer 정보 조회 및 L_SHAREHOLDER_DETAIL 테이블에서 주주 정보 조회 (entity_cd별)
// 개인 Controller Name 목록 정렬에 사용
const loadIndividualPriorityContacts = async (entityCd: string) => {
  if (!entityCd) {
    individualPriorityContactCds.value.clear();
    individualShareholderContactCds.value.clear();
    return;
  }
  
  try {
    // L_OFFICER 테이블에서 CLIENT_CD와 ENTITY_CD로 모든 Officer의 CONTACT_CD 조회
    const officersResponse = await apiClient.get(`/startTask/common/officers-by-entity-all/${entityCd}`);
    const officers = (officersResponse as any).data?.officers || officersResponse.data?.officers || [];
    
    // L_SHAREHOLDER_DETAIL 테이블에서 CLIENT_CD와 ENTITY_CD로 모든 주주의 CONTACT_CD 조회
    const shareholdersResponse = await apiClient.get(`/startTask/common/shareholders-by-entity/${entityCd}`);
    const shareholders = (shareholdersResponse as any).data?.shareholders || shareholdersResponse.data?.shareholders || [];
    
    // Officer contact_cd를 Set에 저장
    individualPriorityContactCds.value.clear();
    individualShareholderContactCds.value.clear();
    officers.forEach((officer: any) => {
      const contactCd = officer.contactCd || officer.CONTACT_CD || '';
      if (contactCd) {
        individualPriorityContactCds.value.add(contactCd);
      }
    });
    
    // 주주 contact_cd를 Set에 저장 (Officer와 중복되지 않는 것만)
    shareholders.forEach((shareholder: any) => {
      const contactCd = shareholder.contactCd || shareholder.CONTACT_CD || '';
      if (contactCd && !individualPriorityContactCds.value.has(contactCd)) {
        individualShareholderContactCds.value.add(contactCd);
      }
    });
    
    // 개인 Contact 목록 재정렬
    if ((individualPriorityContactCds.value.size > 0 || individualShareholderContactCds.value.size > 0) && originalIndividualContactOptions.value.length > 0) {
      sortIndividualContactOptions();
    }
    
    console.log('🔍 loadIndividualPriorityContacts 완료', {
      entityCd,
      officerCount: individualPriorityContactCds.value.size,
      shareholderCount: individualShareholderContactCds.value.size
    });
  } catch (error: any) {
    console.error('Failed to load individual priority contacts:', error);
    individualPriorityContactCds.value.clear();
    individualShareholderContactCds.value.clear();
  }
};

// 개인 Contact 목록 정렬 (Officer → 주주 → 구분선 → 나머지 순서로 배치)
const sortIndividualContactOptions = () => {
  if ((individualPriorityContactCds.value.size === 0 && individualShareholderContactCds.value.size === 0) || originalIndividualContactOptions.value.length === 0) {
    individualContactOptions.value = [...originalIndividualContactOptions.value];
    return;
  }
  
  const officerContacts: Array<{ title: string; value: string }> = [];
  const shareholderContacts: Array<{ title: string; value: string }> = [];
  const others: Array<{ title: string; value: string }> = [];
  
  originalIndividualContactOptions.value.forEach((contact: any) => {
    const contactCd = contact.value;
    if (individualPriorityContactCds.value.has(contactCd)) {
      officerContacts.push(contact);
    } else if (individualShareholderContactCds.value.has(contactCd)) {
      shareholderContacts.push(contact);
    } else {
      others.push(contact);
    }
  });
  
  // 구분선 정의
  const divider: { title: string; value: string; disabled?: boolean; type?: string } = {
    title: '--------------------------------------------------',
    value: 'divider',
    disabled: true,
    type: 'Divider'
  };
  
  const result: Array<{ title: string; value: string; disabled?: boolean; type?: string }> = [];
  
  // Officer 목록 추가
  if (officerContacts.length > 0) {
    result.push(...officerContacts);
  }
  
  // 주주 목록 추가
  if (shareholderContacts.length > 0) {
    result.push(...shareholderContacts);
  }
  
  // 나머지 목록이 있으면 구분선 추가 후 나머지 목록 추가
  if (others.length > 0) {
    if (officerContacts.length > 0 || shareholderContacts.length > 0) {
      result.push(divider);
    }
    result.push(...others);
  }
  
  individualContactOptions.value = result;
};

// 옵션 로드 (ID Type, Nationality, Country, Address Type)
const loadOptions = async () => {
  try {
    // ID Type (B03)
    const idTypeResponse = await baseCodeApi.getCodesByMainCode('CM', 'B03', undefined);
    const idTypeCodes = (idTypeResponse as any).codes || idTypeResponse.data?.codes || [];
    identificationTypeOptions.value = idTypeCodes.map((code: any) => ({
      title: code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '',
      value: code.totalCode || code.TOTAL_CODE || '',
      bigo1: code.bigo1 || code.BIGO1 || '' // BIGO1 값 포함
    })).filter((opt: any) => opt.title && opt.value);
    
    // Nationality (B04) - bigo1 포함
    const nationalityResponse = await baseCodeApi.getCodesByMainCode('CM', 'B04', undefined);
    const nationalityCodes = (nationalityResponse as any).codes || nationalityResponse.data?.codes || [];
    nationalityOptions.value = nationalityCodes.map((code: any) => ({
      title: code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '',
      value: code.totalCode || code.TOTAL_CODE || '',
      bigo1: code.bigo1 || code.BIGO1 || ''
    })).filter((opt: any) => opt.title && opt.value);
    
    // Country (B05) - bigo3 포함
    const countryResponse = await baseCodeApi.getCodesByMainCode('CM', 'B05', undefined);
    const countryCodes = (countryResponse as any).codes || countryResponse.data?.codes || [];
    countryOptions.value = countryCodes.map((code: any) => ({
      title: code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '',
      value: code.totalCode || code.TOTAL_CODE || '',
      bigo3: code.bigo3 || code.BIGO3 || ''
    })).filter((opt: any) => opt.title && opt.value);
    
    // Address Type (B07) - bigo1 포함
    const addressTypeResponse = await baseCodeApi.getCodesByMainCode('CM', 'B07', undefined);
    const addressTypeCodes = (addressTypeResponse as any).codes || addressTypeResponse.data?.codes || [];
    addressTypeOptions.value = addressTypeCodes.map((code: any) => ({
      title: code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '',
      value: code.totalCode || code.TOTAL_CODE || '',
      bigo1: code.bigo1 || code.BIGO1 || ''
    })).filter((opt: any) => opt.title && opt.value);
    
    // Jurisdiction (B15) - 법인 컨트롤러용
    const jurisdictionResponse = await baseCodeApi.getCodesByMainCode('CM', 'B15', undefined);
    const jurisdictionCodes = (jurisdictionResponse as any).codes || jurisdictionResponse.data?.codes || [];
    jurisdictionOptions.value = jurisdictionCodes.map((code: any) => ({
      title: code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '',
      value: code.totalCode || code.TOTAL_CODE || ''
    })).filter((opt: any) => opt.title && opt.value);
  } catch (error: any) {
    console.error('❌ Failed to load options:', error);
  }
};

// Controller Name 선택 시 자동 바인딩
watch(() => individualForm.value.controllerName, async (contactCd, oldContactCd) => {
  // 데이터 복원 중이면 DB 조회하지 않음 (setFormDataFromInitialData에서 이미 설정됨)
  if (isRestoringData.value) {
    console.log('📋 [ControllerAppointmentForm] 데이터 복원 중 - Contact DB 조회 건너뜀');
    return;
  }

  // Controller가 변경되거나 선택 해제될 때 이전 데이터 초기화
  if (!contactCd || (oldContactCd && contactCd !== oldContactCd)) {
    // 이전 Controller의 데이터 초기화
    Object.keys(autoBoundFields.value).forEach(key => {
      autoBoundFields.value[key] = false;
    });
    individualForm.value.identificationType = '';
    individualForm.value.identificationNumber = '';
    individualForm.value.aliases = '';
    individualForm.value.nationality = '';
    individualForm.value.dateOfBirth = '';
    dateOfBirthValue.value = '';
    individualForm.value.emailAddress = '';
    individualForm.value.countryCode = '';
    individualForm.value.areaCode = '';
    individualForm.value.contactNumber = '';
    individualForm.value.addressType = '';
    individualForm.value.postalCode = '';
    individualForm.value.blockHouseNo = '';
    individualForm.value.streetName = '';
    individualForm.value.buildingName = '';
    individualForm.value.level = '';
    individualForm.value.unit = '';
    individualForm.value.foreignAddressLine1 = '';
    individualForm.value.foreignAddressLine2 = '';
    // Contact Address (대체 주소) 초기화
    individualForm.value.alternateAddressType = '';
    individualForm.value.altPostalCode = '';
    individualForm.value.altBlockHouseNo = '';
    individualForm.value.altStreetName = '';
    individualForm.value.altBuildingName = '';
    individualForm.value.altLevel = '';
    individualForm.value.altUnit = '';
    individualForm.value.altForeignAddressLine1 = '';
    individualForm.value.altForeignAddressLine2 = '';
    
    // 선택 해제 시에만 자동 선택 플래그 리셋
    if (!contactCd) {
    hasEmittedAutoSelectForIndividual.value = false;
      return;
    }
  }
  
  // 새로운 Controller가 선택된 경우에만 데이터 바인딩
  if (!contactCd) {
    return;
  }
  
  try {
    // Contact 상세 정보 조회
    const response = await contactsApi.getContactById(contactCd);
    const contact = (response as any).contact || response.data?.contact;
    
    if (contact) {
      // 자동 바인딩
      if (contact.contactRegNo || contact.CONTACT_REG_NO) {
        individualForm.value.identificationNumber = contact.contactRegNo || contact.CONTACT_REG_NO;
        autoBoundFields.value.identificationNumber = true;
      }
      
      // name 필드는 제거 (Controller Name으로 이미 설정됨)
      
      if (contact.aliases || contact.ALIASES) {
        individualForm.value.aliases = contact.aliases || contact.ALIASES;
      }
      
      if (contact.nationalityCd || contact.NATIONALITY_CD) {
        individualForm.value.nationality = contact.nationalityCd || contact.NATIONALITY_CD;
        autoBoundFields.value.nationality = true;
      }
      
      if (contact.dateOfBirth || contact.DATE_OF_BIRTH) {
        individualForm.value.dateOfBirth = contact.dateOfBirth || contact.DATE_OF_BIRTH;
        dateOfBirthValue.value = contact.dateOfBirth || contact.DATE_OF_BIRTH;
        autoBoundFields.value.dateOfBirth = true;
      }
      
      if (contact.idTypeCd || contact.ID_TYPE_CD) {
        individualForm.value.identificationType = contact.idTypeCd || contact.ID_TYPE_CD;
      }
      
      if (contact.contactEmail || contact.CONTACT_EMAIL) {
        individualForm.value.emailAddress = contact.contactEmail || contact.CONTACT_EMAIL;
        autoBoundFields.value.emailAddress = true;
      }
      
      if (contact.countryCd || contact.COUNTRY_CD) {
        individualForm.value.countryCode = contact.countryCd || contact.COUNTRY_CD;
        autoBoundFields.value.countryCode = true;
      }
      
      if (contact.areaCode || contact.AREA_CODE) {
        individualForm.value.areaCode = contact.areaCode || contact.AREA_CODE;
        autoBoundFields.value.areaCode = true;
      }
      
      if (contact.mobileNo || contact.MOBILE_NO) {
        individualForm.value.contactNumber = contact.mobileNo || contact.MOBILE_NO;
        autoBoundFields.value.contactNumber = true;
      }
      
      if (contact.addressTypeCd || contact.ADDRESS_TYPE_CD) {
        individualForm.value.addressType = contact.addressTypeCd || contact.ADDRESS_TYPE_CD;
        autoBoundFields.value.addressType = true;
      }
      
      if (contact.postalCode || contact.POSTAL_CODE) {
        individualForm.value.postalCode = contact.postalCode || contact.POSTAL_CODE;
        autoBoundFields.value.postalCode = true;
      }
      
      if (contact.blockNo || contact.BLOCK_NO || contact.blockHouseNo || contact.BLOCK_HOUSE_NO) {
        individualForm.value.blockHouseNo = contact.blockNo || contact.BLOCK_NO || contact.blockHouseNo || contact.BLOCK_HOUSE_NO;
        autoBoundFields.value.blockHouseNo = true;
      }
      
      if (contact.streetName || contact.STREET_NAME) {
        individualForm.value.streetName = contact.streetName || contact.STREET_NAME;
        autoBoundFields.value.streetName = true;
      }
      
      if (contact.buildingName || contact.BUILDING_NAME) {
        individualForm.value.buildingName = contact.buildingName || contact.BUILDING_NAME;
        autoBoundFields.value.buildingName = true;
      }
      
      if (contact.addrLevel || contact.ADDR_LEVEL) {
        individualForm.value.level = contact.addrLevel || contact.ADDR_LEVEL;
        autoBoundFields.value.level = true;
      }
      
      if (contact.addrUnit || contact.ADDR_UNIT) {
        individualForm.value.unit = contact.addrUnit || contact.ADDR_UNIT;
        autoBoundFields.value.unit = true;
      }
      
      if (contact.foreignAddressLine1 || contact.FOREIGN_ADDRESS_LINE_1) {
        individualForm.value.foreignAddressLine1 = contact.foreignAddressLine1 || contact.FOREIGN_ADDRESS_LINE_1;
        autoBoundFields.value.foreignAddressLine1 = true;
      }
      
      if (contact.foreignAddressLine2 || contact.FOREIGN_ADDRESS_LINE_2) {
        individualForm.value.foreignAddressLine2 = contact.foreignAddressLine2 || contact.FOREIGN_ADDRESS_LINE_2;
        autoBoundFields.value.foreignAddressLine2 = true;
      }
      
      // Contact Address (대체 주소) 필드 바인딩
      if (contact.alternateAddressTypeCd || contact.ALTERNATE_ADDRESS_CD) {
        individualForm.value.alternateAddressType = contact.alternateAddressTypeCd || contact.ALTERNATE_ADDRESS_CD;
      }
      if (contact.altPostalCode || contact.ALT_POSTAL_CODE) {
        individualForm.value.altPostalCode = contact.altPostalCode || contact.ALT_POSTAL_CODE;
      }
      if (contact.altBlockNo || contact.ALT_BLOCK_NO) {
        individualForm.value.altBlockHouseNo = contact.altBlockNo || contact.ALT_BLOCK_NO;
      }
      if (contact.altStreetName || contact.ALT_STREET_NAME) {
        individualForm.value.altStreetName = contact.altStreetName || contact.ALT_STREET_NAME;
      }
      if (contact.altBuildingName || contact.ALT_BUILDING_NAME) {
        individualForm.value.altBuildingName = contact.altBuildingName || contact.ALT_BUILDING_NAME;
      }
      if (contact.altAddrLevel || contact.ALT_ADDR_LEVEL) {
        individualForm.value.altLevel = contact.altAddrLevel || contact.ALT_ADDR_LEVEL;
      }
      if (contact.altAddrUnit || contact.ALT_ADDR_UNIT) {
        individualForm.value.altUnit = contact.altAddrUnit || contact.ALT_ADDR_UNIT;
      }
      if (contact.altForeignAddressLine1 || contact.ALT_FOREIGN_ADDRESS_LINE_1) {
        individualForm.value.altForeignAddressLine1 = contact.altForeignAddressLine1 || contact.ALT_FOREIGN_ADDRESS_LINE_1;
      }
      if (contact.altForeignAddressLine2 || contact.ALT_FOREIGN_ADDRESS_LINE_2) {
        individualForm.value.altForeignAddressLine2 = contact.altForeignAddressLine2 || contact.ALT_FOREIGN_ADDRESS_LINE_2;
      }
      
      console.log('✅ Controller 자동 바인딩 완료:', contact);
    }
  } catch (error: any) {
    console.error('❌ Failed to load contact details:', error);
  }
});

// E-Filing 모드에서 섹션 클릭 시 Contact Detail 모달 열기
const handleEFilingSectionClick = (controller: any, type: 'individual' | 'corporate') => {
  if (props.readOnly || !controller) return;
  
  const contactCd = controller.CONTACT_CD;
  if (!contactCd) {
    console.warn('⚠️ [ControllerAppointmentForm] CONTACT_CD가 없습니다:', controller);
    return;
  }
  
  if (type === 'individual') {
    individualForm.value.controllerName = contactCd;
    showContactDetailModal.value = true;
  } else {
    corporateForm.value.controllerName = contactCd;
    showCorporateContactDetailModal.value = true;
  }
};

// New Contact 모달 열기
const handleNewContact = () => {
  // Controller Name이 선택되어 있으면 해당 Contact의 상세 화면으로 바로 이동
  if (individualForm.value.controllerName) {
    showContactDetailModal.value = true;
  } else {
    // Controller Name이 선택되지 않았으면 Contact 리스트로 이동 (Individual 생성 가능)
    showNewContactModal.value = true;
  }
};

// Contact 상세 화면 모달
const showContactDetailModal = ref(false);

// New Contact 선택 시 처리 (ContactListPage에서 - 새로 만들 때만 사용)
const handleContactSelected = (contact: any) => {
  if (contact && contact.CONTACT_CD) {
    individualForm.value.controllerName = contact.CONTACT_CD;
    showNewContactModal.value = false;
    // Contact 선택 후 상세 정보는 handleContactUpdated에서 처리됨
  }
};

// Contact 상세 화면에서 선택 시 처리 (Select 버튼 클릭 시)
const handleContactDetailSelect = (contact: any) => {
  if (contact && (contact.CONTACT_CD || contact.contactCd)) {
    const contactCd = contact.CONTACT_CD || contact.contactCd;
    individualForm.value.controllerName = contactCd;
    showContactDetailModal.value = false;
    // Contact 선택 후 상세 정보는 handleContactUpdated에서 처리됨
  }
};

// Contact 상세 화면에서 수정 완료 후 자동 바인딩 필드 업데이트 (수정된 데이터 직접 반영)
const handleContactUpdated = async (contact: any) => {
  if (!contact) return;
  
  // 수정된 Contact 데이터로 필드 직접 업데이트 (빈 값도 반영)
  const contactRegNo = contact.contactRegNo || contact.CONTACT_REG_NO || '';
  individualForm.value.identificationNumber = contactRegNo;
  if (contactRegNo) {
    autoBoundFields.value.identificationNumber = true;
  }
  
  // aliases는 현재 페이지에서 수정 가능하므로 자동 바인딩하지 않음
  // individualForm.value.aliases는 수동 입력으로 유지
  
  const nationalityCd = contact.nationalityCd || contact.NATIONALITY_CD || '';
  individualForm.value.nationality = nationalityCd;
  if (nationalityCd) {
    autoBoundFields.value.nationality = true;
  }
  
  const dateOfBirth = contact.dateOfBirth || contact.DATE_OF_BIRTH || '';
  individualForm.value.dateOfBirth = dateOfBirth;
  dateOfBirthValue.value = dateOfBirth;
  if (dateOfBirth) {
    autoBoundFields.value.dateOfBirth = true;
  }
  
  const idTypeCd = contact.idTypeCd || contact.ID_TYPE_CD || '';
  individualForm.value.identificationType = idTypeCd;
  
  const contactEmail = contact.contactEmail || contact.CONTACT_EMAIL || '';
  individualForm.value.emailAddress = contactEmail;
  if (contactEmail) {
    autoBoundFields.value.emailAddress = true;
  }
  
  const countryCd = contact.countryCd || contact.COUNTRY_CD || '';
  individualForm.value.countryCode = countryCd;
  if (countryCd) {
    autoBoundFields.value.countryCode = true;
  }
  
  const areaCode = contact.areaCode || contact.AREA_CODE || '';
  individualForm.value.areaCode = areaCode;
  if (areaCode) {
    autoBoundFields.value.areaCode = true;
  }
  
  const mobileNo = contact.mobileNo || contact.MOBILE_NO || '';
  individualForm.value.contactNumber = mobileNo;
  if (mobileNo) {
    autoBoundFields.value.contactNumber = true;
  }
  
  const addressTypeCd = contact.addressTypeCd || contact.ADDRESS_TYPE_CD || '';
  individualForm.value.addressType = addressTypeCd;
  if (addressTypeCd) {
    autoBoundFields.value.addressType = true;
  }
  
  const postalCode = contact.postalCode || contact.POSTAL_CODE || '';
  individualForm.value.postalCode = postalCode;
  if (postalCode) {
    autoBoundFields.value.postalCode = true;
  }
  
  const blockHouseNo = contact.blockNo || contact.BLOCK_NO || contact.blockHouseNo || contact.BLOCK_HOUSE_NO || '';
  individualForm.value.blockHouseNo = blockHouseNo;
  if (blockHouseNo) {
    autoBoundFields.value.blockHouseNo = true;
  }
  
  const streetName = contact.streetName || contact.STREET_NAME || '';
  individualForm.value.streetName = streetName;
  if (streetName) {
    autoBoundFields.value.streetName = true;
  }
  
  const buildingName = contact.buildingName || contact.BUILDING_NAME || '';
  individualForm.value.buildingName = buildingName;
  if (buildingName) {
    autoBoundFields.value.buildingName = true;
  }
  
  const addrLevel = contact.addrLevel || contact.ADDR_LEVEL || '';
  individualForm.value.level = addrLevel;
  if (addrLevel) {
    autoBoundFields.value.level = true;
  }
  
  const addrUnit = contact.addrUnit || contact.ADDR_UNIT || '';
  individualForm.value.unit = addrUnit;
  if (addrUnit) {
    autoBoundFields.value.unit = true;
  }
  
  const foreignAddressLine1 = contact.foreignAddressLine1 || contact.FOREIGN_ADDRESS_LINE_1 || '';
  individualForm.value.foreignAddressLine1 = foreignAddressLine1;
  if (foreignAddressLine1) {
    autoBoundFields.value.foreignAddressLine1 = true;
  }
  
  const foreignAddressLine2 = contact.foreignAddressLine2 || contact.FOREIGN_ADDRESS_LINE_2 || '';
  individualForm.value.foreignAddressLine2 = foreignAddressLine2;
  if (foreignAddressLine2) {
    autoBoundFields.value.foreignAddressLine2 = true;
  }
  
  // Contact Address (대체 주소) 필드 업데이트
  const alternateAddressTypeCd = contact.alternateAddressTypeCd || contact.ALTERNATE_ADDRESS_CD || contact.addressTypeCd || contact.ADDRESS_TYPE_CD || '';
  individualForm.value.alternateAddressType = alternateAddressTypeCd;
  
  const altPostalCode = contact.altPostalCode || contact.ALT_POSTAL_CODE || '';
  individualForm.value.altPostalCode = altPostalCode;
  
  const altBlockNo = contact.altBlockNo || contact.ALT_BLOCK_NO || '';
  individualForm.value.altBlockHouseNo = altBlockNo;
  
  const altStreetName = contact.altStreetName || contact.ALT_STREET_NAME || '';
  individualForm.value.altStreetName = altStreetName;
  
  const altBuildingName = contact.altBuildingName || contact.ALT_BUILDING_NAME || '';
  individualForm.value.altBuildingName = altBuildingName;
  
  const altAddrLevel = contact.altAddrLevel || contact.ALT_ADDR_LEVEL || '';
  individualForm.value.altLevel = altAddrLevel;
  
  const altAddrUnit = contact.altAddrUnit || contact.ALT_ADDR_UNIT || '';
  individualForm.value.altUnit = altAddrUnit;
  
  const altForeignAddressLine1 = contact.altForeignAddressLine1 || contact.ALT_FOREIGN_ADDRESS_LINE_1 || '';
  individualForm.value.altForeignAddressLine1 = altForeignAddressLine1;
  
  const altForeignAddressLine2 = contact.altForeignAddressLine2 || contact.ALT_FOREIGN_ADDRESS_LINE_2 || '';
  individualForm.value.altForeignAddressLine2 = altForeignAddressLine2;
  
  // E-Filing 모드일 때는 controllers[0]과 individualForm 모두 업데이트
  if (props.eFilingMode && controllers.value.length > 0 && controllers.value[0].CATEGORY_CD === 'B09002') {
    const controller = controllers.value[0];
    controller.FULL_NAME = contact.contactName || contact.CONTACT_NAME || controller.FULL_NAME;
    controller.CONTACT_REG_NO = contactRegNo;
    controller.ID_TYPE = idTypeCd;
    controller.NATIONALITY_CD = nationalityCd;
    controller.DATE_OF_BIRTH = dateOfBirth ? dateOfBirth.substring(0, 10) : controller.DATE_OF_BIRTH;
    controller.ALIASES = contact.aliases || contact.ALIASES || controller.ALIASES;
    controller.CONTACT_EMAIL = contactEmail;
    controller.COUNTRY_CD = countryCd;
    controller.AREA_CODE = areaCode;
    controller.MOBILE_NO = mobileNo;
    controller.ADDRESS_TYPE_CD = addressTypeCd;
    controller.POSTAL_CODE = postalCode;
    controller.BLOCK_NO = blockHouseNo;
    controller.STREET_NAME = streetName;
    controller.BUILDING_NAME = buildingName;
    controller.ADDR_LEVEL = addrLevel;
    controller.ADDR_UNIT = addrUnit;
    controller.FOREIGN_ADDRESS_LINE_1 = foreignAddressLine1;
    controller.FOREIGN_ADDRESS_LINE_2 = foreignAddressLine2;
    
    // individualForm도 업데이트하여 getEFilingData()가 최신 정보를 반영하도록 함
    individualForm.value.identificationType = idTypeCd;
    individualForm.value.nationality = nationalityCd;
    individualForm.value.dateOfBirth = dateOfBirth;
    individualForm.value.emailAddress = contactEmail;
    individualForm.value.countryCode = countryCd;
    individualForm.value.areaCode = areaCode;
    individualForm.value.contactNumber = mobileNo;
    individualForm.value.addressType = addressTypeCd;
    individualForm.value.postalCode = postalCode;
    individualForm.value.blockHouseNo = blockHouseNo;
    individualForm.value.streetName = streetName;
    individualForm.value.buildingName = buildingName;
    individualForm.value.level = addrLevel;
    individualForm.value.unit = addrUnit;
    individualForm.value.foreignAddressLine1 = foreignAddressLine1;
    individualForm.value.foreignAddressLine2 = foreignAddressLine2;
    
    console.log('✅ [ControllerAppointmentForm] E-Filing 모드 Controller 및 Form 업데이트 완료:', controller);
    
    // E-Filing 모드이고 taskId가 있으면 eFilingData 업데이트
    if (props.taskId) {
      try {
        const updatedEFilingData = await getEFilingData();
        await updateControllerAppointmentEFilingData(props.taskId, updatedEFilingData);
        console.log('✅ [ControllerAppointmentForm] E-Filing 데이터 업데이트 완료:', updatedEFilingData);
      } catch (error) {
        console.error('❌ [ControllerAppointmentForm] E-Filing 데이터 업데이트 실패:', error);
      }
    }
  }
  
  console.log('✅ Controller 자동 바인딩 즉시 반영 완료:', contact);
};

// 개인 Controller 추가 폼 버튼 클릭 핸들러
const handleIndividualAddFormClick = () => {
  showIndividualAddForm.value = true;
  // 자동 선택 플래그 리셋 (버튼 클릭 시마다 자동 선택되도록)
  hasEmittedAutoSelectForIndividual.value = false;
  // 개인 컨트롤러(B09002)용 문서 자동 선택
  autoSelectTemplatesByCategory('B09002');
  // Task Flow 자동 업데이트
  updateTaskFlow();
};

// 법인 Controller 추가 폼 버튼 클릭 핸들러
const handleCorporateAddFormClick = () => {
  showCorporateAddForm.value = true;
  // 자동 선택 플래그 리셋 (버튼 클릭 시마다 자동 선택되도록)
  hasEmittedAutoSelectForCorporate.value = false;
  // 법인 컨트롤러(B09001)용 문서 자동 선택
  autoSelectTemplatesByCategory('B09001');
  // Task Flow 자동 업데이트
  updateTaskFlow();
};

// 개인 Controller 폼 DELETE 버튼 클릭 핸들러
const handleDeleteIndividualForm = () => {
  showIndividualAddForm.value = false;
  resetIndividualForm();
  // 자동 선택 플래그 리셋
  hasEmittedAutoSelectForIndividual.value = false;
  // 템플릿 초기화
  emit('reset-templates');
};

// 법인 Controller 폼 DELETE 버튼 클릭 핸들러
const handleDeleteCorporateForm = () => {
  showCorporateAddForm.value = false;
  resetCorporateForm();
  // 자동 선택 플래그 리셋
  hasEmittedAutoSelectForCorporate.value = false;
  // 템플릿 초기화
  emit('reset-templates');
};

// 개인 Controller 폼 초기화
const resetIndividualForm = () => {
  individualForm.value = {
    controllerName: '',
    identificationType: '',
    identificationNumber: '',
    aliases: '',
    nationality: '',
    dateOfBirth: '',
    emailAddress: '',
    countryCode: '',
    areaCode: '',
    contactNumber: '',
    addressType: '',
    postalCode: '',
    blockHouseNo: '',
    streetName: '',
    buildingName: '',
    level: '',
    unit: '',
    foreignAddressLine1: '',
    foreignAddressLine2: '',
    // Contact Address (대체 주소)
    alternateAddressType: '',
    altPostalCode: '',
    altBlockHouseNo: '',
    altStreetName: '',
    altBuildingName: '',
    altLevel: '',
    altUnit: '',
    altForeignAddressLine1: '',
    altForeignAddressLine2: '',
    dateOfBecoming: '',
    confirmationByController: 'No'
  };
  Object.keys(autoBoundFields.value).forEach(key => {
    autoBoundFields.value[key] = false;
  });
  dateOfBirthValue.value = '';
  dateOfBecomingField.dateValue.value = '';
};

// 법인 Controller Name 선택 시 자동 바인딩
watch(() => corporateForm.value.controllerName, async (contactCd, oldContactCd) => {
  // 데이터 복원 중이면 DB 조회하지 않음 (setFormDataFromInitialData에서 이미 설정됨)
  if (isRestoringData.value) {
    console.log('📋 [ControllerAppointmentForm] 데이터 복원 중 - Contact DB 조회 건너뜀');
    return;
  }

  // Controller가 변경되거나 선택 해제될 때 이전 데이터 초기화
  if (!contactCd || (oldContactCd && contactCd !== oldContactCd)) {
    // 이전 Controller의 데이터 초기화
    Object.keys(corporateAutoBoundFields.value).forEach(key => {
      corporateAutoBoundFields.value[key] = false;
    });
    corporateForm.value.uen = '';
    corporateForm.value.legalForm = '';
    corporateForm.value.jurisdiction = '';
    corporateForm.value.incorporationStatute = '';
    corporateForm.value.corporateEntityRegister = '';
    corporateForm.value.emailAddress = '';
    corporateForm.value.countryCode = '';
    corporateForm.value.areaCode = '';
    corporateForm.value.contactNumber = '';
    corporateForm.value.addressType = '';
    corporateForm.value.postalCode = '';
    corporateForm.value.blockHouseNo = '';
    corporateForm.value.streetName = '';
    corporateForm.value.buildingName = '';
    corporateForm.value.level = '';
    corporateForm.value.unit = '';
    corporateForm.value.foreignAddressLine1 = '';
    corporateForm.value.foreignAddressLine2 = '';
    // Contact Address (대체 주소) 초기화
    corporateForm.value.alternateAddressType = '';
    corporateForm.value.altPostalCode = '';
    corporateForm.value.altBlockHouseNo = '';
    corporateForm.value.altStreetName = '';
    corporateForm.value.altBuildingName = '';
    corporateForm.value.altLevel = '';
    corporateForm.value.altUnit = '';
    corporateForm.value.altForeignAddressLine1 = '';
    corporateForm.value.altForeignAddressLine2 = '';
    
    // 선택 해제 시에만 자동 선택 플래그 리셋
    if (!contactCd) {
    hasEmittedAutoSelectForCorporate.value = false;
      return;
    }
  }
  
  // 새로운 Controller가 선택된 경우에만 데이터 바인딩
  if (!contactCd) {
    return;
  }
  
  try {
    // Contact 상세 정보 조회
    const response = await contactsApi.getContactById(contactCd);
    const contact = (response as any).contact || response.data?.contact;
    
    if (contact) {
      // 자동 바인딩
      // UEN (REG_NO 또는 CONTACT_REG_NO)
      if (contact.regNo || contact.REG_NO || contact.contactRegNo || contact.CONTACT_REG_NO) {
        corporateForm.value.uen = contact.regNo || contact.REG_NO || contact.contactRegNo || contact.CONTACT_REG_NO;
        corporateAutoBoundFields.value.uen = true;
      }
      
      // Legal Form
      if (contact.legalForm || contact.LEGAL_FORM) {
        corporateForm.value.legalForm = contact.legalForm || contact.LEGAL_FORM;
        corporateAutoBoundFields.value.legalForm = true;
      }
      
      // Jurisdiction
      if (contact.jurisdiction || contact.JURISDICTION) {
        corporateForm.value.jurisdiction = contact.jurisdiction || contact.JURISDICTION;
        corporateAutoBoundFields.value.jurisdiction = true;
      }
      
      // Incorporation Statute
      if (contact.incorporationStatute || contact.INCORPORATION_STATUTE) {
        corporateForm.value.incorporationStatute = contact.incorporationStatute || contact.INCORPORATION_STATUTE;
        corporateAutoBoundFields.value.incorporationStatute = true;
      }
      
      // Corporate Entity Register
      if (contact.corporateEntityRegister || contact.CORPORATE_ENTITY_REGISTER) {
        corporateForm.value.corporateEntityRegister = contact.corporateEntityRegister || contact.CORPORATE_ENTITY_REGISTER;
        corporateAutoBoundFields.value.corporateEntityRegister = true;
      }
      
      // Email
      if (contact.contactEmail || contact.CONTACT_EMAIL) {
        corporateForm.value.emailAddress = contact.contactEmail || contact.CONTACT_EMAIL;
        corporateAutoBoundFields.value.emailAddress = true;
      }
      
      // Country Code
      if (contact.countryCd || contact.COUNTRY_CD) {
        corporateForm.value.countryCode = contact.countryCd || contact.COUNTRY_CD;
        corporateAutoBoundFields.value.countryCode = true;
      }
      
      // Area Code
      if (contact.areaCode || contact.AREA_CODE) {
        corporateForm.value.areaCode = contact.areaCode || contact.AREA_CODE;
        corporateAutoBoundFields.value.areaCode = true;
      }
      
      // Mobile No
      if (contact.mobileNo || contact.MOBILE_NO) {
        corporateForm.value.contactNumber = contact.mobileNo || contact.MOBILE_NO;
        corporateAutoBoundFields.value.contactNumber = true;
      }
      
      // Address Type
      if (contact.addressTypeCd || contact.ADDRESS_TYPE_CD) {
        corporateForm.value.addressType = contact.addressTypeCd || contact.ADDRESS_TYPE_CD;
        corporateAutoBoundFields.value.addressType = true;
      }
      
      // Postal Code
      if (contact.postalCode || contact.POSTAL_CODE) {
        corporateForm.value.postalCode = contact.postalCode || contact.POSTAL_CODE;
        corporateAutoBoundFields.value.postalCode = true;
      }
      
      // Block No
      if (contact.blockNo || contact.BLOCK_NO || contact.blockHouseNo || contact.BLOCK_HOUSE_NO) {
        corporateForm.value.blockHouseNo = contact.blockNo || contact.BLOCK_NO || contact.blockHouseNo || contact.BLOCK_HOUSE_NO;
        corporateAutoBoundFields.value.blockHouseNo = true;
      }
      
      // Street Name
      if (contact.streetName || contact.STREET_NAME) {
        corporateForm.value.streetName = contact.streetName || contact.STREET_NAME;
        corporateAutoBoundFields.value.streetName = true;
      }
      
      // Building Name
      if (contact.buildingName || contact.BUILDING_NAME) {
        corporateForm.value.buildingName = contact.buildingName || contact.BUILDING_NAME;
        corporateAutoBoundFields.value.buildingName = true;
      }
      
      // Level
      if (contact.addrLevel || contact.ADDR_LEVEL) {
        corporateForm.value.level = contact.addrLevel || contact.ADDR_LEVEL;
        corporateAutoBoundFields.value.level = true;
      }
      
      // Unit
      if (contact.addrUnit || contact.ADDR_UNIT) {
        corporateForm.value.unit = contact.addrUnit || contact.ADDR_UNIT;
        corporateAutoBoundFields.value.unit = true;
      }
      
      // Foreign Address Line 1
      if (contact.foreignAddressLine1 || contact.FOREIGN_ADDRESS_LINE_1) {
        corporateForm.value.foreignAddressLine1 = contact.foreignAddressLine1 || contact.FOREIGN_ADDRESS_LINE_1;
        corporateAutoBoundFields.value.foreignAddressLine1 = true;
      }
      
      // Foreign Address Line 2
      if (contact.foreignAddressLine2 || contact.FOREIGN_ADDRESS_LINE_2) {
        corporateForm.value.foreignAddressLine2 = contact.foreignAddressLine2 || contact.FOREIGN_ADDRESS_LINE_2;
        corporateAutoBoundFields.value.foreignAddressLine2 = true;
      }
      
      // Contact Address (대체 주소) 필드 바인딩
      if (contact.alternateAddressTypeCd || contact.ALTERNATE_ADDRESS_CD) {
        corporateForm.value.alternateAddressType = contact.alternateAddressTypeCd || contact.ALTERNATE_ADDRESS_CD;
      }
      if (contact.altPostalCode || contact.ALT_POSTAL_CODE) {
        corporateForm.value.altPostalCode = contact.altPostalCode || contact.ALT_POSTAL_CODE;
      }
      if (contact.altBlockNo || contact.ALT_BLOCK_NO) {
        corporateForm.value.altBlockHouseNo = contact.altBlockNo || contact.ALT_BLOCK_NO;
      }
      if (contact.altStreetName || contact.ALT_STREET_NAME) {
        corporateForm.value.altStreetName = contact.altStreetName || contact.ALT_STREET_NAME;
      }
      if (contact.altBuildingName || contact.ALT_BUILDING_NAME) {
        corporateForm.value.altBuildingName = contact.altBuildingName || contact.ALT_BUILDING_NAME;
      }
      if (contact.altAddrLevel || contact.ALT_ADDR_LEVEL) {
        corporateForm.value.altLevel = contact.altAddrLevel || contact.ALT_ADDR_LEVEL;
      }
      if (contact.altAddrUnit || contact.ALT_ADDR_UNIT) {
        corporateForm.value.altUnit = contact.altAddrUnit || contact.ALT_ADDR_UNIT;
      }
      if (contact.altForeignAddressLine1 || contact.ALT_FOREIGN_ADDRESS_LINE_1) {
        corporateForm.value.altForeignAddressLine1 = contact.altForeignAddressLine1 || contact.ALT_FOREIGN_ADDRESS_LINE_1;
      }
      if (contact.altForeignAddressLine2 || contact.ALT_FOREIGN_ADDRESS_LINE_2) {
        corporateForm.value.altForeignAddressLine2 = contact.altForeignAddressLine2 || contact.ALT_FOREIGN_ADDRESS_LINE_2;
      }
      
      console.log('✅ 법인 Controller 자동 바인딩 완료:', contact);
    }
  } catch (error: any) {
    console.error('❌ Failed to load corporate contact details:', error);
  }
});

// 법인 Controller 폼 초기화
const resetCorporateForm = () => {
  corporateForm.value = {
    controllerName: '',
    uen: '',
    legalForm: '',
    jurisdiction: '',
    incorporationStatute: '',
    corporateEntityRegister: '',
    emailAddress: '',
    countryCode: '',
    areaCode: '',
    contactNumber: '',
    addressType: '',
    postalCode: '',
    blockHouseNo: '',
    streetName: '',
    buildingName: '',
    level: '',
    unit: '',
    foreignAddressLine1: '',
    foreignAddressLine2: '',
    // Contact Address (대체 주소)
    alternateAddressType: '',
    altPostalCode: '',
    altBlockHouseNo: '',
    altStreetName: '',
    altBuildingName: '',
    altLevel: '',
    altUnit: '',
    altForeignAddressLine1: '',
    altForeignAddressLine2: '',
    dateOfBecoming: '',
    confirmationByController: 'No'
  };
  Object.keys(corporateAutoBoundFields.value).forEach(key => {
    corporateAutoBoundFields.value[key] = false;
  });
  corporateDateOfBecomingField.dateValue.value = '';
};

// 법인 Controller용 날짜 필드
const corporateDateOfBecomingField = useDateField();
const corporateDateOfBecomingDisplay = computed(() => formatDateByJurisdiction(corporateDateOfBecomingField.dateValue.value));
const corporateDateOfBecomingMenu = computed({
  get: () => corporateDateOfBecomingField.dateMenu.value,
  set: (val: boolean) => { corporateDateOfBecomingField.dateMenu.value = val; }
});
const corporateDateOfBecomingPickerValue = computed({
  get: () => corporateDateOfBecomingField.datePickerValue.value,
  set: (val: Date | null) => { corporateDateOfBecomingField.datePickerValue.value = val; }
});

watch(() => corporateDateOfBecomingField.dateValue.value, (value) => {
  corporateForm.value.dateOfBecoming = value || '';
});

// 내부 validation 상태 관리
const internalValidationErrors = ref<Record<string, string>>({});

// Controller Required Error Messages (computed)
const controllerRequiredErrorMessages = computed(() => {
  if (internalValidationErrors.value.controllerRequired) {
    return [internalValidationErrors.value.controllerRequired];
  }
  return [];
});

// 필드 refs (포커스 이동용)
const individualControllerNameInputRef = ref<any>(null);
const individualDateOfBecomingInputRef = ref<any>(null);
const individualEmailAddressInputRef = ref<any>(null);
const individualCountryCodeInputRef = ref<any>(null);
const individualContactNumberInputRef = ref<any>(null);
const corporateControllerNameInputRef = ref<any>(null);
const corporateDateOfBecomingInputRef = ref<any>(null);
const corporateEmailAddressInputRef = ref<any>(null);
const corporateCountryCodeInputRef = ref<any>(null);
const corporateContactNumberInputRef = ref<any>(null);

// 개인 Controller Name 검증 함수
const validateIndividualControllerName = (): boolean => {
  if (!showIndividualAddForm.value) {
    return true;
  }
  if (!individualForm.value.controllerName || !individualForm.value.controllerName.trim()) {
    internalValidationErrors.value.individualControllerName = t('startTask.This field is required') || 'This field is required';
    internalValidationErrors.value = { ...internalValidationErrors.value };
    return false;
  }
  if (internalValidationErrors.value.individualControllerName) {
    delete internalValidationErrors.value.individualControllerName;
  }
  return true;
};

// 개인 Date of Becoming 검증 함수
const validateIndividualDateOfBecoming = (): boolean => {
  // E-Filing 모드가 아니고 폼이 열려있지 않으면 검증하지 않음
  if (!props.eFilingMode && !showIndividualAddForm.value) {
    return true;
  }
  // E-Filing 모드일 때는 폼이 열려있지 않아도 controllers 배열에 개인 컨트롤러가 있으면 검증
  if (props.eFilingMode && !showIndividualAddForm.value) {
    const hasIndividualController = controllers.value.some(c => c.CATEGORY_CD === 'B09002');
    if (!hasIndividualController) {
      return true;
    }
    // E-Filing 모드에서는 controllers 배열의 값도 확인
    const individualController = controllers.value.find(c => c.CATEGORY_CD === 'B09002');
    if (individualController) {
      const dateValue = dateOfBecomingField.dateValue.value || individualController.BECOMING_DATE || '';
      if (!dateValue.trim()) {
    internalValidationErrors.value.individualDateOfBecoming = t('startTask.This field is required') || 'This field is required';
        internalValidationErrors.value = { ...internalValidationErrors.value };
        return false;
      }
      if (internalValidationErrors.value.individualDateOfBecoming) {
        delete internalValidationErrors.value.individualDateOfBecoming;
      }
      return true;
    }
  }
  if (!dateOfBecomingField.dateValue.value || !dateOfBecomingField.dateValue.value.trim()) {
    internalValidationErrors.value.individualDateOfBecoming = t('startTask.This field is required') || 'This field is required';
    internalValidationErrors.value = { ...internalValidationErrors.value };
    return false;
  }
  if (internalValidationErrors.value.individualDateOfBecoming) {
    delete internalValidationErrors.value.individualDateOfBecoming;
  }
  return true;
};

// 개인 Email Address 검증 함수
const validateIndividualEmailAddress = (): boolean => {
  // E-Filing 모드가 아니고 폼이 열려있지 않으면 검증하지 않음
  if (!props.eFilingMode && !showIndividualAddForm.value) {
    return true;
  }
  // E-Filing 모드일 때는 폼이 열려있지 않아도 controllers 배열에 개인 컨트롤러가 있으면 검증
  if (props.eFilingMode && !showIndividualAddForm.value) {
    const hasIndividualController = controllers.value.some(c => c.CATEGORY_CD === 'B09002');
    if (!hasIndividualController) {
      return true;
    }
    // E-Filing 모드에서는 controllers 배열의 값도 확인
    const individualController = controllers.value.find(c => c.CATEGORY_CD === 'B09002');
    if (individualController) {
      const emailValue = individualForm.value.emailAddress || individualController.CONTACT_EMAIL || '';
      if (!emailValue.trim()) {
    internalValidationErrors.value.individualEmailAddress = t('startTask.This field is required') || 'This field is required';
        internalValidationErrors.value = { ...internalValidationErrors.value };
        return false;
      }
      if (internalValidationErrors.value.individualEmailAddress) {
        delete internalValidationErrors.value.individualEmailAddress;
      }
      return true;
    }
  }
  if (!individualForm.value.emailAddress || !individualForm.value.emailAddress.trim()) {
    internalValidationErrors.value.individualEmailAddress = t('startTask.This field is required') || 'This field is required';
    internalValidationErrors.value = { ...internalValidationErrors.value };
    return false;
  }
  if (internalValidationErrors.value.individualEmailAddress) {
    delete internalValidationErrors.value.individualEmailAddress;
  }
  return true;
};

// 개인 Country Code 검증 함수
const validateIndividualCountryCode = (): boolean => {
  // E-Filing 모드가 아니고 폼이 열려있지 않으면 검증하지 않음
  if (!props.eFilingMode && !showIndividualAddForm.value) {
    return true;
  }
  // E-Filing 모드일 때는 폼이 열려있지 않아도 controllers 배열에 개인 컨트롤러가 있으면 검증
  if (props.eFilingMode && !showIndividualAddForm.value) {
    const hasIndividualController = controllers.value.some(c => c.CATEGORY_CD === 'B09002');
    if (!hasIndividualController) {
      return true;
    }
    // E-Filing 모드에서는 controllers 배열의 값도 확인
    const individualController = controllers.value.find(c => c.CATEGORY_CD === 'B09002');
    if (individualController) {
      const countryCodeValue = individualForm.value.countryCode || individualController.COUNTRY_CD || '';
      if (!countryCodeValue.trim()) {
    internalValidationErrors.value.individualCountryCode = t('startTask.This field is required') || 'This field is required';
        internalValidationErrors.value = { ...internalValidationErrors.value };
        return false;
      }
      if (internalValidationErrors.value.individualCountryCode) {
        delete internalValidationErrors.value.individualCountryCode;
      }
      return true;
    }
  }
  if (!individualForm.value.countryCode || !individualForm.value.countryCode.trim()) {
    internalValidationErrors.value.individualCountryCode = t('startTask.This field is required') || 'This field is required';
    internalValidationErrors.value = { ...internalValidationErrors.value };
    return false;
  }
  if (internalValidationErrors.value.individualCountryCode) {
    delete internalValidationErrors.value.individualCountryCode;
  }
  return true;
};

// 개인 Contact Number 검증 함수
const validateIndividualContactNumber = (): boolean => {
  // E-Filing 모드가 아니고 폼이 열려있지 않으면 검증하지 않음
  if (!props.eFilingMode && !showIndividualAddForm.value) {
    return true;
  }
  // E-Filing 모드일 때는 폼이 열려있지 않아도 controllers 배열에 개인 컨트롤러가 있으면 검증
  if (props.eFilingMode && !showIndividualAddForm.value) {
    const hasIndividualController = controllers.value.some(c => c.CATEGORY_CD === 'B09002');
    if (!hasIndividualController) {
      return true;
    }
    // E-Filing 모드에서는 controllers 배열의 값도 확인
    const individualController = controllers.value.find(c => c.CATEGORY_CD === 'B09002');
    if (individualController) {
      const contactNumberValue = individualForm.value.contactNumber || individualController.MOBILE_NO || '';
      if (!contactNumberValue.trim()) {
    internalValidationErrors.value.individualContactNumber = t('startTask.This field is required') || 'This field is required';
        internalValidationErrors.value = { ...internalValidationErrors.value };
    nextTick(() => {
          const field = document.querySelector('[data-field="individualContactNumber"]');
          if (field) {
            const textField = field.closest('.v-text-field');
            if (textField) {
              (textField as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
              const inputElement = (textField as HTMLElement).querySelector('input');
        if (inputElement) {
                setTimeout(() => {
          inputElement.focus();
                }, 100);
              }
        }
      }
    });
        return false;
      }
      if (internalValidationErrors.value.individualContactNumber) {
        delete internalValidationErrors.value.individualContactNumber;
      }
      return true;
    }
  }
  if (!individualForm.value.contactNumber || !individualForm.value.contactNumber.trim()) {
    internalValidationErrors.value.individualContactNumber = t('startTask.This field is required') || 'This field is required';
    internalValidationErrors.value = { ...internalValidationErrors.value };
    return false;
  }
  if (internalValidationErrors.value.individualContactNumber) {
    delete internalValidationErrors.value.individualContactNumber;
  }
  return true;
};

// 법인 Controller Name 검증 함수
const validateCorporateControllerName = (): boolean => {
  if (!showCorporateAddForm.value) {
    return true;
  }
  if (!corporateForm.value.controllerName || !corporateForm.value.controllerName.trim()) {
    internalValidationErrors.value.corporateControllerName = t('startTask.This field is required') || 'This field is required';
    internalValidationErrors.value = { ...internalValidationErrors.value };
    return false;
  }
  if (internalValidationErrors.value.corporateControllerName) {
    delete internalValidationErrors.value.corporateControllerName;
  }
  return true;
};

// 법인 Date of Becoming 검증 함수
const validateCorporateDateOfBecoming = (): boolean => {
  // E-Filing 모드가 아니고 폼이 열려있지 않으면 검증하지 않음
  if (!props.eFilingMode && !showCorporateAddForm.value) {
    return true;
  }
  // E-Filing 모드일 때는 폼이 열려있지 않아도 controllers 배열에 법인 컨트롤러가 있으면 검증
  if (props.eFilingMode && !showCorporateAddForm.value) {
    const hasCorporateController = controllers.value.some(c => c.CATEGORY_CD === 'B09001');
    if (!hasCorporateController) {
      return true;
    }
    // E-Filing 모드에서는 controllers 배열의 값도 확인
    const corporateController = controllers.value.find(c => c.CATEGORY_CD === 'B09001');
    if (corporateController) {
      const dateValue = corporateDateOfBecomingField.dateValue.value || corporateController.BECOMING_DATE || '';
      if (!dateValue.trim()) {
    internalValidationErrors.value.corporateDateOfBecoming = t('startTask.This field is required') || 'This field is required';
        internalValidationErrors.value = { ...internalValidationErrors.value };
        return false;
      }
      if (internalValidationErrors.value.corporateDateOfBecoming) {
        delete internalValidationErrors.value.corporateDateOfBecoming;
      }
      return true;
    }
  }
  if (!corporateDateOfBecomingField.dateValue.value || !corporateDateOfBecomingField.dateValue.value.trim()) {
    internalValidationErrors.value.corporateDateOfBecoming = t('startTask.This field is required') || 'This field is required';
    internalValidationErrors.value = { ...internalValidationErrors.value };
    return false;
  }
  if (internalValidationErrors.value.corporateDateOfBecoming) {
    delete internalValidationErrors.value.corporateDateOfBecoming;
  }
  return true;
};

// 법인 Email Address 검증 함수
const validateCorporateEmailAddress = (): boolean => {
  // E-Filing 모드가 아니고 폼이 열려있지 않으면 검증하지 않음
  if (!props.eFilingMode && !showCorporateAddForm.value) {
    return true;
  }
  // E-Filing 모드일 때는 폼이 열려있지 않아도 controllers 배열에 법인 컨트롤러가 있으면 검증
  if (props.eFilingMode && !showCorporateAddForm.value) {
    const hasCorporateController = controllers.value.some(c => c.CATEGORY_CD === 'B09001');
    if (!hasCorporateController) {
      return true;
    }
    // E-Filing 모드에서는 controllers 배열의 값도 확인
    const corporateController = controllers.value.find(c => c.CATEGORY_CD === 'B09001');
    if (corporateController) {
      const emailValue = corporateForm.value.emailAddress || corporateController.CONTACT_EMAIL || '';
      if (!emailValue.trim()) {
    internalValidationErrors.value.corporateEmailAddress = t('startTask.This field is required') || 'This field is required';
        internalValidationErrors.value = { ...internalValidationErrors.value };
        return false;
      }
      if (internalValidationErrors.value.corporateEmailAddress) {
        delete internalValidationErrors.value.corporateEmailAddress;
      }
      return true;
    }
  }
  if (!corporateForm.value.emailAddress || !corporateForm.value.emailAddress.trim()) {
    internalValidationErrors.value.corporateEmailAddress = t('startTask.This field is required') || 'This field is required';
    internalValidationErrors.value = { ...internalValidationErrors.value };
    return false;
  }
  if (internalValidationErrors.value.corporateEmailAddress) {
    delete internalValidationErrors.value.corporateEmailAddress;
  }
  return true;
};

// 법인 Country Code 검증 함수
const validateCorporateCountryCode = (): boolean => {
  // E-Filing 모드가 아니고 폼이 열려있지 않으면 검증하지 않음
  if (!props.eFilingMode && !showCorporateAddForm.value) {
    return true;
  }
  // E-Filing 모드일 때는 폼이 열려있지 않아도 controllers 배열에 법인 컨트롤러가 있으면 검증
  if (props.eFilingMode && !showCorporateAddForm.value) {
    const hasCorporateController = controllers.value.some(c => c.CATEGORY_CD === 'B09001');
    if (!hasCorporateController) {
      return true;
    }
    // E-Filing 모드에서는 controllers 배열의 값도 확인
    const corporateController = controllers.value.find(c => c.CATEGORY_CD === 'B09001');
    if (corporateController) {
      const countryCodeValue = corporateForm.value.countryCode || corporateController.COUNTRY_CD || '';
      if (!countryCodeValue.trim()) {
    internalValidationErrors.value.corporateCountryCode = t('startTask.This field is required') || 'This field is required';
        internalValidationErrors.value = { ...internalValidationErrors.value };
        return false;
      }
      if (internalValidationErrors.value.corporateCountryCode) {
        delete internalValidationErrors.value.corporateCountryCode;
      }
      return true;
    }
  }
  if (!corporateForm.value.countryCode || !corporateForm.value.countryCode.trim()) {
    internalValidationErrors.value.corporateCountryCode = t('startTask.This field is required') || 'This field is required';
    internalValidationErrors.value = { ...internalValidationErrors.value };
    return false;
  }
  if (internalValidationErrors.value.corporateCountryCode) {
    delete internalValidationErrors.value.corporateCountryCode;
  }
  return true;
};

// 법인 Contact Number 검증 함수
const validateCorporateContactNumber = (): boolean => {
  // E-Filing 모드가 아니고 폼이 열려있지 않으면 검증하지 않음
  if (!props.eFilingMode && !showCorporateAddForm.value) {
    return true;
  }
  // E-Filing 모드일 때는 폼이 열려있지 않아도 controllers 배열에 법인 컨트롤러가 있으면 검증
  if (props.eFilingMode && !showCorporateAddForm.value) {
    const hasCorporateController = controllers.value.some(c => c.CATEGORY_CD === 'B09001');
    if (!hasCorporateController) {
      return true;
    }
    // E-Filing 모드에서는 controllers 배열의 값도 확인
    const corporateController = controllers.value.find(c => c.CATEGORY_CD === 'B09001');
    if (corporateController) {
      const contactNumberValue = corporateForm.value.contactNumber || corporateController.MOBILE_NO || '';
      if (!contactNumberValue.trim()) {
    internalValidationErrors.value.corporateContactNumber = t('startTask.This field is required') || 'This field is required';
        internalValidationErrors.value = { ...internalValidationErrors.value };
        return false;
      }
      if (internalValidationErrors.value.corporateContactNumber) {
        delete internalValidationErrors.value.corporateContactNumber;
      }
      return true;
    }
  }
  if (!corporateForm.value.contactNumber || !corporateForm.value.contactNumber.trim()) {
    internalValidationErrors.value.corporateContactNumber = t('startTask.This field is required') || 'This field is required';
    internalValidationErrors.value = { ...internalValidationErrors.value };
    return false;
  }
  if (internalValidationErrors.value.corporateContactNumber) {
    delete internalValidationErrors.value.corporateContactNumber;
  }
  return true;
};

// 법인 Validation 체크 함수 (deprecated - validateAll 사용)
const validateCorporateForm = (): boolean => {
  if (!showCorporateAddForm.value) {
    return true;
  }
  const isControllerNameValid = validateCorporateControllerName();
  if (!isControllerNameValid) return false;
  const isDateOfBecomingValid = validateCorporateDateOfBecoming();
  if (!isDateOfBecomingValid) return false;
  const isEmailAddressValid = validateCorporateEmailAddress();
  if (!isEmailAddressValid) return false;
  const isCountryCodeValid = validateCorporateCountryCode();
  if (!isCountryCodeValid) return false;
  const isContactNumberValid = validateCorporateContactNumber();
  if (!isContactNumberValid) return false;
  return true;
};

// 법인 주소 조합 (표시용) - 공통 함수 사용
const formattedCorporateAddress = computed(() => {
  const form = corporateForm.value;
  const addressInfo = {
    ADDRESS_TYPE_CD: form.addressType || '',
    BLOCK_NO: form.blockHouseNo || '',
    STREET_NAME: form.streetName || '',
    ADDR_LEVEL: form.level || '',
    ADDR_UNIT: form.unit || '',
    BUILDING_NAME: form.buildingName || '',
    POSTAL_CODE: form.postalCode || '',
    FOREIGN_ADDRESS_LINE_1: form.foreignAddressLine1 || '',
    FOREIGN_ADDRESS_LINE_2: form.foreignAddressLine2 || ''
  };
  const address = formatAddressUtil(addressInfo, { useComma: true });
  return typeof address === 'string' ? address : '';
});

// 법인 Save 핸들러
const handleSaveCorporateController = async () => {
  // Validation 체크
  if (!validateCorporateForm()) {
    console.log('Corporate Validation failed:', internalValidationErrors.value);
    return;
  }

  // TODO: 실제 저장 로직 구현
  console.log('Save corporate controller:', corporateForm.value);
  
  // 저장 후 폼 닫기 및 초기화
  showCorporateAddForm.value = false;
  resetCorporateForm();
  
  // Controller 목록 새로고침
  if (entity.value) {
    await loadControllers(entity.value);
  }
};

// 법인 New Contact 모달 열기
const handleNewCorporateContact = () => {
  // Controller Name이 선택되어 있으면 해당 Contact의 상세 화면으로 바로 이동
  if (corporateForm.value.controllerName) {
    showCorporateContactDetailModal.value = true;
  } else {
    // Controller Name이 선택되지 않았으면 Contact 리스트로 이동 (Corporate 생성 가능)
    showNewCorporateContactModal.value = true;
  }
};

// 법인 Contact 상세 화면 모달
const showCorporateContactDetailModal = ref(false);

// 법인 New Contact 선택 시 처리
const handleCorporateContactSelected = (contact: any) => {
  if (contact && contact.CONTACT_CD) {
    corporateForm.value.controllerName = contact.CONTACT_CD;
    showNewCorporateContactModal.value = false;
  }
};

// 법인 Contact 상세 화면에서 선택 시 처리
const handleCorporateContactDetailSelect = (contact: any) => {
  if (contact && (contact.CONTACT_CD || contact.contactCd)) {
    const contactCd = contact.CONTACT_CD || contact.contactCd;
    corporateForm.value.controllerName = contactCd;
    showCorporateContactDetailModal.value = false;
  }
};

// 법인 Contact 상세 화면에서 수정 완료 후 자동 바인딩 필드 업데이트
const handleCorporateContactUpdated = async (contact: any) => {
  if (!contact) return;
  
  // 수정된 Contact 데이터로 필드 직접 업데이트
  const regNo = contact.regNo || contact.REG_NO || contact.contactRegNo || contact.CONTACT_REG_NO || '';
  corporateForm.value.uen = regNo;
  if (regNo) {
    corporateAutoBoundFields.value.uen = true;
  }
  
  const legalForm = contact.legalForm || contact.LEGAL_FORM || '';
  corporateForm.value.legalForm = legalForm;
  if (legalForm) {
    corporateAutoBoundFields.value.legalForm = true;
  }
  
  const jurisdiction = contact.jurisdiction || contact.JURISDICTION || '';
  corporateForm.value.jurisdiction = jurisdiction;
  if (jurisdiction) {
    corporateAutoBoundFields.value.jurisdiction = true;
  }
  
  const incorporationStatute = contact.incorporationStatute || contact.INCORPORATION_STATUTE || '';
  corporateForm.value.incorporationStatute = incorporationStatute;
  if (incorporationStatute) {
    corporateAutoBoundFields.value.incorporationStatute = true;
  }
  
  const corporateEntityRegister = contact.corporateEntityRegister || contact.CORPORATE_ENTITY_REGISTER || '';
  corporateForm.value.corporateEntityRegister = corporateEntityRegister;
  if (corporateEntityRegister) {
    corporateAutoBoundFields.value.corporateEntityRegister = true;
  }
  
  const contactEmail = contact.contactEmail || contact.CONTACT_EMAIL || '';
  corporateForm.value.emailAddress = contactEmail;
  if (contactEmail) {
    corporateAutoBoundFields.value.emailAddress = true;
  }
  
  const countryCd = contact.countryCd || contact.COUNTRY_CD || '';
  corporateForm.value.countryCode = countryCd;
  if (countryCd) {
    corporateAutoBoundFields.value.countryCode = true;
  }
  
  const areaCode = contact.areaCode || contact.AREA_CODE || '';
  corporateForm.value.areaCode = areaCode;
  if (areaCode) {
    corporateAutoBoundFields.value.areaCode = true;
  }
  
  const mobileNo = contact.mobileNo || contact.MOBILE_NO || '';
  corporateForm.value.contactNumber = mobileNo;
  if (mobileNo) {
    corporateAutoBoundFields.value.contactNumber = true;
  }
  
  const addressTypeCd = contact.addressTypeCd || contact.ADDRESS_TYPE_CD || '';
  corporateForm.value.addressType = addressTypeCd;
  if (addressTypeCd) {
    corporateAutoBoundFields.value.addressType = true;
  }
  
  const postalCode = contact.postalCode || contact.POSTAL_CODE || '';
  corporateForm.value.postalCode = postalCode;
  if (postalCode) {
    corporateAutoBoundFields.value.postalCode = true;
  }
  
  const blockHouseNo = contact.blockNo || contact.BLOCK_NO || contact.blockHouseNo || contact.BLOCK_HOUSE_NO || '';
  corporateForm.value.blockHouseNo = blockHouseNo;
  if (blockHouseNo) {
    corporateAutoBoundFields.value.blockHouseNo = true;
  }
  
  const streetName = contact.streetName || contact.STREET_NAME || '';
  corporateForm.value.streetName = streetName;
  if (streetName) {
    corporateAutoBoundFields.value.streetName = true;
  }
  
  const buildingName = contact.buildingName || contact.BUILDING_NAME || '';
  corporateForm.value.buildingName = buildingName;
  if (buildingName) {
    corporateAutoBoundFields.value.buildingName = true;
  }
  
  const addrLevel = contact.addrLevel || contact.ADDR_LEVEL || '';
  corporateForm.value.level = addrLevel;
  if (addrLevel) {
    corporateAutoBoundFields.value.level = true;
  }
  
  const addrUnit = contact.addrUnit || contact.ADDR_UNIT || '';
  corporateForm.value.unit = addrUnit;
  if (addrUnit) {
    corporateAutoBoundFields.value.unit = true;
  }
  
  const foreignAddressLine1 = contact.foreignAddressLine1 || contact.FOREIGN_ADDRESS_LINE_1 || '';
  corporateForm.value.foreignAddressLine1 = foreignAddressLine1;
  if (foreignAddressLine1) {
    corporateAutoBoundFields.value.foreignAddressLine1 = true;
  }
  
  const foreignAddressLine2 = contact.foreignAddressLine2 || contact.FOREIGN_ADDRESS_LINE_2 || '';
  corporateForm.value.foreignAddressLine2 = foreignAddressLine2;
  if (foreignAddressLine2) {
    corporateAutoBoundFields.value.foreignAddressLine2 = true;
  }
  
  // Contact Address (대체 주소) 필드 업데이트
  const alternateAddressTypeCd = contact.alternateAddressTypeCd || contact.ALTERNATE_ADDRESS_CD || contact.addressTypeCd || contact.ADDRESS_TYPE_CD || '';
  corporateForm.value.alternateAddressType = alternateAddressTypeCd;
  
  const altPostalCode = contact.altPostalCode || contact.ALT_POSTAL_CODE || '';
  corporateForm.value.altPostalCode = altPostalCode;
  
  const altBlockNo = contact.altBlockNo || contact.ALT_BLOCK_NO || '';
  corporateForm.value.altBlockHouseNo = altBlockNo;
  
  const altStreetName = contact.altStreetName || contact.ALT_STREET_NAME || '';
  corporateForm.value.altStreetName = altStreetName;
  
  const altBuildingName = contact.altBuildingName || contact.ALT_BUILDING_NAME || '';
  corporateForm.value.altBuildingName = altBuildingName;
  
  const altAddrLevel = contact.altAddrLevel || contact.ALT_ADDR_LEVEL || '';
  corporateForm.value.altLevel = altAddrLevel;
  
  const altAddrUnit = contact.altAddrUnit || contact.ALT_ADDR_UNIT || '';
  corporateForm.value.altUnit = altAddrUnit;
  
  const altForeignAddressLine1 = contact.altForeignAddressLine1 || contact.ALT_FOREIGN_ADDRESS_LINE_1 || '';
  corporateForm.value.altForeignAddressLine1 = altForeignAddressLine1;
  
  const altForeignAddressLine2 = contact.altForeignAddressLine2 || contact.ALT_FOREIGN_ADDRESS_LINE_2 || '';
  corporateForm.value.altForeignAddressLine2 = altForeignAddressLine2;
  
  // E-Filing 모드일 때는 controllers[0]과 corporateForm 모두 업데이트
  if (props.eFilingMode && controllers.value.length > 0 && controllers.value[0].CATEGORY_CD === 'B09001') {
    const controller = controllers.value[0];
    controller.FULL_NAME = contact.contactName || contact.CONTACT_NAME || controller.FULL_NAME;
    controller.REG_NO = regNo;
    controller.LEGAL_FORM = legalForm;
    controller.JURISDICTION = jurisdiction;
    controller.INCORPORATION_STATUTE = incorporationStatute;
    controller.CORPORATE_ENTITY_REGISTER = corporateEntityRegister;
    controller.CONTACT_EMAIL = contactEmail;
    controller.COUNTRY_CD = countryCd;
    controller.AREA_CODE = areaCode;
    controller.MOBILE_NO = mobileNo;
    controller.ADDRESS_TYPE_CD = addressTypeCd;
    controller.POSTAL_CODE = postalCode;
    controller.BLOCK_NO = blockHouseNo;
    controller.STREET_NAME = streetName;
    controller.BUILDING_NAME = buildingName;
    controller.ADDR_LEVEL = addrLevel;
    controller.ADDR_UNIT = addrUnit;
    controller.FOREIGN_ADDRESS_LINE_1 = foreignAddressLine1;
    controller.FOREIGN_ADDRESS_LINE_2 = foreignAddressLine2;
    
    // corporateForm도 업데이트하여 getEFilingData()가 최신 정보를 반영하도록 함
    corporateForm.value.uen = regNo;
    corporateForm.value.legalForm = legalForm;
    corporateForm.value.jurisdiction = jurisdiction;
    corporateForm.value.incorporationStatute = incorporationStatute;
    corporateForm.value.corporateEntityRegister = corporateEntityRegister;
    corporateForm.value.emailAddress = contactEmail;
    corporateForm.value.countryCode = countryCd;
    corporateForm.value.areaCode = areaCode;
    corporateForm.value.contactNumber = mobileNo;
    corporateForm.value.addressType = addressTypeCd;
    corporateForm.value.postalCode = postalCode;
    corporateForm.value.blockHouseNo = blockHouseNo;
    corporateForm.value.streetName = streetName;
    corporateForm.value.buildingName = buildingName;
    corporateForm.value.level = addrLevel;
    corporateForm.value.unit = addrUnit;
    corporateForm.value.foreignAddressLine1 = foreignAddressLine1;
    corporateForm.value.foreignAddressLine2 = foreignAddressLine2;
    
    console.log('✅ [ControllerAppointmentForm] E-Filing 모드 법인 Controller 및 Form 업데이트 완료:', controller);
    
    // E-Filing 모드이고 taskId가 있으면 eFilingData 업데이트
    if (props.taskId) {
      try {
        const updatedEFilingData = await getEFilingData();
        await updateControllerAppointmentEFilingData(props.taskId, updatedEFilingData);
        console.log('✅ [ControllerAppointmentForm] E-Filing 데이터 업데이트 완료:', updatedEFilingData);
      } catch (error) {
        console.error('❌ [ControllerAppointmentForm] E-Filing 데이터 업데이트 실패:', error);
      }
    }
  }
  
  console.log('✅ 법인 Controller 자동 바인딩 즉시 반영 완료:', contact);
};

// 날짜 동기화
watch(dateOfBirthValue, (value) => {
  individualForm.value.dateOfBirth = value || '';
});

watch(() => dateOfBecomingField.dateValue.value, (value) => {
  individualForm.value.dateOfBecoming = value || '';
});

// 주소 조합 (표시용) - 공통 함수 사용
const formattedAddress = computed(() => {
  const form = individualForm.value;
  const addressInfo = {
    ADDRESS_TYPE_CD: form.addressType || '',
    BLOCK_NO: form.blockHouseNo || '',
    STREET_NAME: form.streetName || '',
    ADDR_LEVEL: form.level || '',
    ADDR_UNIT: form.unit || '',
    BUILDING_NAME: form.buildingName || '',
    POSTAL_CODE: form.postalCode || '',
    FOREIGN_ADDRESS_LINE_1: form.foreignAddressLine1 || '',
    FOREIGN_ADDRESS_LINE_2: form.foreignAddressLine2 || ''
  };
  const address = formatAddressUtil(addressInfo, { useComma: true });
  return typeof address === 'string' ? address : '';
});

// 개인 Validation 체크 함수 (deprecated - validateAll 사용)
const validateIndividualForm = (): boolean => {
  if (!showIndividualAddForm.value) {
    return true;
  }
  const isControllerNameValid = validateIndividualControllerName();
  if (!isControllerNameValid) return false;
  const isDateOfBecomingValid = validateIndividualDateOfBecoming();
  if (!isDateOfBecomingValid) return false;
  const isEmailAddressValid = validateIndividualEmailAddress();
  if (!isEmailAddressValid) return false;
  const isCountryCodeValid = validateIndividualCountryCode();
  if (!isCountryCodeValid) return false;
  const isContactNumberValid = validateIndividualContactNumber();
  if (!isContactNumberValid) return false;
  return true;
};

// 전체 필수 필드 검증 함수
const validateAll = async (): Promise<boolean> => {
  // 모든 validation 에러 초기화
  internalValidationErrors.value = {};
  
  // Vue 반응성 업데이트를 위해 nextTick 대기
  await nextTick();
  
  // E-Filing 모드일 때는 controllers 배열의 데이터를 individualForm/corporateForm에 동기화
  if (props.eFilingMode && controllers.value.length > 0) {
    const controller = controllers.value[0];
    if (controller.CATEGORY_CD === 'B09002') {
      // 개인 컨트롤러 - controllers 데이터를 individualForm에 동기화 (값이 없을 때만)
      if (!individualForm.value.controllerName && controller.CONTACT_CD) {
        individualForm.value.controllerName = controller.CONTACT_CD;
      }
      if (!individualForm.value.emailAddress && controller.CONTACT_EMAIL) {
        individualForm.value.emailAddress = controller.CONTACT_EMAIL;
      }
      if (!individualForm.value.countryCode && controller.COUNTRY_CD) {
        individualForm.value.countryCode = controller.COUNTRY_CD;
      }
      if (!individualForm.value.contactNumber && controller.MOBILE_NO) {
        individualForm.value.contactNumber = controller.MOBILE_NO;
      }
      if (!dateOfBecomingField.dateValue.value && controller.BECOMING_DATE) {
        dateOfBecomingField.dateValue.value = controller.BECOMING_DATE;
      }
    } else if (controller.CATEGORY_CD === 'B09001') {
      // 법인 컨트롤러 - controllers 데이터를 corporateForm에 동기화 (값이 없을 때만)
      if (!corporateForm.value.controllerName && controller.CONTACT_CD) {
        corporateForm.value.controllerName = controller.CONTACT_CD;
      }
      if (!corporateForm.value.emailAddress && controller.CONTACT_EMAIL) {
        corporateForm.value.emailAddress = controller.CONTACT_EMAIL;
      }
      if (!corporateForm.value.countryCode && controller.COUNTRY_CD) {
        corporateForm.value.countryCode = controller.COUNTRY_CD;
      }
      if (!corporateForm.value.contactNumber && controller.MOBILE_NO) {
        corporateForm.value.contactNumber = controller.MOBILE_NO;
      }
      if (!corporateDateOfBecomingField.dateValue.value && controller.BECOMING_DATE) {
        corporateDateOfBecomingField.dateValue.value = controller.BECOMING_DATE;
      }
    }
    // 동기화 후 Vue 반응성 업데이트
    await nextTick();
  }
  
  // 개인 컨트롤러가 있는지 확인
  const hasIndividualController = !!(individualForm.value.controllerName && individualForm.value.controllerName.trim()) ||
                                   (props.eFilingMode && controllers.value.some(c => c.CATEGORY_CD === 'B09002'));
  
  // 법인 컨트롤러가 있는지 확인
  const hasCorporateController = !!(corporateForm.value.controllerName && corporateForm.value.controllerName.trim()) ||
                                  (props.eFilingMode && controllers.value.some(c => c.CATEGORY_CD === 'B09001'));
  
  // 모든 필드를 체크하고 에러를 수집 (첫 번째 에러에서 멈추지 않음)
  let hasError = false;
  let firstErrorField: string | null = null;
  
  // Entity 검증 (가장 먼저)
  const isEntityValid = validateEntity();
  if (!isEntityValid) {
    hasError = true;
    firstErrorField = 'entity';
  }
  
  // 개인 폼이 활성화되어 있거나, E-Filing 모드에서 개인 컨트롤러가 있으면 개인 폼 검증
  if (showIndividualAddForm.value || (props.eFilingMode && hasIndividualController)) {
    const validations = [
      { name: 'individualControllerName', validate: validateIndividualControllerName },
      { name: 'individualDateOfBecoming', validate: validateIndividualDateOfBecoming },
      { name: 'individualEmailAddress', validate: validateIndividualEmailAddress },
      { name: 'individualCountryCode', validate: validateIndividualCountryCode },
      { name: 'individualContactNumber', validate: validateIndividualContactNumber }
    ];
    
    for (const { name, validate } of validations) {
      const isValid = validate();
      if (!isValid) {
        hasError = true;
        if (!firstErrorField) {
          firstErrorField = name;
        }
      }
    }
  }
  
  // 법인 폼이 활성화되어 있거나, E-Filing 모드에서 법인 컨트롤러가 있으면 법인 폼 검증
  if (showCorporateAddForm.value || (props.eFilingMode && hasCorporateController)) {
    const validations = [
      { name: 'corporateControllerName', validate: validateCorporateControllerName },
      { name: 'corporateDateOfBecoming', validate: validateCorporateDateOfBecoming },
      { name: 'corporateEmailAddress', validate: validateCorporateEmailAddress },
      { name: 'corporateCountryCode', validate: validateCorporateCountryCode },
      { name: 'corporateContactNumber', validate: validateCorporateContactNumber }
    ];
    
    for (const { name, validate } of validations) {
      const isValid = validate();
      if (!isValid) {
        hasError = true;
        if (!firstErrorField) {
          firstErrorField = name;
        }
      }
    }
  }
  
  // 모든 검증 완료 후 Vue 반응성 업데이트 (에러 메시지가 화면에 표시되도록)
  internalValidationErrors.value = { ...internalValidationErrors.value };
  await nextTick();
  await new Promise(resolve => setTimeout(resolve, 50));
  
  // 첫 번째 에러 필드로 포커스 이동
  if (hasError && firstErrorField) {
    const field = document.querySelector(`[data-field="${firstErrorField}"]`);
    if (field) {
      const inputElement = field.closest('.v-text-field, .v-autocomplete');
      if (inputElement) {
        (inputElement as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = (inputElement as HTMLElement).querySelector('input');
        if (input) {
          setTimeout(() => {
            input.focus();
          }, 100);
        }
      }
    }
  }
  
  // 모든 검증 통과 여부 반환
  return !hasError;
};

// Entity validation 함수
const validateEntity = (): boolean => {
  if (!entity.value || !entity.value.trim()) {
    internalValidationErrors.value.entity = t('startTask.This field is required') || 'This field is required';
    internalValidationErrors.value = { ...internalValidationErrors.value };
    return false;
  }
  if (internalValidationErrors.value.entity) {
    delete internalValidationErrors.value.entity;
  }
  return true;
};

// Entity error messages (computed)
const entityErrorMessages = computed(() => {
  if (internalValidationErrors.value.entity) {
    return [internalValidationErrors.value.entity];
  }
  return [];
});

// Validation error messages (computed)
const individualControllerNameErrorMessages = computed(() => {
  if (internalValidationErrors.value.individualControllerName) {
    return [internalValidationErrors.value.individualControllerName];
  }
  return [];
});

const individualDateOfBecomingErrorMessages = computed(() => {
  if (internalValidationErrors.value.individualDateOfBecoming) {
    return [internalValidationErrors.value.individualDateOfBecoming];
  }
  return [];
});

const individualEmailAddressErrorMessages = computed(() => {
  if (internalValidationErrors.value.individualEmailAddress) {
    return [internalValidationErrors.value.individualEmailAddress];
  }
  return [];
});

const individualCountryCodeErrorMessages = computed(() => {
  if (internalValidationErrors.value.individualCountryCode) {
    return [internalValidationErrors.value.individualCountryCode];
  }
  return [];
});

const individualContactNumberErrorMessages = computed(() => {
  if (internalValidationErrors.value.individualContactNumber) {
    return [internalValidationErrors.value.individualContactNumber];
  }
  return [];
});

const corporateControllerNameErrorMessages = computed(() => {
  if (internalValidationErrors.value.corporateControllerName) {
    return [internalValidationErrors.value.corporateControllerName];
  }
  return [];
});

const corporateDateOfBecomingErrorMessages = computed(() => {
  if (internalValidationErrors.value.corporateDateOfBecoming) {
    return [internalValidationErrors.value.corporateDateOfBecoming];
  }
  return [];
});

const corporateEmailAddressErrorMessages = computed(() => {
  if (internalValidationErrors.value.corporateEmailAddress) {
    return [internalValidationErrors.value.corporateEmailAddress];
  }
  return [];
});

const corporateCountryCodeErrorMessages = computed(() => {
  if (internalValidationErrors.value.corporateCountryCode) {
    return [internalValidationErrors.value.corporateCountryCode];
  }
  return [];
});

const corporateContactNumberErrorMessages = computed(() => {
  if (internalValidationErrors.value.corporateContactNumber) {
    return [internalValidationErrors.value.corporateContactNumber];
  }
  return [];
});

// 개인 필드 변경 시 validation 에러 제거
watch(() => individualForm.value.controllerName, () => {
  if (internalValidationErrors.value.individualControllerName) {
    delete internalValidationErrors.value.individualControllerName;
  }
});

watch(() => dateOfBecomingField.dateValue.value, () => {
  if (internalValidationErrors.value.individualDateOfBecoming) {
    delete internalValidationErrors.value.individualDateOfBecoming;
  }
});

watch(() => individualForm.value.emailAddress, () => {
  if (internalValidationErrors.value.individualEmailAddress) {
    delete internalValidationErrors.value.individualEmailAddress;
  }
});

watch(() => individualForm.value.countryCode, () => {
  if (internalValidationErrors.value.individualCountryCode) {
    delete internalValidationErrors.value.individualCountryCode;
  }
});

watch(() => individualForm.value.contactNumber, () => {
  if (internalValidationErrors.value.individualContactNumber) {
    delete internalValidationErrors.value.individualContactNumber;
  }
});

// 법인 필드 변경 시 validation 에러 제거
watch(() => corporateForm.value.controllerName, () => {
  if (internalValidationErrors.value.corporateControllerName) {
    delete internalValidationErrors.value.corporateControllerName;
  }
});

watch(() => corporateDateOfBecomingField.dateValue.value, () => {
  if (internalValidationErrors.value.corporateDateOfBecoming) {
    delete internalValidationErrors.value.corporateDateOfBecoming;
  }
});

watch(() => corporateForm.value.emailAddress, () => {
  if (internalValidationErrors.value.corporateEmailAddress) {
    delete internalValidationErrors.value.corporateEmailAddress;
  }
});

watch(() => corporateForm.value.countryCode, () => {
  if (internalValidationErrors.value.corporateCountryCode) {
    delete internalValidationErrors.value.corporateCountryCode;
  }
});

watch(() => corporateForm.value.contactNumber, () => {
  if (internalValidationErrors.value.corporateContactNumber) {
    delete internalValidationErrors.value.corporateContactNumber;
  }
});

// 최종 저장 시 새로 추가한 컨트롤러가 있는지 검증
const validateControllerAdded = (): boolean => {
  // 에러 초기화
  if (internalValidationErrors.value.controllerRequired) {
    delete internalValidationErrors.value.controllerRequired;
  }
  
  // 개인 컨트롤러 추가 폼이 활성화되어 있고 필수 필드가 입력되어 있으면 새로 추가한 것으로 간주
  if (showIndividualAddForm.value) {
    const hasControllerName = individualForm.value.controllerName && individualForm.value.controllerName.trim();
    const hasDateOfBecoming = dateOfBecomingField.dateValue.value && dateOfBecomingField.dateValue.value.trim();
    const hasEmail = individualForm.value.emailAddress && individualForm.value.emailAddress.trim();
    const hasCountryCode = individualForm.value.countryCode && individualForm.value.countryCode.trim();
    const hasContactNumber = individualForm.value.contactNumber && individualForm.value.contactNumber.trim();
    
    // 필수 필드가 모두 입력되어 있으면 새로 추가한 것으로 간주
    if (hasControllerName && hasDateOfBecoming && hasEmail && hasCountryCode && hasContactNumber) {
      return true;
    }
  }
  
  // 법인 컨트롤러 추가 폼이 활성화되어 있고 필수 필드가 입력되어 있으면 새로 추가한 것으로 간주
  if (showCorporateAddForm.value) {
    const hasControllerName = corporateForm.value.controllerName && corporateForm.value.controllerName.trim();
    const hasDateOfBecoming = corporateDateOfBecomingField.dateValue.value && corporateDateOfBecomingField.dateValue.value.trim();
    const hasEmail = corporateForm.value.emailAddress && corporateForm.value.emailAddress.trim();
    const hasCountryCode = corporateForm.value.countryCode && corporateForm.value.countryCode.trim();
    const hasContactNumber = corporateForm.value.contactNumber && corporateForm.value.contactNumber.trim();
    
    // 필수 필드가 모두 입력되어 있으면 새로 추가한 것으로 간주
    if (hasControllerName && hasDateOfBecoming && hasEmail && hasCountryCode && hasContactNumber) {
      return true;
    }
  }
  
  // E-Filing 모드에서는 controllers 배열에 데이터가 있으면 통과
  if (props.eFilingMode && controllers.value.length > 0) {
    return true;
  }
  
  // 추가 폼이 없거나 필수 필드가 입력되지 않았으면 새로 추가한 것이 없음
  // 에러 메시지는 표시하지 않음 (각 필드에 에러 메시지 표시)
  return false;
};

// E-Filing 데이터 생성 (Controller Appointment용) - saveController 기반
const getEFilingData = async () => {
  // 세션에서 Client 정보 가져오기
  const customerInfo = sessionStore.getCustomerInfo();
  const filingAgentUEN = customerInfo?.regNo || '';

  // Entity 정보
  const selectedEntity = officerAppointmentStore.entities.find((ent: any) => 
    (ent.entityCd || ent.ENTITY_CD) === entity.value
  );
  const entityCompanyType = selectedEntity?.companyType || selectedEntity?.COMPANY_TYPE || '';

  // Controller 정보 (개인 또는 법인)
  const isCorporate = !!corporateForm.value.controllerName;
  const controllerNameCd = isCorporate 
    ? corporateForm.value.controllerName 
    : individualForm.value.controllerName;

  // Contact Name 가져오기 (현재 화면의 데이터 우선 사용)
  let contactName = '';
  if (isCorporate) {
    // 법인 컨트롤러 - 화면의 이름 사용
    contactName = corporateControllerNameDisplay.value || '';
  } else {
    // 개인 컨트롤러 - 화면의 이름 사용
    contactName = individualControllerNameDisplay.value || '';
  }
  
  // 화면에 이름이 없으면 DB에서 조회 (fallback)
  if (!contactName && controllerNameCd) {
    try {
      const response = await contactsApi.getContactById(controllerNameCd);
      const contactData = (response as any).contact || response.data?.contact || {};
      contactName = contactData.contactName || contactData.CONTACT_NAME || '';
    } catch (error) {
      console.error('Failed to load contact:', error);
    }
  }

  // Entity Type 코드명 (A04) - BIGO1에서 E-Filing용 name 가져오기 - SG 국가코드 사용
  let companyTypeName = '';
  if (entityCompanyType) {
    try {
      const response = await baseCodeApi.getCodeByTotalCode('SG', entityCompanyType);
      companyTypeName = (response as any).code?.bigo1 || (response as any).data?.code?.bigo1 || '';
    } catch (e) { console.warn('Entity Type BIGO1 조회 실패:', e); }
  }

  // Officer Category 코드명 (B09) - BIGO3에서 E-Filing용 name 가져오기
  const categoryCd = isCorporate ? 'B09001' : 'B09002';
  let contactCategoryName = '';
  try {
    const response = await baseCodeApi.getCodeByTotalCode('CM', categoryCd);
    contactCategoryName = (response as any).code?.bigo3 || (response as any).data?.code?.bigo3 || '';
  } catch (e) { console.warn('Officer Category BIGO3 조회 실패:', e); }

  // saveController 생성 (백엔드와 동일한 로직)
  // 개인/법인 구분하여 처리
  let saveController: any = {};
  
  if (isCorporate) {
    // 법인 컨트롤러
    const selectedCountryOption = countryOptions.value.find((opt: any) => opt.value === corporateForm.value.countryCode);
    const countryBigo3 = (selectedCountryOption as any)?.bigo3 || '';
    const mobileNoCountryCode = countryBigo3 ? (countryBigo3.startsWith('+') ? countryBigo3.substring(1) : countryBigo3) : '';
    
    const selectedAddressTypeOption = addressTypeOptions.value.find((opt: any) => opt.value === corporateForm.value.addressType);
    const addressTypeBigo1Val = (selectedAddressTypeOption as any)?.bigo1 || '';
    
    const jurisdictionCd = corporateForm.value.jurisdiction || '';
    let incorpCountryEnum = '';
    if (jurisdictionCd) {
      try {
        const response = await baseCodeApi.getCodeByTotalCode('CM', jurisdictionCd);
        incorpCountryEnum = (response as any).code?.bigo1 || (response as any).data?.code?.bigo1 || '';
      } catch (e) { console.warn('IncorpCountry BIGO1 조회 실패:', e); }
    }
    
    // 주소 타입에 따라 해당하지 않는 필드는 빈 문자열로 저장
    const addressTypeCd = corporateForm.value.addressType || '';
    const isLocalAddress = addressTypeCd === 'B07001';
    const isForeignAddress = addressTypeCd === 'B07002';
    
    saveController = {
      rcCategory: 'CORP',
      registrableControllerId: '',
      idType: '', // 법인은 idType 없음
      registrationIdNo: corporateForm.value.uen || '',
      nationality: '', // 법인은 nationality 없음
      incorpCountry: incorpCountryEnum,
      incorpCountryRegistrarName: corporateForm.value.corporateEntityRegister || '',
      legalForm: corporateForm.value.legalForm || '',
      statute: corporateForm.value.incorporationStatute || '',
      birthDate: '', // 법인은 birthDate 없음
      name: contactName,
      nameAlias: '',
      email: corporateForm.value.emailAddress || '',
      mobileNoCountryCode,
      mobileNoAreaCode: corporateForm.value.areaCode || '',
      mobileNo: corporateForm.value.contactNumber || '',
      addressType: addressTypeBigo1Val,
      postalCode: isLocalAddress ? (corporateForm.value.postalCode || '') : '',
      blkhseNo: isLocalAddress ? (corporateForm.value.blockHouseNo || '') : '',
      streetName: isLocalAddress ? (corporateForm.value.streetName || '') : '',
      buildingName: isLocalAddress ? (corporateForm.value.buildingName || '') : '',
      levelNo: isLocalAddress ? (corporateForm.value.level || '') : '',
      unitNo: isLocalAddress ? (corporateForm.value.unit || '') : '',
      foreignAddr1: isForeignAddress ? (corporateForm.value.foreignAddressLine1 || '') : '',
      foreignAddr2: isForeignAddress ? (corporateForm.value.foreignAddressLine2 || '') : '',
      entryDate: corporateDateOfBecomingField.dateValue.value || '',
      ceasedDate: '',
      ceasedReason: '',
      confirmationFlag: corporateForm.value.confirmationByController === 'Yes',
      appointmentStatus: '',
      updatedFlag: true
    };
  } else {
    // 개인 컨트롤러 - 기존 getSaveController() 로직 사용
    const selectedNationalityOption = nationalityOptions.value.find((opt: any) => opt.value === individualForm.value.nationality);
    const nationalityBigo1Val = (selectedNationalityOption as any)?.bigo1 || '';
    
    const selectedAddressTypeOption = addressTypeOptions.value.find((opt: any) => opt.value === individualForm.value.addressType);
    const addressTypeBigo1Val = (selectedAddressTypeOption as any)?.bigo1 || '';
    
    const selectedCountryOption = countryOptions.value.find((opt: any) => opt.value === individualForm.value.countryCode);
    const countryBigo3 = (selectedCountryOption as any)?.bigo3 || '';
    const mobileNoCountryCode = countryBigo3 ? (countryBigo3.startsWith('+') ? countryBigo3.substring(1) : countryBigo3) : '';
    
    const dateOfBirthVal = individualForm.value.dateOfBirth || '';
    const formatDateOfBirth = (dateStr: string): string => {
      if (!dateStr) return '';
      try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      } catch {
        return '';
      }
    };
    
    // 주소 타입에 따라 해당하지 않는 필드는 빈 문자열로 저장
    const addressTypeCd = individualForm.value.addressType || '';
    const isLocalAddress = addressTypeCd === 'B07001';
    const isForeignAddress = addressTypeCd === 'B07002';
    
    saveController = {
      rcCategory: 'IND',
      registrableControllerId: '',
      idType: '', // saveController에는 idType이 빈 문자열
      registrationIdNo: individualForm.value.identificationNumber || '',
      nationality: nationalityBigo1Val,
      incorpCountry: '',
      incorpCountryRegistrarName: '',
      legalForm: '',
      statute: '',
      birthDate: formatDateOfBirth(dateOfBirthVal),
      name: contactName,
      nameAlias: individualForm.value.aliases || '',
      email: individualForm.value.emailAddress || '',
      mobileNoCountryCode,
      mobileNoAreaCode: individualForm.value.areaCode || '',
      mobileNo: individualForm.value.contactNumber || '',
      addressType: addressTypeBigo1Val,
      postalCode: isLocalAddress ? (individualForm.value.postalCode || '') : '',
      blkhseNo: isLocalAddress ? (individualForm.value.blockHouseNo || '') : '',
      streetName: isLocalAddress ? (individualForm.value.streetName || '') : '',
      buildingName: isLocalAddress ? (individualForm.value.buildingName || '') : '',
      levelNo: isLocalAddress ? (individualForm.value.level || '') : '',
      unitNo: isLocalAddress ? (individualForm.value.unit || '') : '',
      foreignAddr1: isForeignAddress ? (individualForm.value.foreignAddressLine1 || '') : '',
      foreignAddr2: isForeignAddress ? (individualForm.value.foreignAddressLine2 || '') : '',
      entryDate: dateOfBecomingField.dateValue.value || '',
      ceasedDate: '',
      ceasedReason: '',
      confirmationFlag: individualForm.value.confirmationByController === 'Yes',
      appointmentStatus: '',
      updatedFlag: true
    };
  }

  // ID Type 조회 (공통코드의 BIGO1 값 사용) - 현재 화면의 데이터 우선 사용
  let idTypeEnum = '';
  if (!isCorporate) {
    // 개인 컨트롤러 - 화면의 identificationType 사용
    const idTypeCd = individualForm.value.identificationType || '';
      
      if (idTypeCd) {
        // 먼저 options에서 bigo1 찾기 (이미 로드된 데이터 사용)
        const idTypeItem = identificationTypeOptions.value.find(opt => opt.value === idTypeCd);
        if (idTypeItem && (idTypeItem as any).bigo1) {
          idTypeEnum = (idTypeItem as any).bigo1;
        } else {
          // options에 없으면 API로 조회
          try {
            const idTypeCodeData = await baseCodeApi.getCodeByTotalCode('SG', idTypeCd);
            // 응답 구조 확인: code.bigo1 또는 data.code.bigo1
            idTypeEnum = (idTypeCodeData as any).code?.bigo1 || (idTypeCodeData as any).code?.BIGO1 || 
                        (idTypeCodeData as any).data?.code?.bigo1 || (idTypeCodeData as any).data?.code?.BIGO1 || '';
            
            // 여전히 없으면 fallback
            if (!idTypeEnum) {
              idTypeEnum = idTypeItem?.title || '';
            }
          } catch (e) { 
            console.warn('ID Type BIGO1 조회 실패:', e);
            // 최종 fallback
            idTypeEnum = idTypeItem?.title || '';
          }
      }
    } else {
      // 화면에 idType이 없으면 DB에서 조회 (fallback)
      if (controllerNameCd) {
        try {
          const response = await contactsApi.getContactById(controllerNameCd);
          const contactData = (response as any).contact || response.data?.contact || {};
          const fallbackIdTypeCd = contactData.idTypeCd || contactData.ID_TYPE_CD || '';
          
          if (fallbackIdTypeCd) {
            const idTypeItem = identificationTypeOptions.value.find(opt => opt.value === fallbackIdTypeCd);
            if (idTypeItem && (idTypeItem as any).bigo1) {
              idTypeEnum = (idTypeItem as any).bigo1;
        }
      }
    } catch (e) { 
      console.warn('Contact 조회 실패:', e);
        }
      }
    }
  }

  // saveController에 idType 추가
  if (!isCorporate) {
    if (idTypeEnum) {
      saveController.idType = idTypeEnum;
    } else {
      // idType이 없으면 빈 문자열로 설정 (백엔드에서 검증)
      saveController.idType = '';
      console.warn('⚠️ [ControllerAppointmentForm] idType이 없습니다. 백엔드에서 검증됩니다.');
    }
  }

  const entityUENFromStore = selectedEntity?.regNo || selectedEntity?.REG_NO || '';
  const fd = props.initialData?.formData || {};
  const entityUENFromInitial = String(
    props.initialData?.entityUEN
    ?? props.initialData?.eFilingData?.EntityUEN
    ?? fd.entity_uen
    ?? fd.entityUEN
    ?? fd.EntityUEN
    ?? fd.company_registration_no
    ?? fd.companyRegistrationNo
    ?? (typeof fd.entityRegNo === 'object' && fd.entityRegNo?.value != null
      ? fd.entityRegNo.value
      : fd.entityRegNo)
    ?? '',
  ).trim();
  const entityUEN = entityUENFromStore || entityUENFromInitial;

  const entityNameStr = String(
    selectedEntity?.companyName
    ?? selectedEntity?.COMPANY_NAME
    ?? props.initialData?.entityName
    ?? props.initialData?.formData?.entityName?.value
    ?? props.initialData?.formData?.company_name
    ?? '',
  ).trim();

  // E-Filing JSON 구조 (AddControllerList 배열, WithdrawControllerList 배열, EntityUEN/FilingAgentUEN)
  const eFilingData = {
    AddControllerList: [saveController],
    WithdrawControllerList: [],
    EntityUEN: entityUEN,
    FilingAgentUEN: filingAgentUEN,
    entityName: entityNameStr,
  };

  console.log('📋 [ControllerAppointmentForm] getEFilingData (AddControllerList 기반):', eFilingData);
  console.log('📋 [ControllerAppointmentForm] saveController 필수 필드 확인:', {
    rcCategory: saveController.rcCategory,
    name: saveController.name,
    registrationIdNo: saveController.registrationIdNo,
    idType: saveController.idType,
    nationality: saveController.nationality,
    birthDate: saveController.birthDate,
    addressType: saveController.addressType,
    entryDate: saveController.entryDate,
    incorpCountry: saveController.incorpCountry
  });
  return eFilingData;
};

// Contact 정보 전체 반환 함수 (저장 시 사용) - 개인/법인 컨트롤러 모두 지원
const getContactData = () => {
  // 법인 컨트롤러인지 확인
  const isCorporate = !!corporateForm.value.controllerName;
  
  if (isCorporate) {
    // 법인 컨트롤러 데이터
    const selectedCountryOption = countryOptions.value.find((opt: any) => opt.value === corporateForm.value.countryCode);
    const countryCodeName = selectedCountryOption?.title || '';
    const countryBigo3 = (selectedCountryOption as any)?.bigo3 || '';
    const countryCodeNumber = countryBigo3 ? (countryBigo3.startsWith('+') ? countryBigo3.substring(1) : countryBigo3) : '';
    const countryText = countryCodeName && countryCodeNumber ? `${countryCodeName} +${countryCodeNumber}` : countryCodeName;
    
    const selectedAddressTypeOption = addressTypeOptions.value.find((opt: any) => opt.value === corporateForm.value.addressType);
    const addressTypeText = selectedAddressTypeOption?.title || '';
    
    // 주소 타입에 따라 해당하지 않는 필드는 빈 문자열로 저장
    const addressTypeCd = corporateForm.value.addressType || '';
    const isLocalAddress = addressTypeCd === 'B07001';
    const isForeignAddress = addressTypeCd === 'B07002';
    
    const alternateAddressCd = corporateForm.value.alternateAddressType || corporateForm.value.addressType || '';
    const isAltLocalAddress = alternateAddressCd === 'B07001';
    const isAltForeignAddress = alternateAddressCd === 'B07002';
    
    return {
      // Basic 섹션
      contactCd: corporateForm.value.controllerName, // CONTACT_CD (GUID)
      contactName: corporateControllerNameDisplay.value,
      // 법인은 개인 정보 없음
      firstName: '',
      middleName: '',
      surname: '',
      idTypeCd: '',
      idTypeText: '',
      contactRegNo: corporateForm.value.uen || '', // 법인의 경우 UEN
      nationalityCd: '',
      nationalityText: '',
      dateOfBirth: '',
      categoryCd: 'B09001', // 법인
      // Residential Address 섹션
      addressTypeCd: addressTypeCd,
      addressTypeText: addressTypeText,
      postalCode: isLocalAddress ? (corporateForm.value.postalCode || '') : '',
      blockNo: isLocalAddress ? (corporateForm.value.blockHouseNo || '') : '',
      streetName: isLocalAddress ? (corporateForm.value.streetName || '') : '',
      buildingName: isLocalAddress ? (corporateForm.value.buildingName || '') : '',
      addrLevel: isLocalAddress ? (corporateForm.value.level || '') : '',
      addrUnit: isLocalAddress ? (corporateForm.value.unit || '') : '',
      foreignAddressLine1: isForeignAddress ? (corporateForm.value.foreignAddressLine1 || '') : '',
      foreignAddressLine2: isForeignAddress ? (corporateForm.value.foreignAddressLine2 || '') : '',
      // Contact Address 섹션 (대체 주소)
      alternateAddressCd: alternateAddressCd,
      altPostalCode: isAltLocalAddress ? (corporateForm.value.altPostalCode || '') : '',
      altBlockNo: isAltLocalAddress ? (corporateForm.value.altBlockHouseNo || '') : '',
      altStreetName: isAltLocalAddress ? (corporateForm.value.altStreetName || '') : '',
      altBuildingName: isAltLocalAddress ? (corporateForm.value.altBuildingName || '') : '',
      altAddrLevel: isAltLocalAddress ? (corporateForm.value.altLevel || '') : '',
      altAddrUnit: isAltLocalAddress ? (corporateForm.value.altUnit || '') : '',
      altForeignAddressLine1: isAltForeignAddress ? (corporateForm.value.altForeignAddressLine1 || '') : '',
      altForeignAddressLine2: isAltForeignAddress ? (corporateForm.value.altForeignAddressLine2 || '') : '',
      // Contact 섹션
      countryCd: corporateForm.value.countryCode,
      countryText: countryText,
      areaCode: corporateForm.value.areaCode,
      mobileNo: corporateForm.value.contactNumber,
      contactEmail: corporateForm.value.emailAddress,
      // 법인 특화 정보
      uen: corporateForm.value.uen,
      legalForm: corporateForm.value.legalForm,
      jurisdiction: corporateForm.value.jurisdiction,
      incorporationStatute: corporateForm.value.incorporationStatute,
      corporateEntityRegister: corporateForm.value.corporateEntityRegister,
      // Controller details
      dateOfBecoming: corporateDateOfBecomingField.dateValue.value || corporateForm.value.dateOfBecoming || '',
      confirmationByController: corporateForm.value.confirmationByController || 'No'
    };
  } else {
    // 개인 컨트롤러 데이터
    const selectedIdentificationTypeOption = identificationTypeOptions.value.find((opt: any) => opt.value === individualForm.value.identificationType);
    const idTypeText = selectedIdentificationTypeOption?.title || '';
    
    const selectedNationalityOption = nationalityOptions.value.find((opt: any) => opt.value === individualForm.value.nationality);
    const nationalityText = selectedNationalityOption?.title || '';
    
    const selectedAddressTypeOption = addressTypeOptions.value.find((opt: any) => opt.value === individualForm.value.addressType);
    const addressTypeText = selectedAddressTypeOption?.title || '';
    
    const selectedCountryOption = countryOptions.value.find((opt: any) => opt.value === individualForm.value.countryCode);
    const countryCodeName = selectedCountryOption?.title || '';
    const countryBigo3 = (selectedCountryOption as any)?.bigo3 || '';
    const countryCodeNumber = countryBigo3 ? (countryBigo3.startsWith('+') ? countryBigo3.substring(1) : countryBigo3) : '';
    const countryText = countryCodeName && countryCodeNumber ? `${countryCodeName} +${countryCodeNumber}` : countryCodeName;
    
    // 주소 타입에 따라 해당하지 않는 필드는 빈 문자열로 저장
    const addressTypeCd = individualForm.value.addressType || '';
    const isLocalAddress = addressTypeCd === 'B07001';
    const isForeignAddress = addressTypeCd === 'B07002';
    
    const alternateAddressCd = individualForm.value.alternateAddressType || individualForm.value.addressType || '';
    const isAltLocalAddress = alternateAddressCd === 'B07001';
    const isAltForeignAddress = alternateAddressCd === 'B07002';
    
    return {
      // Basic 섹션
      contactCd: individualForm.value.controllerName, // CONTACT_CD (GUID)
      contactName: individualControllerNameDisplay.value,
      firstName: '', // 개인 컨트롤러는 이름이 전체 이름으로만 저장됨
      middleName: '',
      surname: '',
      idTypeCd: individualForm.value.identificationType,
      idTypeText: idTypeText,
      contactRegNo: individualForm.value.identificationNumber,
      nationalityCd: individualForm.value.nationality,
      nationalityText: nationalityText,
      dateOfBirth: individualForm.value.dateOfBirth,
      categoryCd: 'B09002', // 개인
      // Residential Address 섹션
      addressTypeCd: addressTypeCd,
      addressTypeText: addressTypeText,
      postalCode: isLocalAddress ? (individualForm.value.postalCode || '') : '',
      blockNo: isLocalAddress ? (individualForm.value.blockHouseNo || '') : '',
      streetName: isLocalAddress ? (individualForm.value.streetName || '') : '',
      buildingName: isLocalAddress ? (individualForm.value.buildingName || '') : '',
      addrLevel: isLocalAddress ? (individualForm.value.level || '') : '',
      addrUnit: isLocalAddress ? (individualForm.value.unit || '') : '',
      foreignAddressLine1: isForeignAddress ? (individualForm.value.foreignAddressLine1 || '') : '',
      foreignAddressLine2: isForeignAddress ? (individualForm.value.foreignAddressLine2 || '') : '',
      // Contact Address 섹션 (대체 주소)
      alternateAddressCd: alternateAddressCd,
      altPostalCode: isAltLocalAddress ? (individualForm.value.altPostalCode || '') : '',
      altBlockNo: isAltLocalAddress ? (individualForm.value.altBlockHouseNo || '') : '',
      altStreetName: isAltLocalAddress ? (individualForm.value.altStreetName || '') : '',
      altBuildingName: isAltLocalAddress ? (individualForm.value.altBuildingName || '') : '',
      altAddrLevel: isAltLocalAddress ? (individualForm.value.altLevel || '') : '',
      altAddrUnit: isAltLocalAddress ? (individualForm.value.altUnit || '') : '',
      altForeignAddressLine1: isAltForeignAddress ? (individualForm.value.altForeignAddressLine1 || '') : '',
      altForeignAddressLine2: isAltForeignAddress ? (individualForm.value.altForeignAddressLine2 || '') : '',
      // Contact 섹션
      countryCd: individualForm.value.countryCode,
      countryText: countryText,
      areaCode: individualForm.value.areaCode,
      mobileNo: individualForm.value.contactNumber,
      contactEmail: individualForm.value.emailAddress,
      // 개인 특화 정보
      aliases: individualForm.value.aliases,
      // Controller details
      dateOfBecoming: dateOfBecomingField.dateValue.value || individualForm.value.dateOfBecoming || '',
      confirmationByController: individualForm.value.confirmationByController || 'No'
    };
  }
};

// 외부에서 접근 가능하도록 expose
defineExpose({
  entity,
  getFormData,
  getFormDataWithDocVariables,
  getSaveController,
  getContactData, // Contact 데이터 반환
  getEFilingData, // E-Filing 데이터 생성
  validateIndividualForm, // 최종 Save 시 validation 체크용 (deprecated - validateAll 사용)
  validateCorporateForm, // 법인 컨트롤러 validation 체크용 (deprecated - validateAll 사용)
  validateAll, // 전체 필수 필드 검증 함수
  validateControllerAdded, // 컨트롤러 추가 여부 검증
  setFormData: setFormDataFromInitialData, // E-Filing 페이지에서 TASK_DATA 설정용 (OfficerAppointmentForm과 동일한 인터페이스)
  // 개인 validation 함수들
  validateIndividualControllerName,
  validateIndividualDateOfBecoming,
  validateIndividualEmailAddress,
  validateIndividualCountryCode,
  validateIndividualContactNumber,
  // 법인 validation 함수들
  validateCorporateControllerName,
  validateCorporateDateOfBecoming,
  validateCorporateEmailAddress,
  validateCorporateCountryCode,
  validateCorporateContactNumber
});

// Save 핸들러
const handleSaveIndividualController = async () => {
  // Validation 체크
  if (!validateIndividualForm()) {
    console.log('Validation failed:', internalValidationErrors.value);
    return;
  }

  // TODO: 실제 저장 로직 구현
  console.log('Save individual controller:', individualForm.value);
  
  // 저장 후 폼 닫기 및 초기화
  showIndividualAddForm.value = false;
  resetIndividualForm();
  
  // Controller 목록 새로고침
  if (entity.value) {
    await loadControllers(entity.value);
  }
};

// taskConfig가 변경될 때 Task Flow 업데이트
watch(() => props.taskConfig, (newConfig) => {
  if (newConfig) {
    updateTaskFlow();
  }
}, { immediate: true });

onMounted(async () => {
  try {
    await Promise.all([
      officerAppointmentStore.fetchEntities(),
      loadIdTypeCodeNames(),
      loadNationalityCodeNames(),
      loadJurisdictionCodeNames(),
      loadIndividualContacts(),
      loadCorporateContacts(),
      loadOptions()
    ]);
    
    // 초기 Task Flow 설정
    updateTaskFlow();
  } catch (error) {
    console.error('Failed to fetch initial data:', error);
  }
});
</script>

<template>
  <v-card
    variant="outlined"
    class="mb-6"
    style="border: 1px solid #e0e0e0"
  >
    <v-card-title class="text-h6 pa-4 pb-2">
      {{ t('startTask.Appoint Registrable Controller') || 'Appoint Registrable Controller' }}
    </v-card-title>
    <v-card-text class="pa-6 pt-2">
      <!-- Entity Field -->
      <div class="mb-6" style="max-width: 70%;">
        <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
          {{ t('startTask.Entity') || 'Entity' }} <span class="text-error">*</span>
        </label>
        <v-autocomplete
          v-model="entity"
          :items="officerAppointmentStore.entityOptions"
          :placeholder="t('startTask.Search') || 'Search'"
          variant="outlined"
          density="compact"
          :hide-details="entityErrorMessages.length === 0"
          append-inner-icon="mdi-magnify"
          clearable
          :disabled="eFilingMode"
          data-field="entity"
          :error-messages="entityErrorMessages"
        ></v-autocomplete>
      </div>

      <!-- 일반 모드: Controller 목록 섹션 -->
      <div v-if="entity && !eFilingMode" class="mb-6">
        <!-- 개인 Registrable Controllers -->
        <div class="mb-6">
          <div v-if="individualControllers.length > 0" class="d-flex align-center justify-space-between mb-3">
            <div class="d-flex align-center">
              <v-avatar size="24" color="brown" class="mr-2">
                <span class="text-white text-caption">{{ individualControllers.length }}</span>
              </v-avatar>
              <h3 class="text-h6 ma-0">
                {{ t('startTask.Individual registrable controllers') || 'Individual registrable controllers' }}
              </h3>
            </div>
            <div class="d-flex" style="gap: 8px;">
              <a href="#" @click.prevent="expandAllIndividual" class="text-caption text-primary text-decoration-none">
                {{ t('startTask.Expand all') || 'Expand all' }}
              </a>
              <span class="text-caption">|</span>
              <a href="#" @click.prevent="collapseAllIndividual" class="text-caption text-primary text-decoration-none">
                {{ t('startTask.Collapse all') || 'Collapse all' }}
              </a>
            </div>
          </div>

          <v-expansion-panels
            v-if="individualControllers.length > 0"
            v-model="individualExpandedPanels"
            multiple
            variant="accordion"
          >
            <v-expansion-panel
              v-for="(controller, index) in individualControllers"
              :key="controller.REGISTRABLE_CONTROLLER_ID"
              :value="controller.REGISTRABLE_CONTROLLER_ID"
            >
              <v-expansion-panel-title class="controller-accordion-header">
                <div class="d-flex align-center flex-wrap">
                  <span class="font-weight-bold text-white mr-2">{{ index + 1 }}. {{ controller.FULL_NAME }}</span>
                  <span class="text-white text-caption" v-if="formatIndividualControllerInfo(controller)">{{ formatIndividualControllerInfo(controller) }}</span>
                </div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <div class="pa-4">
                  <!-- Personal particulars -->
                  <div class="mb-4">
                    <h4 class="section-title">{{ t('startTask.Personal particulars') || 'Personal particulars' }}</h4>
                    <div class="field-grid">
                      <div class="field-item">
                        <span class="field-label">{{ t('startTask.Identification type') || 'Identification type' }}:</span>
                        <span class="field-value">{{ getIdTypeCodeName(controller.ID_TYPE || '') || '-' }}</span>
                      </div>
                      <div class="field-item">
                        <span class="field-label">{{ t('startTask.Identification number') || 'Identification number' }}:</span>
                        <span class="field-value">{{ controller.CONTACT_REG_NO || '-' }}</span>
                      </div>
                      <div class="field-item">
                        <span class="field-label">{{ t('startTask.Name') || 'Name' }}:</span>
                        <span class="field-value">{{ controller.FULL_NAME || '-' }}</span>
                      </div>
                      <div class="field-item">
                        <span class="field-label">{{ t('startTask.Nationality') || 'Nationality' }}:</span>
                        <span class="field-value">{{ getNationalityCodeName(controller.NATIONALITY_CD || '') || controller.NATIONALITY_SCRAPING || '-' }}</span>
                      </div>
                      <div class="field-item">
                        <span class="field-label">{{ t('startTask.Date of birth') || 'Date of birth' }}:</span>
                        <span class="field-value">{{ formatDateLong(controller.DATE_OF_BIRTH) || '-' }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Contact information -->
                  <div class="mb-4">
                    <h4 class="section-title">{{ t('startTask.Contact information') || 'Contact information' }}</h4>
                    <div class="field-grid">
                      <div class="field-item">
                        <span class="field-label">{{ t('startTask.Email address') || 'Email address' }}:</span>
                        <span class="field-value">{{ controller.CONTACT_EMAIL || '-' }}</span>
                      </div>
                      <div class="field-item">
                        <span class="field-label">{{ t('startTask.Contact number') || 'Contact number' }}:</span>
                        <span class="field-value">{{ formatContactNumber(controller) || '-' }}</span>
                      </div>
                      <div class="field-item field-item-full">
                        <span class="field-label">{{ t('startTask.Residential address') || 'Residential address' }}:</span>
                        <div class="field-value address-lines">
                          <template v-if="formatAddressLines(controller).length > 0">
                            <div v-for="(line, idx) in formatAddressLines(controller)" :key="idx" class="address-line">
                              {{ line }}
                            </div>
                          </template>
                          <span v-else>-</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Controller details -->
                  <div>
                    <h4 class="section-title">{{ t('startTask.Controller details') || 'Controller details' }}</h4>
                    <div class="field-grid">
                      <div class="field-item">
                        <span class="field-label">{{ t('startTask.Date of becoming a controller') || 'Date of becoming a controller' }}:</span>
                        <span class="field-value">{{ formatDateLong(controller.BECOMING_DATE) || '-' }}</span>
                      </div>
                      <div class="field-item">
                        <span class="field-label">{{ t('startTask.Date of cessation as a controller') || 'Date of cessation as a controller' }}:</span>
                        <span class="field-value">{{ formatDateLong(controller.CESSATION_DATE) || '-' }}</span>
                      </div>
                      <div class="field-item">
                        <span class="field-label">{{ t('startTask.Confirmation by controller') || 'Confirmation by controller' }}:</span>
                        <span class="field-value">{{ controller.CONFIRMATION_CONTROLLER ? (t('startTask.Yes') || 'Yes') : (t('startTask.No') || 'No') }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>

          <v-btn
            v-if="!showIndividualAddForm && !showCorporateAddForm && !eFilingMode"
            variant="outlined"
            color="grey"
            size="small"
            class="mt-3"
            @click="handleIndividualAddFormClick"
          >
            <v-icon start>mdi-plus</v-icon>
            {{ t('startTask.Appoint another Registrable Controller') || '+ Appoint another Registrable Controller' }}
          </v-btn>
        </div>

        <!-- 법인 Registrable Controllers -->
        <div class="mb-6">
          <div v-if="corporateControllers.length > 0" class="d-flex align-center justify-space-between mb-3">
            <div class="d-flex align-center">
              <v-avatar size="24" color="brown" class="mr-2">
                <span class="text-white text-caption">{{ corporateControllers.length }}</span>
              </v-avatar>
              <h3 class="text-h6 ma-0">
                {{ t('startTask.Corporate registrable controllers') || 'Corporate registrable controllers' }}
              </h3>
            </div>
            <div class="d-flex" style="gap: 8px;">
              <a href="#" @click.prevent="expandAllCorporate" class="text-caption text-primary text-decoration-none">
                {{ t('startTask.Expand all') || 'Expand all' }}
              </a>
              <span class="text-caption">|</span>
              <a href="#" @click.prevent="collapseAllCorporate" class="text-caption text-primary text-decoration-none">
                {{ t('startTask.Collapse all') || 'Collapse all' }}
              </a>
            </div>
          </div>

          <v-expansion-panels
            v-if="corporateControllers.length > 0"
            v-model="corporateExpandedPanels"
            multiple
            variant="accordion"
          >
            <v-expansion-panel
              v-for="(controller, index) in corporateControllers"
              :key="controller.REGISTRABLE_CONTROLLER_ID"
              :value="controller.REGISTRABLE_CONTROLLER_ID"
            >
              <v-expansion-panel-title class="controller-accordion-header">
                <div class="d-flex align-center flex-wrap">
                  <span class="font-weight-bold text-white mr-2">{{ index + 1 }}. {{ controller.FULL_NAME }}</span>
                  <span class="text-white text-caption" v-if="formatCorporateControllerInfo(controller)">{{ formatCorporateControllerInfo(controller) }}</span>
                </div>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <div class="pa-4">
                  <!-- Corporate particulars -->
                  <div class="mb-4">
                    <h4 class="section-title">{{ t('startTask.Corporate particulars') || 'Corporate particulars' }}</h4>
                    <div class="field-grid">
                      <div class="field-item">
                        <span class="field-label">{{ t('startTask.UEN') || 'UEN' }}:</span>
                        <span class="field-value">{{ controller.REG_NO || '-' }}</span>
                      </div>
                      <div class="field-item">
                        <span class="field-label">{{ t('startTask.Name') || 'Name' }}:</span>
                        <span class="field-value">{{ controller.FULL_NAME || '-' }}</span>
                      </div>
                      <div class="field-item">
                        <span class="field-label">{{ t('startTask.Jurisdiction') || 'Jurisdiction' }}:</span>
                        <span class="field-value">{{ controller.JURISDICTION ? getJurisdictionCodeName(controller.JURISDICTION) : '-' }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Contact information -->
                  <div class="mb-4">
                    <h4 class="section-title">{{ t('startTask.Contact information') || 'Contact information' }}</h4>
                    <div class="field-grid">
                      <div class="field-item">
                        <span class="field-label">{{ t('startTask.Email address') || 'Email address' }}:</span>
                        <span class="field-value">{{ controller.CONTACT_EMAIL || '-' }}</span>
                      </div>
                      <div class="field-item">
                        <span class="field-label">{{ t('startTask.Contact number') || 'Contact number' }}:</span>
                        <span class="field-value">{{ formatContactNumber(controller) || '-' }}</span>
                      </div>
                      <div class="field-item field-item-full">
                        <span class="field-label">{{ t('startTask.Address') || 'Address' }}:</span>
                        <div class="field-value address-lines">
                          <template v-if="formatAddressLines(controller).length > 0">
                            <div v-for="(line, idx) in formatAddressLines(controller)" :key="idx" class="address-line">
                              {{ line }}
                            </div>
                          </template>
                          <span v-else>-</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Controller details -->
                  <div>
                    <h4 class="section-title">{{ t('startTask.Controller details') || 'Controller details' }}</h4>
                    <div class="field-grid">
                      <div class="field-item">
                        <span class="field-label">{{ t('startTask.Date of becoming a controller') || 'Date of becoming a controller' }}:</span>
                        <span class="field-value">{{ formatDateLong(controller.BECOMING_DATE) || '-' }}</span>
                      </div>
                      <div class="field-item">
                        <span class="field-label">{{ t('startTask.Date of cessation as a controller') || 'Date of cessation as a controller' }}:</span>
                        <span class="field-value">{{ formatDateLong(controller.CESSATION_DATE) || '-' }}</span>
                      </div>
                      <div class="field-item">
                        <span class="field-label">{{ t('startTask.Confirmation by controller') || 'Confirmation by controller' }}:</span>
                        <span class="field-value">{{ controller.CONFIRMATION_CONTROLLER ? (t('startTask.Yes') || 'Yes') : (t('startTask.No') || 'No') }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>

          <v-btn
            v-if="!showCorporateAddForm && !showIndividualAddForm && !eFilingMode"
            variant="outlined"
            color="grey"
            size="small"
            class="mt-3"
            @click="handleCorporateAddFormClick"
          >
            <v-icon start>mdi-plus</v-icon>
            {{ t('startTask.Appoint another Registrable Controller') || '+ Appoint another Registrable Controller' }} ({{ t('startTask.Corporate') || 'Corporate' }})
          </v-btn>
        </div>
      </div>

      <!-- 개인 Controller 추가 폼 (E-Filing 모드와 일반 모드 공통) -->
          <v-card
        v-if="entity && showIndividualAddForm"
            variant="outlined"
            class="mt-4"
            style="border: 1px solid #e0e0e0;"
          >
            <v-card-title class="d-flex align-center justify-space-between pa-4">
              <div class="d-flex align-center">
                <span class="text-h6">{{ t('startTask.Registrable Controller') || 'Registrable Controller' }}</span>
                <v-chip color="success" size="small" class="ml-3">{{ t('startTask.To be added') || 'To be added' }}</v-chip>
              </div>
            </v-card-title>
            <v-divider />
            <v-card-text class="pa-6">
              <!-- Controller Name (위에 배치) -->
              <div class="mb-6" style="max-width: 70%;">
                <label class="text-subtitle-2 font-weight-medium mb-2 d-block">
                  {{ t('startTask.Controller Name') || 'Controller Name' }} <span class="text-error">*</span>
                </label>
                <div class="d-flex align-start" style="gap: 12px;">
            <v-autocomplete
                        v-model="individualForm.controllerName"
                        :items="individualContactOptions"
              item-title="title"
              item-value="value"
              :item-props="(item: any) => ({ disabled: item.raw?.disabled || false })"
                    :placeholder="t('startTask.Search') || 'Search'"
              variant="outlined"
                    density="compact"
              :hide-details="individualControllerNameErrorMessages.length === 0"
              :loading="isLoadingContacts"
                        style="flex: 1;"
                    append-inner-icon="mdi-magnify"
              clearable
              data-field="individualControllerName"
              :error-messages="individualControllerNameErrorMessages"
                    :disabled="eFilingMode"
                  ></v-autocomplete>
                      <v-btn
                    v-if="!eFilingMode"
                        color="primary"
                        variant="outlined"
                    density="compact"
                        @click="handleNewContact"
                    style="min-width: 100px; flex-shrink: 0; margin-top: 0;"
                      >
                        {{ t('startTask.Contact') || 'Contact' }}
                      </v-btn>
          </div>
              </div>

              <!-- Contact Details (Controller Name 선택 후 표시) -->
              <template v-if="individualForm.controllerName">
                <!-- 1. Personal information 섹션 -->
                <v-card class="contact-section-card mb-4" :class="{ 'contact-section-card-readonly': readOnly }">
                  <v-card-title class="contact-section-title">
                    {{ t('contact.personalInformation') || 'Personal information' }}
                  </v-card-title>
                  <v-card-text>
                    <!-- Name (Legal Name) -->
                    <v-row>
                      <v-col cols="12">
                        <v-text-field
                          :model-value="individualControllerNameDisplay"
                          :label="t('contact.Name') || 'Name'"
                          variant="outlined"
                          density="comfortable"
                          readonly
                          hide-details
                          class="readonly-field"
                        />
        </v-col>
      </v-row>
                    <!-- Identification Type, Nationality/Citizenship -->
                    <v-row>
                      <v-col cols="12" md="6">
                    <v-select
                      v-model="individualForm.identificationType"
                      :items="identificationTypeOptions"
                      item-title="title"
                      item-value="value"
                      variant="outlined"
                      density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
                          clearable
                        >
                          <template v-slot:label>
                            {{ t('contact.Identification Type') || 'Identification Type' }}
                          </template>
                        </v-select>
        </v-col>
                      <v-col cols="12" md="6">
                        <v-select
                          v-model="individualForm.nationality"
                          :items="nationalityOptions"
                          item-title="title"
                          item-value="value"
                          variant="outlined"
                          density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
                          clearable
                        >
                          <template v-slot:label>
                            {{ t('contact.Nationality/Citizenship') || 'Nationality/Citizenship' }}
                          </template>
                        </v-select>
                      </v-col>
                    </v-row>
                    <!-- Identification No., Date of Birth -->
                    <v-row>
                      <v-col cols="12" md="6">
                    <v-text-field
                      v-model="individualForm.identificationNumber"
                          :label="t('contact.Identification No') || 'Identification No.'"
                      variant="outlined"
                      density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
                    />
        </v-col>
                      <v-col cols="12" md="6">
                        <v-menu
                          v-model="dateOfBirthMenu"
                          :close-on-content-click="false"
                          location="bottom"
                          :disabled="readOnly"
                        >
                          <template v-slot:activator="{ props: menuProps }">
                            <v-text-field
                              v-bind="menuProps"
                              :model-value="dateOfBirthDisplay"
                              :placeholder="datePlaceholder"
                              variant="outlined"
                              density="comfortable"
                              :readonly="readOnly"
                              :class="{ 'readonly-field': readOnly }"
                              prepend-inner-icon="mdi-calendar"
                            >
                              <template v-slot:label>
                                {{ t('contact.Date of Birth') || 'Date of Birth' }}
                              </template>
                            </v-text-field>
                          </template>
                          <v-date-picker
                            v-model="dateOfBirthPickerValue"
                            v-bind="datePickerProps"
                            @update:model-value="dateOfBirthMenu = false"
                            :max="todayDate"
                          ></v-date-picker>
                        </v-menu>
                      </v-col>
                    </v-row>
                    <!-- Aliases -->
                    <v-row>
                      <v-col cols="12">
              <v-text-field
                      v-model="individualForm.aliases"
                      :label="t('startTask.Alias(es)') || 'Alias(es)'"
                variant="outlined"
                      density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
                hide-details
                    />
        </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>

                <!-- 2. Residential Address 섹션 -->
                <v-card class="contact-section-card mb-4" :class="{ 'contact-section-card-readonly': readOnly }">
                  <v-card-title class="contact-section-title">
                    {{ t('contact.Residential Address') || 'Residential Address' }}
                  </v-card-title>
                  <v-card-text>
                    <!-- Address Type Radio -->
                    <v-row>
                      <v-col cols="12">
                        <v-radio-group
                          v-model="individualForm.addressType"
                          inline
                          hide-details
                        >
                          <v-radio
                            :label="t('contact.Residential Address') || 'Residential Address'"
                            value="B07001"
                          />
                          <v-radio
                            :label="t('contact.Service Address') || 'Service Address'"
                            value="B07002"
                          />
                        </v-radio-group>
                      </v-col>
                    </v-row>

                    <!-- Local Address (B07001) -->
                    <template v-if="individualForm.addressType === 'B07001'">
                      <v-row>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="individualForm.postalCode"
                      variant="outlined"
                      density="comfortable"
                            :readonly="readOnly"
                            :class="{ 'readonly-field': readOnly }"
                          >
                            <template v-slot:label>
                              {{ t('contact.Postal Code') || 'Postal Code' }}
                            </template>
                          </v-text-field>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="individualForm.blockHouseNo"
                            :label="t('contact.Block/House No') || 'Block/House No'"
                            variant="outlined"
                            density="comfortable"
                            :readonly="readOnly"
                            :class="{ 'readonly-field': readOnly }"
                      hide-details
                    />
          </v-col>
                        <v-col cols="12" md="4">
            <v-text-field
                            v-model="individualForm.streetName"
                            :label="t('contact.Street Name') || 'Street Name'"
                  variant="outlined"
                      density="comfortable"
                            :readonly="readOnly"
                            :class="{ 'readonly-field': readOnly }"
                            hide-details
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="individualForm.buildingName"
                            :label="t('contact.Building/Estate Name') || 'Building/Estate Name'"
                            variant="outlined"
                            density="comfortable"
                            :readonly="readOnly"
                            :class="{ 'readonly-field': readOnly }"
                  hide-details
                    />
          </v-col>
        </v-row>
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="individualForm.level"
                            :label="t('contact.Level') || 'Level'"
                            variant="outlined"
                            density="comfortable"
                            :readonly="readOnly"
                            :class="{ 'readonly-field': readOnly }"
                            hide-details
                          />
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="individualForm.unit"
                            :label="t('contact.Unit') || 'Unit'"
                            variant="outlined"
                            density="comfortable"
                            :readonly="readOnly"
                            :class="{ 'readonly-field': readOnly }"
                            hide-details
                          />
                        </v-col>
                      </v-row>
                    </template>

                    <!-- Foreign Address (B07002) -->
                    <template v-else>
                      <v-row>
                        <v-col cols="12">
                          <v-text-field
                            v-model="individualForm.foreignAddressLine1"
                            variant="outlined"
                            density="comfortable"
                            :readonly="readOnly"
                            :class="{ 'readonly-field': readOnly }"
                          >
                            <template v-slot:label>
                              {{ t('contact.Foreign Address Line 1') || 'Foreign Address Line 1' }}
                            </template>
                          </v-text-field>
                        </v-col>
                        <v-col cols="12">
                          <v-text-field
                            v-model="individualForm.foreignAddressLine2"
                            :label="t('contact.Foreign Address Line 2') || 'Foreign Address Line 2'"
                            variant="outlined"
                            density="comfortable"
                            :readonly="readOnly"
                            :class="{ 'readonly-field': readOnly }"
                            hide-details
                          />
                        </v-col>
                      </v-row>
                    </template>
                  </v-card-text>
                </v-card>

                <!-- 3. Contact Address 섹션 (대체 주소) -->
                <v-card class="contact-section-card mb-4" :class="{ 'contact-section-card-readonly': readOnly }">
                  <v-card-title class="contact-section-title">
                    {{ t('contact.Contact Address') || 'Contact Address' }}
                  </v-card-title>
                  <v-card-text>
                    <!-- Address Type Radio -->
                    <v-row>
                      <v-col cols="12">
                        <v-radio-group
                          v-model="individualContactAddressTypeCd"
                          inline
                          hide-details
                        >
                          <v-radio
                            :label="t('contact.Local Address') || 'Local Address'"
                            value="B07001"
                          />
                          <v-radio
                            :label="t('contact.Foreign Address') || 'Foreign Address'"
                            value="B07002"
                          />
                        </v-radio-group>
                      </v-col>
                    </v-row>

                    <!-- Local Address (ALT - 대체 주소) -->
                    <template v-if="isIndividualContactAddressLocal">
                      <v-row>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="individualForm.altPostalCode"
                            variant="outlined"
                            density="comfortable"
                            :readonly="readOnly"
                            :class="{ 'readonly-field': readOnly }"
                          >
                            <template v-slot:label>
                              {{ t('contact.Postal Code') || 'Postal Code' }}
                            </template>
                          </v-text-field>
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="individualForm.altBlockHouseNo"
                            :label="t('contact.Block/House No') || 'Block/House No'"
                            variant="outlined"
                            density="comfortable"
                            :readonly="readOnly"
                            :class="{ 'readonly-field': readOnly }"
                            hide-details
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="individualForm.altStreetName"
                            :label="t('contact.Street Name') || 'Street Name'"
                            variant="outlined"
                            density="comfortable"
                            :readonly="readOnly"
                            :class="{ 'readonly-field': readOnly }"
                            hide-details
                          />
                        </v-col>
                        <v-col cols="12" md="4">
                          <v-text-field
                            v-model="individualForm.altBuildingName"
                            :label="t('contact.Building/Estate Name') || 'Building/Estate Name'"
                            variant="outlined"
                            density="comfortable"
                            :readonly="readOnly"
                            :class="{ 'readonly-field': readOnly }"
                            hide-details
                          />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="individualForm.altLevel"
                            :label="t('contact.Level') || 'Level'"
                            variant="outlined"
                            density="comfortable"
                            :readonly="readOnly"
                            :class="{ 'readonly-field': readOnly }"
                            hide-details
                          />
                        </v-col>
                        <v-col cols="12" md="6">
                          <v-text-field
                            v-model="individualForm.altUnit"
                            :label="t('contact.Unit') || 'Unit'"
                            variant="outlined"
                            density="comfortable"
                            :readonly="readOnly"
                            :class="{ 'readonly-field': readOnly }"
                            hide-details
                          />
                        </v-col>
                      </v-row>
                    </template>

                    <!-- Foreign Address (ALT - 대체 주소) -->
                    <template v-else>
                      <v-row>
                        <v-col cols="12">
                          <v-text-field
                            v-model="individualForm.altForeignAddressLine1"
                            variant="outlined"
                            density="comfortable"
                            :readonly="readOnly"
                            :class="{ 'readonly-field': readOnly }"
                          >
                            <template v-slot:label>
                              {{ t('contact.Foreign Address Line 1') || 'Foreign Address Line 1' }}
                            </template>
                          </v-text-field>
                        </v-col>
                        <v-col cols="12">
                          <v-text-field
                            v-model="individualForm.altForeignAddressLine2"
                            :label="t('contact.Foreign Address Line 2') || 'Foreign Address Line 2'"
                            variant="outlined"
                            density="comfortable"
                            :readonly="readOnly"
                            :class="{ 'readonly-field': readOnly }"
                            hide-details
                          />
                        </v-col>
                      </v-row>
                    </template>
                  </v-card-text>
                </v-card>

                <!-- 4. Contact 섹션 -->
                <v-card class="contact-section-card mb-4" :class="{ 'contact-section-card-readonly': readOnly }">
                  <v-card-title class="contact-section-title">
                    {{ t('contact.Contact') || 'Contact' }}
                  </v-card-title>
                  <v-card-text>
                    <!-- Email -->
                    <v-row>
                  <v-col cols="12">
                    <v-text-field
                      v-model="individualForm.emailAddress"
                      variant="outlined"
                      density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
                      :hide-details="individualEmailAddressErrorMessages.length === 0"
                      data-field="individualEmailAddress"
                      :error-messages="individualEmailAddressErrorMessages"
                    >
                      <template v-slot:label>
                            {{ t('contact.Email Address') || 'Email Address' }} <span class="text-error">*</span>
                      </template>
                    </v-text-field>
          </v-col>
                    </v-row>
                    <!-- Country Code, Area Code, Mobile No -->
                    <v-row>
                  <v-col cols="12" md="4">
                        <v-select
                      v-model="individualForm.countryCode"
                      :items="countryOptions"
                      item-title="title"
                      item-value="value"
                      variant="outlined"
                      density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
                      :hide-details="individualCountryCodeErrorMessages.length === 0"
                      data-field="individualCountryCode"
                      :error-messages="individualCountryCodeErrorMessages"
                          clearable
                    >
                      <template v-slot:label>
                            {{ t('contact.Country Code') || 'Country Code' }} <span class="text-error">*</span>
                      </template>
                        </v-select>
          </v-col>
                  <v-col cols="12" md="4">
            <v-text-field
                      v-model="individualForm.areaCode"
                          :label="t('contact.Area Code') || 'Area Code'"
              variant="outlined"
                      density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
              hide-details
                    />
          </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="individualForm.contactNumber"
                      variant="outlined"
                      density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
                      :hide-details="individualContactNumberErrorMessages.length === 0"
                      data-field="individualContactNumber"
                      :error-messages="individualContactNumberErrorMessages"
                    >
                      <template v-slot:label>
                            {{ t('contact.Mobile No') || 'Mobile No' }} <span class="text-error">*</span>
                      </template>
                    </v-text-field>
          </v-col>
                </v-row>
                  </v-card-text>
                </v-card>

              <!-- Controller details -->
                <v-card class="contact-section-card mb-4" :class="{ 'contact-section-card-readonly': readOnly }">
                  <v-card-title class="contact-section-title">
                    {{ t('startTask.Controller details') || 'Controller details' }}
                  </v-card-title>
                  <v-card-text>
                    <v-row>
                  <v-col cols="12" md="4">
                        <v-menu
                          v-model="dateOfBecomingMenu"
                          :close-on-content-click="false"
                          location="bottom"
                          :disabled="readOnly"
                        >
                      <template v-slot:activator="{ props: menuProps }">
                        <v-text-field
                          v-bind="menuProps"
                              :model-value="dateOfBecomingDisplay"
                              :placeholder="datePlaceholder"
                          variant="outlined"
                          density="comfortable"
                              :readonly="readOnly"
                              :class="{ 'readonly-field': readOnly }"
                              prepend-inner-icon="mdi-calendar"
                          :hide-details="individualDateOfBecomingErrorMessages.length === 0"
                          data-field="individualDateOfBecoming"
                          :error-messages="individualDateOfBecomingErrorMessages"
                        >
                          <template v-slot:label>
                            {{ t('startTask.Date of becoming a controller') || 'Date of becoming a controller' }} <span class="text-error">*</span>
                          </template>
                        </v-text-field>
                      </template>
                      <v-date-picker
                        v-model="dateOfBecomingPickerValue"
                            v-bind="datePickerProps"
                        @update:model-value="dateOfBecomingMenu = false"
                        :max="todayDate"
                          ></v-date-picker>
                    </v-menu>
          </v-col>
                  <v-col cols="12" md="4">
                    <div class="d-flex flex-column">
                      <label class="text-caption mb-2">{{ t('startTask.Confirmation by Registrable Controller') || 'Confirmation by Registrable Controller' }}</label>
                      <v-radio-group v-model="individualForm.confirmationByController" inline hide-details class="mt-0">
                        <v-radio
                          :label="t('startTask.Yes') || 'Yes'"
                          value="Yes"
                        />
                        <v-radio
                          :label="t('startTask.No') || 'No'"
                          value="No"
                        />
                      </v-radio-group>
      </div>
                  </v-col>
                </v-row>
                  </v-card-text>
                </v-card>
              </template>

              <!-- Action Buttons -->
              <v-card-actions v-if="!eFilingMode" class="pa-0">
                <v-spacer />
                <v-btn
                  variant="text"
                  color="error"
                  prepend-icon="mdi-delete-outline"
                  @click="handleDeleteIndividualForm"
                >
                  {{ t('startTask.Delete') || 'Delete' }}
                </v-btn>
              </v-card-actions>
            </v-card-text>
          </v-card>

      <!-- 법인 Controller 추가 폼 (E-Filing 모드와 일반 모드 공통) -->
          <v-card
        v-if="entity && showCorporateAddForm"
            variant="outlined"
            class="mt-4"
            style="border: 1px solid #e0e0e0;"
          >
            <v-card-title class="d-flex align-center justify-space-between pa-4">
              <div class="d-flex align-center">
                <span class="text-h6">{{ t('startTask.Registrable Controller') || 'Registrable Controller' }}</span>
                <v-chip color="success" size="small" class="ml-3">{{ t('startTask.To be added') || 'To be added' }}</v-chip>
              </div>
            </v-card-title>
            <v-divider />
            <v-card-text class="pa-6">
              <!-- Controller Name (위에 배치) -->
              <section class="mb-6">
                <v-row dense>
                  <v-col cols="12" md="10">
                    <div class="d-flex" style="gap: 12px;">
                      <v-autocomplete
                        v-model="corporateForm.controllerName"
                        :items="corporateContactOptions"
                        item-title="title"
                        item-value="value"
                        :label="t('startTask.Controller Name') || 'Controller Name'"
                        variant="outlined"
                        density="comfortable"
                        :hide-details="corporateControllerNameErrorMessages.length === 0"
                        :loading="isLoadingCorporateContacts"
                        style="flex: 1;"
                        clearable
                        data-field="corporateControllerName"
                        :error-messages="corporateControllerNameErrorMessages"
                      >
                        <template v-slot:label>
                          {{ t('startTask.Controller Name') || 'Controller Name' }} <span class="text-error">*</span>
                        </template>
                      </v-autocomplete>
                      <v-btn
                        color="primary"
                        variant="outlined"
                        @click="handleNewCorporateContact"
                      >
                        {{ t('startTask.Contact') || 'Contact' }}
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
              </section>

              <!-- Entity Information -->
              <section class="mb-6">
                <h4 class="section-title mb-4">{{ t('startTask.Entity information') || 'Entity information' }}</h4>
                <v-row dense>
                  <!-- Entity UEN -->
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="corporateForm.uen"
                      :label="t('startTask.Entity UEN') || 'Entity UEN'"
                      placeholder="Enter UEN"
                      variant="outlined"
                      density="comfortable"
                      :readonly="readOnly"
                      :class="{ 'readonly-field': readOnly }"
                      hide-details
                    />
                  </v-col>
                  
                  <!-- Legal form of the corporate controller -->
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="corporateForm.legalForm"
                      :label="t('startTask.Legal form of the corporate controller') || 'Legal form of the corporate controller'"
                      placeholder="e.g. Company, Limited Liability Partnership, LLC etc."
                      variant="outlined"
                      density="comfortable"
                      :readonly="readOnly"
                      :class="{ 'readonly-field': readOnly }"
                      hide-details
                    />
                  </v-col>
                  
                  <!-- Jurisdiction where the corporate controller is formed -->
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="corporateForm.jurisdiction"
                      :items="jurisdictionOptions"
                      item-title="title"
                      item-value="value"
                      :label="t('startTask.Jurisdiction where the corporate controller is formed') || 'Jurisdiction where the corporate controller is formed'"
                      placeholder="Select jurisdiction"
                      variant="outlined"
                      density="comfortable"
                      :readonly="readOnly"
                      :class="{ 'readonly-field': readOnly }"
                      hide-details
                    />
                  </v-col>
                  
                  <!-- Statute where the corporate controller is formed or incorporated -->
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="corporateForm.incorporationStatute"
                      :label="t('startTask.Statute where the corporate controller is formed or incorporated') || 'Statute where the corporate controller is formed or incorporated'"
                      placeholder="e.g. Companies Act, Limited Liability Act etc."
                      variant="outlined"
                      density="comfortable"
                      :readonly="readOnly"
                      :class="{ 'readonly-field': readOnly }"
                      hide-details
                    />
                  </v-col>
                  
                  <!-- Name of the corporate entity register -->
                  <v-col cols="12" md="6">
                    <div class="d-flex align-center" style="gap: 8px;">
                      <v-text-field
                        v-model="corporateForm.corporateEntityRegister"
                        :label="t('startTask.Name of the corporate entity register of the jurisdiction in which the corporate controller is formed or incorporated') || 'Name of the corporate entity register of the jurisdiction in which the corporate controller is formed or incorporated'"
                        placeholder="e.g. ACRA, Companies House UK etc."
                        variant="outlined"
                        density="comfortable"
                        :readonly="readOnly"
                        :class="{ 'readonly-field': readOnly }"
                        hide-details
                        style="flex: 1;"
                      />
                      <v-btn
                        v-if="corporateForm.corporateEntityRegister"
                        icon="mdi-delete"
                        variant="text"
                        color="error"
                        size="small"
                        @click="corporateForm.corporateEntityRegister = ''"
                        :title="t('startTask.Delete name of corporate entity register') || 'Delete name of corporate entity register'"
                      />
                    </div>
                  </v-col>
                </v-row>
              </section>

              <!-- 1. Residential Address 섹션 -->
              <v-card class="contact-section-card mb-4" :class="{ 'contact-section-card-readonly': readOnly }">
                <v-card-title class="contact-section-title">
                  {{ t('contact.Residential Address') || 'Residential Address' }}
                </v-card-title>
                <v-card-text>
                  <!-- Address Type Radio -->
                  <v-row>
                    <v-col cols="12">
                      <v-radio-group
                        v-model="corporateForm.addressType"
                        inline
                        hide-details
                      >
                        <v-radio
                          :label="t('contact.Residential Address') || 'Residential Address'"
                          value="B07001"
                        />
                        <v-radio
                          :label="t('contact.Service Address') || 'Service Address'"
                          value="B07002"
                        />
                      </v-radio-group>
                    </v-col>
                  </v-row>

                  <!-- Local Address (B07001) -->
                  <template v-if="corporateForm.addressType === 'B07001'">
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="corporateForm.postalCode"
                          variant="outlined"
                          density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
                        >
                          <template v-slot:label>
                            {{ t('contact.Postal Code') || 'Postal Code' }}
                          </template>
                        </v-text-field>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="corporateForm.blockHouseNo"
                          :label="t('contact.Block/House No') || 'Block/House No'"
                          variant="outlined"
                          density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
                          hide-details
                        />
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="corporateForm.streetName"
                          :label="t('contact.Street Name') || 'Street Name'"
                          variant="outlined"
                          density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
                          hide-details
                        />
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="corporateForm.buildingName"
                          :label="t('contact.Building Name') || 'Building Name'"
                          variant="outlined"
                          density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
                          hide-details
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="corporateForm.level"
                          :label="t('contact.Level') || 'Level'"
                          variant="outlined"
                          density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
                          hide-details
                        />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="corporateForm.unit"
                          :label="t('contact.Unit') || 'Unit'"
                          variant="outlined"
                          density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
                          hide-details
                        />
                      </v-col>
                    </v-row>
                  </template>

                  <!-- Foreign Address (B07002) -->
                  <template v-else>
                    <v-row>
                      <v-col cols="12">
                        <v-text-field
                          v-model="corporateForm.foreignAddressLine1"
                          :label="t('contact.Foreign Address Line 1') || 'Foreign Address Line 1'"
                          variant="outlined"
                          density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
                          hide-details
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <v-text-field
                          v-model="corporateForm.foreignAddressLine2"
                          :label="t('contact.Foreign Address Line 2') || 'Foreign Address Line 2'"
                          variant="outlined"
                          density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
                          hide-details
                        />
                      </v-col>
                    </v-row>
                  </template>
                </v-card-text>
              </v-card>

              <!-- 2. Contact Address 섹션 -->
              <v-card class="contact-section-card mb-4" :class="{ 'contact-section-card-readonly': readOnly }">
                <v-card-title class="contact-section-title">
                  {{ t('contact.Contact Address') || 'Contact Address' }}
                </v-card-title>
                <v-card-text>
                  <!-- Address Type Radio -->
                  <v-row>
                    <v-col cols="12">
                      <v-radio-group
                        v-model="corporateContactAddressTypeCd"
                        inline
                        hide-details
                      >
                        <v-radio
                          :label="t('contact.Residential Address') || 'Residential Address'"
                          value="B07001"
                        />
                        <v-radio
                          :label="t('contact.Service Address') || 'Service Address'"
                          value="B07002"
                        />
                      </v-radio-group>
                    </v-col>
                  </v-row>

                  <!-- Local Address (B07001) -->
                  <template v-if="isCorporateContactAddressLocal">
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="corporateForm.altPostalCode"
                          variant="outlined"
                          density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
                        >
                          <template v-slot:label>
                            {{ t('contact.Postal Code') || 'Postal Code' }}
                          </template>
                        </v-text-field>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="corporateForm.altBlockHouseNo"
                          :label="t('contact.Block/House No') || 'Block/House No'"
                          variant="outlined"
                          density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
                          hide-details
                        />
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="corporateForm.altStreetName"
                          :label="t('contact.Street Name') || 'Street Name'"
                          variant="outlined"
                          density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
                          hide-details
                        />
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field
                          v-model="corporateForm.altBuildingName"
                          :label="t('contact.Building Name') || 'Building Name'"
                          variant="outlined"
                          density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
                          hide-details
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="corporateForm.altLevel"
                          :label="t('contact.Level') || 'Level'"
                          variant="outlined"
                          density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
                          hide-details
                        />
                      </v-col>
                      <v-col cols="12" md="6">
                        <v-text-field
                          v-model="corporateForm.altUnit"
                          :label="t('contact.Unit') || 'Unit'"
                          variant="outlined"
                          density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
                          hide-details
                        />
                      </v-col>
                    </v-row>
                  </template>

                  <!-- Foreign Address (B07002) -->
                  <template v-else>
                    <v-row>
                      <v-col cols="12">
                        <v-text-field
                          v-model="corporateForm.altForeignAddressLine1"
                          :label="t('contact.Foreign Address Line 1') || 'Foreign Address Line 1'"
                          variant="outlined"
                          density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
                          hide-details
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col cols="12">
                        <v-text-field
                          v-model="corporateForm.altForeignAddressLine2"
                          :label="t('contact.Foreign Address Line 2') || 'Foreign Address Line 2'"
                          variant="outlined"
                          density="comfortable"
                          :readonly="readOnly"
                          :class="{ 'readonly-field': readOnly }"
                          hide-details
                        />
                      </v-col>
                    </v-row>
                  </template>
                </v-card-text>
              </v-card>

              <!-- 3. Contact 섹션 -->
              <v-card class="contact-section-card mb-4" :class="{ 'contact-section-card-readonly': readOnly }">
                <v-card-title class="contact-section-title">
                  {{ t('contact.Contact') || 'Contact' }}
                </v-card-title>
                <v-card-text>
                  <!-- Email -->
                  <v-row>
                  <v-col cols="12">
                    <v-text-field
                      v-model="corporateForm.emailAddress"
                      variant="outlined"
                      density="comfortable"
                        :readonly="readOnly"
                        :class="{ 'readonly-field': readOnly }"
                      :hide-details="corporateEmailAddressErrorMessages.length === 0"
                      data-field="corporateEmailAddress"
                      :error-messages="corporateEmailAddressErrorMessages"
                    >
                      <template v-slot:label>
                          {{ t('contact.Email Address') || 'Email Address' }} <span class="text-error">*</span>
                      </template>
                    </v-text-field>
                  </v-col>
                  </v-row>
                  <!-- Country Code, Area Code, Mobile No -->
                  <v-row>
                  <v-col cols="12" md="4">
                    <v-autocomplete
                      v-model="corporateForm.countryCode"
                      :items="countryOptions"
                      item-title="title"
                      item-value="value"
                      variant="outlined"
                      density="comfortable"
                        :readonly="readOnly"
                        :class="{ 'readonly-field': readOnly }"
                      :hide-details="corporateCountryCodeErrorMessages.length === 0"
                      data-field="corporateCountryCode"
                      :error-messages="corporateCountryCodeErrorMessages"
                        clearable
                    >
                      <template v-slot:label>
                          {{ t('contact.Country Code') || 'Country Code' }} <span class="text-error">*</span>
                      </template>
                    </v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="corporateForm.areaCode"
                        :label="t('contact.Area Code') || 'Area Code'"
                      variant="outlined"
                      density="comfortable"
                        :readonly="readOnly"
                        :class="{ 'readonly-field': readOnly }"
                      hide-details
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="corporateForm.contactNumber"
                      variant="outlined"
                      density="comfortable"
                        :readonly="readOnly"
                        :class="{ 'readonly-field': readOnly }"
                      :hide-details="corporateContactNumberErrorMessages.length === 0"
                      data-field="corporateContactNumber"
                      :error-messages="corporateContactNumberErrorMessages"
                    >
                      <template v-slot:label>
                          {{ t('contact.Mobile No') || 'Mobile No' }} <span class="text-error">*</span>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>
                </v-card-text>
              </v-card>

              <!-- 4. Controller details 섹션 -->
              <v-card class="contact-section-card mb-4" :class="{ 'contact-section-card-readonly': readOnly }">
                <v-card-title class="contact-section-title">
                  {{ t('startTask.Controller details') || 'Controller details' }}
                </v-card-title>
                <v-card-text>
                  <v-row>
                  <v-col cols="12" md="4">
                      <v-menu
                        v-model="corporateDateOfBecomingMenu"
                        :close-on-content-click="false"
                        location="bottom"
                        :disabled="readOnly"
                      >
                      <template v-slot:activator="{ props: menuProps }">
                        <v-text-field
                          v-bind="menuProps"
                            :model-value="corporateDateOfBecomingDisplay"
                            :placeholder="datePlaceholder"
                          variant="outlined"
                          density="comfortable"
                            :readonly="readOnly"
                            :class="{ 'readonly-field': readOnly }"
                            prepend-inner-icon="mdi-calendar"
                          :hide-details="corporateDateOfBecomingErrorMessages.length === 0"
                          data-field="corporateDateOfBecoming"
                          :error-messages="corporateDateOfBecomingErrorMessages"
                        >
                          <template v-slot:label>
                            {{ t('startTask.Date of becoming a controller') || 'Date of becoming a controller' }} <span class="text-error">*</span>
                          </template>
                        </v-text-field>
                      </template>
                      <v-date-picker
                        v-model="corporateDateOfBecomingPickerValue"
                          v-bind="datePickerProps"
                        @update:model-value="corporateDateOfBecomingMenu = false"
                        :max="todayDate"
                        ></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12" md="4">
                    <div class="d-flex flex-column">
                      <label class="text-caption mb-2">{{ t('startTask.Confirmation by Registrable Controller') || 'Confirmation by Registrable Controller' }}</label>
                      <v-radio-group v-model="corporateForm.confirmationByController" inline hide-details class="mt-0">
                        <v-radio
                          :label="t('startTask.Yes') || 'Yes'"
                          value="Yes"
                        />
                        <v-radio
                          :label="t('startTask.No') || 'No'"
                          value="No"
                        />
                      </v-radio-group>
                    </div>
                  </v-col>
                </v-row>
                </v-card-text>
              </v-card>

              <!-- Action Buttons -->
              <v-card-actions v-if="!eFilingMode" class="pa-0">
                <v-spacer />
                <v-btn
                  variant="text"
                  color="error"
                  prepend-icon="mdi-delete-outline"
                  @click="handleDeleteCorporateForm"
                >
                  {{ t('startTask.Delete') || 'Delete' }}
                </v-btn>
              </v-card-actions>
            </v-card-text>
          </v-card>

        <!-- 로딩 상태 -->
        <div v-if="isLoadingControllers" class="text-center py-8">
          <v-progress-circular
            indeterminate
            color="primary"
            size="48"
          ></v-progress-circular>
      </div>

      <!-- Task Flow Section (doc template 바로 위에 표시) -->
      <TaskFlowSection
        v-if="(showIndividualAddForm || showCorporateAddForm) && showTaskFlow && !eFilingMode"
        v-model="taskFlow"
        :visible="true"
        :is-all-supported="isAllSupported"
        :is-create-document-supported="isCreateDocumentSupported"
        :is-request-t-sign-supported="isRequestTSignSupported"
        :is-proceed-with-e-filing-supported="isProceedWithEFilingSupported"
        class="mb-6 mt-6"
      />
    </v-card-text>
  </v-card>
  
  <!-- 개인 New Contact 모달 (Contact 생성용) -->
  <ContactListPage
    v-model="showNewContactModal"
    :is-modal="true"
    :on-select="handleContactSelected"
    :initial-category="'B09002'"
  />

  <!-- Contact 상세 화면 모달 (선택된 Contact의 상세 정보) -->
  <v-dialog
    v-model="showContactDetailModal"
    max-width="1400px"
    width="95%"
    persistent
  >
    <v-card rounded="md">
      <v-card-title class="d-flex justify-space-between align-center pa-4">
        <span class="text-h6">{{ t('contact.Contact Detail') || 'Contact Detail' }}</span>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          @click="showContactDetailModal = false"
        />
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-4" style="max-height: 80vh; overflow-y: auto;">
        <ContactDetailPage
          v-if="individualForm.controllerName"
          :contact-id="individualForm.controllerName"
          :is-modal="true"
          :on-select="handleContactDetailSelect"
          @select="handleContactDetailSelect"
          @updated="handleContactUpdated"
          @back="showContactDetailModal = false"
        />
      </v-card-text>
    </v-card>
  </v-dialog>

  <!-- 법인 New Contact 모달 (Contact 생성용) -->
  <ContactListPage
    v-model="showNewCorporateContactModal"
    :is-modal="true"
    :on-select="handleCorporateContactSelected"
    :initial-category="'B09001'"
  />

  <!-- 법인 Contact 상세 화면 모달 (선택된 Contact의 상세 정보) -->
  <v-dialog
    v-model="showCorporateContactDetailModal"
    max-width="1400px"
    width="95%"
    persistent
  >
    <v-card rounded="md">
      <v-card-title class="d-flex justify-space-between align-center pa-4">
        <span class="text-h6">{{ t('contact.Contact Detail') || 'Contact Detail' }}</span>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          @click="showCorporateContactDetailModal = false"
        />
      </v-card-title>
      <v-divider />
      <v-card-text class="pa-4" style="max-height: 80vh; overflow-y: auto;">
        <ContactDetailPage
          v-if="corporateForm.controllerName"
          :contact-id="corporateForm.controllerName"
          :is-modal="true"
          :on-select="handleCorporateContactDetailSelect"
          @select="handleCorporateContactDetailSelect"
          @updated="handleCorporateContactUpdated"
          @back="showCorporateContactDetailModal = false"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
/* 폰트 패밀리 - Inter */
* {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* 카드 타이틀 - 18px Bold */
:deep(.v-card-title) {
  font-size: 18px !important;
  font-weight: 700 !important;
  color: #3C4650;
}

/* 레이블 - 14px */
:deep(.v-label),
:deep(.v-field-label) {
  font-size: 14px !important;
  color: #3C4650;
}

/* 입력 필드 - 14px */
:deep(.v-field__input) {
  font-size: 14px !important;
  color: #3C4650;
}

/* placeholder - 14px #777E85 */
:deep(.v-field__input::placeholder),
:deep(input::placeholder) {
  font-size: 14px !important;
  color: #777E85 !important;
}

/* 버튼 - 14px */
:deep(.v-btn) {
  font-size: 14px !important;
  font-weight: 500 !important;
  text-transform: none;
}

/* Primary 버튼 */
:deep(.v-btn--color-primary) {
  background-color: #1B84FF !important;
}

.text-error {
  color: rgb(var(--v-theme-error));
}

/* 자동 바인딩된 필드 클릭 가능 스타일 */
.clickable-field {
  cursor: pointer !important;
}

.clickable-field:hover {
  background-color: rgba(27, 132, 255, 0.05) !important;
  border-color: rgba(27, 132, 255, 0.3) !important;
}

.clickable-field:focus-within {
  border-color: rgba(27, 132, 255, 0.5) !important;
}

/* readonly 필드의 회색 배경 제거 */
:deep(.v-field--variant-filled) {
  background-color: transparent !important;
}

:deep(.v-field--variant-filled .v-field__input) {
  background-color: transparent !important;
}

/* readonly 필드도 outlined 스타일 유지 */
.clickable-field :deep(.v-field__input) {
  background-color: transparent !important;
}

/* 아코디언 헤더 스타일 (Save 버튼과 동일한 색상) */
.controller-accordion-header {
  background-color: #1B84FF !important;
  color: white !important;
}

.controller-accordion-header :deep(.v-expansion-panel-title__overlay) {
  background-color: #1B84FF !important;
}

.controller-accordion-header :deep(.v-expansion-panel-title__icon) {
  color: white !important;
}

/* 아코디언 항목 간 간격 추가 */
:deep(.v-expansion-panels .v-expansion-panel) {
  margin-bottom: 12px !important;
}

:deep(.v-expansion-panels .v-expansion-panel:last-child) {
  margin-bottom: 0 !important;
}

/* 아코디언 내용 스타일 */
:deep(.v-expansion-panel-text__wrapper) {
  padding: 0;
}

/* 섹션 제목 스타일 (ControllerCessationForm과 통일) */
.section-title {
  font-size: 16px !important;
  font-weight: 600 !important;
  color: #3C4650 !important;
  margin-bottom: 16px !important;
  margin-top: 0 !important;
}

/* 필드 그리드 (한 줄에 4개) */
.field-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px 24px;
  color: #3C4650;
  font-size: 14px;
  font-weight: 400;
}

/* 필드 아이템 */
.field-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* 전체 너비 필드 (주소 등) */
.field-item-full {
  grid-column: 1 / -1;
}

/* Contact 섹션 카드 스타일 (E-Filing 모드용) */
.contact-section-card {
  background-color: white !important;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: white !important;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.12) !important;
    border-color: rgba(27, 132, 255, 0.3) !important;
  }

  /* v-card 내부 overlay 제거 */
  :deep(.v-card__overlay) {
    display: none !important;
    opacity: 0 !important;
    background-color: transparent !important;
  }
}

.contact-section-title {
  font-size: 18px;
  font-weight: 700;
  color: #3C4650;
  padding: 16px 24px;
}

.contact-section-card :deep(.v-card-text) {
  padding: 0 24px 24px !important;
}

/* Readonly 필드 스타일 - disabled 상태 유지, 배경색 흰색, hover 효과 제거 */
.readonly-field {
  pointer-events: none;
}

.readonly-field :deep(.v-field) {
  background-color: white !important;
  opacity: 1 !important;
}

.readonly-field :deep(.v-field:hover) {
  background-color: white !important;
}

.readonly-field :deep(.v-field__input) {
  color: rgba(0, 0, 0, 0.87) !important;
  -webkit-text-fill-color: rgba(0, 0, 0, 0.87) !important;
  background-color: white !important;
}

.readonly-field :deep(.v-field--variant-outlined) {
  background-color: white !important;
}

.readonly-field :deep(.v-field--variant-outlined:hover) {
  background-color: white !important;
}

.readonly-field :deep(.v-field__overlay) {
  background-color: transparent !important;
  opacity: 0 !important;
}

.readonly-field :deep(input) {
  cursor: default !important;
  background-color: white !important;
}

/* Contact 섹션 카드 내부 필드 hover 제거 */
.contact-section-card :deep(.v-field__overlay) {
  background-color: transparent !important;
  opacity: 0 !important;
}

.contact-section-card :deep(.v-field:hover .v-field__overlay) {
  background-color: transparent !important;
  opacity: 0 !important;
}

/* Radio group readonly 스타일 */
.contact-section-card :deep(.v-radio-group) {
  pointer-events: auto !important;
}

.contact-section-card :deep(.v-selection-control--disabled) {
  opacity: 1 !important;
}

.contact-section-card :deep(.v-label--clickable) {
  cursor: default !important;
  opacity: 1 !important;
}

/* ReadOnly 상태에서 Contact 카드 클릭 비활성화 */
.contact-section-card-readonly {
  cursor: default !important;
  pointer-events: none;
}

/* ReadOnly 상태에서도 라디오 그룹은 클릭 가능하도록 */
.contact-section-card-readonly :deep(.v-radio-group) {
  pointer-events: auto !important;
}

.contact-section-card-readonly:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08) !important;
  border-color: rgba(0, 0, 0, 0.12) !important;
}

/* 필드 레이블 (ControllerCessationForm과 통일) */
.field-label {
  color: #3C4650;
  font-size: 14px;
  font-weight: 500;
}

/* 필드 값 (ControllerCessationForm과 통일) */
.field-value {
  color: #3C4650;
  font-size: 14px;
  font-weight: 400;
}

/* 주소 여러 줄 표시 */
.address-lines {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.address-line {
  color: #3C4650;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
}

/* 아코디언 내용의 일반 텍스트 스타일 */
:deep(.v-expansion-panel-text) {
  color: #3C4650;
  font-size: 14px;
  font-weight: 400;
}
</style>